import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { ServingsStepper } from "./ServingsStepper";
import { difficultyLabel, totalTimeMin } from "../data/recipes";
import { findIngredient, unitLabel, compareQty, inferCanonicalFromName, getIngredientInfo, siblingsInHub, allCanonicals } from "../data/ingredients";
import IngredientCard from "./IngredientCard";
import CookComplete from "./CookComplete";
import { recipeNutrition, recipeNutritionBreakdown, formatMacros } from "../lib/nutrition";
import { useIngredientInfo } from "../lib/useIngredientInfo";
import { useBrandNutrition } from "../lib/useBrandNutrition";
import { pairRecipeIngredients, describePairing, normalizeForMatch, deriveRowHeader, subTierLabel } from "../lib/recipePairing";
import { useCookSession, isAddedToBowl } from "../lib/useCookSession";
import { windowForRecipe } from "../lib/useActiveCookSession";
import { deriveCounterComponents, classifyWetDry } from "../lib/recipeComponents";
import CounterList from "./CounterList";
import { useBackgroundTimers } from "../lib/useBackgroundTimers";
import { runRecipeTechniqueChecks } from "../lib/recipeValidator";
import TechniqueCheckBadge from "./TechniqueCheckBadge";
import { useCookTelemetry } from "../lib/useCookTelemetry";
import { useTeamCookSession } from "../lib/useTeamCookSession";
import { BoodaTimer } from "../lib/boodaTimer";
import { useVoiceCommands } from "../lib/useVoiceCommands";
import VoiceListeningOverlay from "./VoiceListeningOverlay";
import { isHandsFreeEnabled } from "./Settings";
import { playChadwickStep, replayChadwickStep, stopChadwickAudio, startIdleChitChat, stopIdleChitChat, playChadwickMinimize } from "../lib/chadwickAudio";
import { playVoiceline } from "../lib/playVoiceline";
import { claim as audioFloorClaim, extend as audioFloorExtend, release as audioFloorRelease } from "../lib/audioFloor";
import { playEngineNarration, stopEngineNarration, engineNarrationSupported } from "../lib/engineNarration";
import { useUserRecipes } from "../lib/useUserRecipes";
import { supabase } from "../lib/supabase";
import { createRecipeShare } from "../lib/recipeShares";
import { Share } from "@capacitor/share";
import { applyCookSessionToRecipe, buildUnitConversionTargets, countActiveSwaps, recipeBrandUpgrades, recipeSwapSummary, relevantSwapsForStep, tokenizeSwappedInstruction } from "../lib/effectiveRecipe";
import { playTimerChime, playStepCompleteChime, primeCookAudio } from "../lib/cookAudio";
import { useWebPush } from "../lib/useWebPush";
import UnitPicker from "./UnitPicker";
import ModalSheet from "./ModalSheet";
import CookChatSheet from "./CookChatSheet";
import { applyPreferredUnit, prefKeyForIngredient, useUnitPrefsVersion, DISPLAY_CONTEXT } from "../lib/unitPrefs";
import { scaleRecipe } from "../lib/recipeScaling";
import { clampServings } from "../lib/recipeConstants";
import RecipeNutritionLine from "./RecipeNutritionLine";
import StepTimeline from "./StepTimeline";
import OvernightDay1Cap from "./OvernightDay1Cap";
import { totalStepSeconds } from "../lib/calendarUtils";
import { AnimationMap } from "./CookAnimations";
import { Timer, MultiStepTimer, TimerStack, ManualTimer, timerActionBtnStyle } from "./CookTimers";
import { resolveStepTimers } from "../lib/inferTimersFromInstruction";
import { donenessProteinsInRecipe, applyDonenessToRecipe, defaultThicknessIn, defaultDonenessFor } from "../lib/doneness";
import CookDonenessGate from "./CookDonenessGate";
import { inferStepKind } from "../lib/inferStepKind";
import StepLottie from "./StepLottie";
import ShareRecipeSheet from "./ShareRecipeSheet";
import TeamCookOverlay from "./TeamCookOverlay";
import CookOtherTimersStrip from "./CookOtherTimersStrip";
import StepAssignmentBanner from "./StepAssignmentBanner";
import CookPreviewAssigner from "./CookPreviewAssigner";
import TechniqueChips from "./TechniqueChips";
import { claimCookSession, releaseCookSession } from "../lib/cookSessionClaim";
import { paletteEntry } from "../lib/participantPalette";
import { absoluteAvatarURL } from "../lib/absoluteAvatarURL";

// True when a pantry row's PRIMARY canonical identity matches the given
// ingredient id (after alias / hub-family redirect). A recipe calling for
// standalone mozzarella is NOT satisfied by a frozen pizza that happens
// to list mozzarella in its ingredients[] composition — user won't
// crack open the pizza to cook with. Deduction needs primary identity.
//
// Previously this function ALSO checked ingredients[] membership (the
// migration-0033 "composite items satisfy component calls" rule), which
// caused pizzas / leftovers to auto-pair with component ingredients.
// The composition axis still exists in the data; it's useful for "what
// macros does this pizza contain?" questions but not for deduction
// pairing.

// Decide how a recipe ingredient shows up in the checklist. Reads
// from the live pairing pass so the badge, the italic pairing line,
// and the deduction screen all share one source of truth — no more
// "IN KITCHEN" badge on the same row whose pairing line says "Closest
// match: …". Inputs:
//   ing      — the (effective) recipe ingredient
//   pairing  — the row from pairRecipeIngredients(...) for this ing
//
//   status: "skip"          — not tracked (no ingredientId)
//   status: "missing"       — not in pantry, no substitute either
//   status: "wrong-state"   — have the ingredient but in the wrong form
//                             (loaf when recipe wants crumbs)
//   status: "substitute"    — only an authored sub or hub-sibling on hand
//   status: "low"           — paired exactly, but cooking will drain us
//                             below threshold
//   status: "ok"            — paired exactly, plenty on hand
function statusFor(ing, pairing) {
  if (!ing.ingredientId) return { ing, status: "skip" };
  if (!pairing) return { ing, status: "missing", row: null };
  if (pairing.status === "wrong-state") {
    return {
      ing,
      status: "wrong-state",
      row: null,
      candidates: pairing.wrongStateCandidates || [],
    };
  }
  if (pairing.status === "substitute") {
    return { ing, status: "substitute", row: pairing.closestMatch || null };
  }
  if (pairing.status === "missing") {
    return { ing, status: "missing", row: null };
  }
  // status === "paired" — layer compareQty on top to detect LOW.
  const row = pairing.paired;
  const canonical = findIngredient(ing.ingredientId);
  if (!row || row.amount <= 0) return { ing, status: "missing", row: null };
  if (!canonical || !ing.qty) return { ing, status: "ok", row };
  const cmp = compareQty({
    have: { amount: row.amount, unit: row.unit },
    need: ing.qty,
    lowThreshold: row.lowThreshold,
    ingredient: canonical,
  });
  if (cmp === "unknown") return { ing, status: "ok", row };
  return { ing, status: cmp, row };
}

// Guarantee unique step ids within a recipe. The timer-deadline
// adoption layer matches telemetry rows to steps by step.id STRING
// (CookMode.jsx endsAt resolution, stepsWithEndedTimer, telemetry
// resume-adopt) — NEVER by step index. Model-authored ids are never
// validated for uniqueness (generate-recipe / parse-recipe-text both
// just tell the model to emit "step1", "step2", …; bundled recipes
// use numeric ids), so two steps that happen to share an id let the
// SECOND step adopt the FIRST's running countdown — "timers adopt
// other timers' values."
//
// This pass is collision-ONLY and STABLE: the FIRST occurrence of each
// id keeps it verbatim (so any telemetry already written for it still
// matches on resume), and only later duplicates are renamed
// deterministically. A well-formed recipe (all ids distinct) returns
// the SAME object identity, so the technique-check memo isn't disturbed.
function dedupeStepIds(recipe) {
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : null;
  if (!steps || steps.length === 0) return recipe;
  const seen = new Set();
  let changed = false;
  const nextSteps = steps.map((s, i) => {
    const raw = s?.id != null ? String(s.id) : "";
    let id = raw || `step${i + 1}`;
    if (seen.has(id)) {
      let n = 2;
      while (seen.has(`${id}__${n}`)) n += 1;
      id = `${id}__${n}`;
    }
    seen.add(id);
    if (id === raw) return s;
    changed = true;
    return { ...s, id };
  });
  return changed ? { ...recipe, steps: nextSteps } : recipe;
}

export default function CookMode({
  recipe: recipeRaw, onDone, onExit, onAbandon, onSchedule, onOpenFamilyKitchen,
  pantry = [], setPantry, setShoppingList, onGoToShopping,
  userId, family = [], friends = [],
  // Revise-for-next-time callback — (recipe, seedText) => void. Wired
  // by App; CookComplete's post-summary offer routes the cook's notes
  // into the AI revise sheet. Null hides the offer.
  onReviseRecipe = null,
  // Lapsed-cook closure: adopt THIS session id (own, any age) instead
  // of the 2h slug re-find, and when initialFinishNow is set jump
  // straight into the post-cook logging flow once the session is
  // adopted — the know-it-by-heart cook never wanted the step list.
  resumeSessionId = null,
  initialFinishNow = false,
  // Fork-to-new-recipe callback. Wired by the parent (Plan.jsx /
  // CreateMenu.jsx) to useUserRecipes.saveRecipe. When the user
  // finishes a cook with active swaps, CookComplete offers "SAVE
  // CHANGES AS NEW RECIPE" which calls this with the materialized
  // effective recipe; saveRecipe generates a unique slug, so the
  // original recipe stays untouched — "the recipe remains the
  // golden standard" principle the user called out when designing
  // this flow. Optional: if not provided, the save action simply
  // isn't offered.
  onForkRecipe = null,
  // Resume-from-banner handoff. When the CookBanner re-opens CookMode
  // on an already-active session, App passes the live step index + a
  // hint to skip past the ingredient-prep overview + the real
  // cook_step_notifications.deliver_at so the in-app Timer shows
  // wall-clock-accurate remaining seconds instead of a fresh
  // step.timer countdown. Defaults keep the first-launch flow
  // (overview → step 0 → fresh countdown from step.timer) intact.
  initialView       = "overview",
  initialStepIndex  = 0,
  initialTimerEndsAt = null,
  // When the resume came from a TimerStack stage (telemetry's
  // step_id carried a `-s${stageIdx}` suffix), App parses it out and
  // hands the index here so TimerStack can hydrate that stage to
  // "running" with the wall-clock deadline. `null` for fresh cooks
  // and resumes onto a single-Timer step.
  initialTimerStageIdx = null,
  // Team-cook join: when a helper/diner taps a participation push,
  // App routes them here with the lead's session ID. Telemetry
  // adopts that session instead of creating a duplicate for the
  // helper, and write-gates step rows so only the lead's CookMode
  // mutates the timeline. Helpers see live state via realtime.
  joinSessionId = null,
  // Opens the manual add-item drawer pre-seeded with the
  // ingredient identity. Used by the pre-cook-screen "+ ADD"
  // button on missing rows — lets the cook log "I just bought
  // nutmeg" without routing through shopping. Optional.
  onAddToPantry = null,
}) {
  // Recipe-level technique validation at read-time. Pure JS, runs in
  // milliseconds. Returns BOTH the corrected recipe (with safety cues
  // appended to step prose) AND the diagnostics that drive the
  // TechniqueCheckBadge below. We render the corrected recipe so the
  // badge's "we added 165°F to chicken" message matches what the user
  // actually sees in the step list — no badge lying about a fix that
  // didn't land. Memoized on recipeRaw identity so the servings
  // stepper doesn't re-validate; idempotent on already-fixed input.
  const { recipe, diagnostics: techniqueDiagnostics } = useMemo(() => {
    if (!recipeRaw) return { recipe: recipeRaw, diagnostics: null };
    // dedupeStepIds FIRST so every downstream id-keyed path (timer
    // adoption, telemetry, React keys) sees collision-free step ids.
    try {
      const checked = runRecipeTechniqueChecks(recipeRaw, { findIngredient });
      return { recipe: dedupeStepIds(checked.recipe), diagnostics: checked.diagnostics };
    } catch (e) {
      console.warn("[CookMode] technique validate threw:", e?.message || e);
      return { recipe: dedupeStepIds(recipeRaw), diagnostics: null };
    }
  }, [recipeRaw]);

  const [view, setView] = useState(initialView);
  const [activeStep, setActiveStep] = useState(initialStepIndex);
  // Team-cook helpers: deadline pulled from the lead's
  // cook_session_steps.started_at + nominal_seconds. Null on solo
  // cooks and for the lead's own view.
  const [syncedEndsAt, setSyncedEndsAt] = useState(null);
  const [syncedStepKey, setSyncedStepKey] = useState(null);

  // Team-cook lead-ended auto-close: helpers/diners stay in CookMode
  // only as long as the lead's session is active. When the lead
  // finishes or abandons, the session row's status flips and we
  // pop out of CookMode automatically so the helper isn't staring
  // at a frozen step list.
  useEffect(() => {
    const sessionId = joinSessionId;
    if (!sessionId || !userId || !onExit) return undefined;
    const ch = supabase
      .channel(`rt:teamcook-session:${sessionId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "cook_sessions", filter: `id=eq.${sessionId}` },
        (payload) => {
          const next = payload.new;
          if (next && next.status && next.status !== "active") {
            try { onExit(); } catch {}
            return;
          }
          // Live servings sync — the lead bumping servings mid-cook
          // should re-scale the helper's ingredient amounts too. The
          // UPDATE payload already carries the row; only an in-mount
          // USER pick on this device outranks it.
          const n = Number(next?.servings);
          if (Number.isFinite(n) && n > 0 && servingsSourceRef.current !== "user") {
            servingsSourceRef.current = "session";
            setServingsOverride(n);
          }
        },
      )
      .subscribe();
    return () => { try { supabase.removeChannel(ch); } catch {} };
  }, [joinSessionId, userId, onExit]);

  // Team-cook helper land-on-assignment: when this user is a
  // helper with active assignments, jump them to their FIRST
  // pending/accepted assigned step instead of step 0. Parallel
  // cooks open into their own lane: "you start the onions, I'll
  // do the pasta" — both go straight to their step. Runs once
  // on session adoption.
  useEffect(() => {
    const sessionId = joinSessionId;
    if (!sessionId || !userId || !recipe?.steps?.length) return undefined;
    let cancelled = false;
    (async () => {
      const { data: mine } = await supabase
        .from("cook_step_assignments")
        .select("step_id, status, assigned_at")
        .eq("cook_session_id", sessionId)
        .eq("assigned_to", userId)
        .in("status", ["pending", "accepted", "in_progress"])
        .order("assigned_at", { ascending: true });
      if (cancelled || !mine?.length) return;
      const stripStage = (s) => String(s || "").replace(/-s\d+$/, "");
      for (const a of mine) {
        const target = stripStage(a.step_id);
        const idx = recipe.steps.findIndex((s) => String(s.id) === target);
        if (idx >= 0) {
          setActiveStep(idx);
          setView("cook");
          break;
        }
      }
    })();
    return () => { cancelled = true; };
  }, [joinSessionId, userId, recipe?.steps?.length]);

  // Team-cook helper sync: when this CookMode adopted someone else's
  // session (helper / diner mirroring the lead), subscribe to that
  // session's cook_session_steps rows. We follow ONLY THE LEAD's
  // startStep / finishStep writes — NOT other helpers' parallel
  // work. Otherwise: Marissa starts step 1, Trevor (also a helper)
  // starts step 2 → Marissa's client sees Trevor's INSERT, auto-jumps
  // her to step 2, the activeStep useEffect fires startStep for her
  // at step 2, and her step 1 row is orphaned (looks "auto-completed"
  // even though finished_at stays null — visually she's gone from the
  // step she was actually working on). Confirmed bug 2026-05-14.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const sessionId = joinSessionId;
    if (!sessionId || !userId) return undefined;
    const stripStage = (s) => String(s || "").replace(/-s\d+$/, "");
    const findIdxByStepId = (rawStepId) => {
      const target = stripStage(rawStepId);
      if (!target || !recipe?.steps?.length) return -1;
      return recipe.steps.findIndex((s) => String(s.id) === target);
    };
    let cancelled = false;
    let leadUserId = null;
    const applyStepRow = async (row, { isInitial = false } = {}) => {
      if (!row || row.finished_at) return;
      // Only follow the LEAD's navigation. Other helpers cooking
      // their own steps in parallel must not yank us off ours.
      // Self-rows are no-ops (we're already on this step locally).
      // If leadUserId hasn't resolved yet, we defer rather than
      // accidentally follow a non-lead row during the boot window.
      if (!leadUserId) return;
      if (row.user_id !== leadUserId) return;
      if (row.user_id === userId) return;
      // Initial mount: yes, drop us where the lead currently is so
      // the helper joins INTO the live cook context.
      // Subsequent realtime updates: only follow if THIS user has no
      // open work of their own. Without this check the lead merely
      // navigating to a future step (e.g., to assign it) yanks the
      // helper off whatever they were actually cooking. Once the
      // helper has tapped Start on anything, the lead's nav stops
      // dragging them around — they're free to focus.
      if (!isInitial) {
        try {
          const { data: myOpen } = await supabase
            .from("cook_session_steps")
            .select("id")
            .eq("cook_session_id", sessionId)
            .eq("user_id", userId)
            .is("finished_at", null)
            .limit(1);
          if (myOpen && myOpen.length > 0) return;
        } catch { /* fall through and follow */ }
      }
      const idx = findIdxByStepId(row.step_id);
      if (idx >= 0) {
        setActiveStep(idx);
        setView("cook");
      }
      // Don't use started_at + nominal_seconds as timer source —
      // that's the step ENTRY time, not the actual TIMER tap. The
      // notification subscription below tracks the real deliver_at.
      setSyncedStepKey(String(row.step_id));
    };
    // Pull the live cook_step_notifications row's deliver_at —
    // that's the wall-clock deadline the lead's timer is counting
    // down to. Updates when the lead taps Start Timer, restarts,
    // pauses, or extends.
    const syncTimerFromNotification = async (stepId) => {
      const { data } = await supabase
        .from("cook_step_notifications")
        .select("deliver_at, delivered_at, dismissed_at, step_id")
        .eq("cook_session_id", sessionId)
        .eq("step_id", String(stepId))
        .is("delivered_at", null)
        .is("dismissed_at", null)
        .order("deliver_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (data?.deliver_at) {
        const ms = new Date(data.deliver_at).getTime();
        // Ignore the parked-future placeholder (the lead queued the step
        // but hasn't tapped Start). Real cook timers ring within hours;
        // anything 12h+ out is the not-yet-armed sentinel.
        setSyncedEndsAt(ms - Date.now() > 12 * 60 * 60 * 1000 ? null : ms);
      } else {
        setSyncedEndsAt(null);
      }
    };
    // Initial fetch — resolve the lead's user_id (so realtime
    // filtering can ignore other helpers' parallel rows) AND find
    // the lead's currently-open step row.
    (async () => {
      const { data: session } = await supabase
        .from("cook_sessions")
        .select("user_id")
        .eq("id", sessionId)
        .maybeSingle();
      if (cancelled) return;
      leadUserId = session?.user_id || null;
      const leadStepQuery = supabase
        .from("cook_session_steps")
        .select("step_id, started_at, finished_at, nominal_seconds, user_id")
        .eq("cook_session_id", sessionId)
        .is("finished_at", null)
        .order("started_at", { ascending: false })
        .limit(1);
      const { data } = leadUserId
        ? await leadStepQuery.eq("user_id", leadUserId).maybeSingle()
        : await leadStepQuery.maybeSingle();
      if (cancelled || !data) return;
      applyStepRow(data, { isInitial: true });
      syncTimerFromNotification(data.step_id);
    })();
    // Realtime tail
    const ch = supabase
      .channel(`rt:teamcook-steps:${sessionId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_steps", filter: `cook_session_id=eq.${sessionId}` },
        (payload) => {
          const row = payload.new || payload.old;
          if (!row) return;
          if (payload.eventType === "INSERT" && !row.finished_at) {
            applyStepRow(row);
            syncTimerFromNotification(row.step_id);
          }
          if (payload.eventType === "UPDATE" && !row.finished_at) {
            applyStepRow(row);
            syncTimerFromNotification(row.step_id);
          }
          if (row.finished_at && String(row.step_id) === syncedStepKey) {
            setSyncedEndsAt(null);
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_step_notifications", filter: `cook_session_id=eq.${sessionId}` },
        (payload) => {
          const row = payload.new || payload.old;
          if (!row) return;
          // Re-pull the latest live notification for whatever step
          // we're showing. Simpler than reasoning about INSERT vs
          // UPDATE vs DELETE — single source of truth fetch.
          if (syncedStepKey && String(row.step_id) === syncedStepKey) {
            syncTimerFromNotification(syncedStepKey);
          }
        },
      )
      .subscribe();
    return () => {
      cancelled = true;
      try { supabase.removeChannel(ch); } catch {}
    };
  }, [joinSessionId, userId, recipe?.steps?.length]);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  // Set<baseStepId> of steps whose timer has demonstrably rung at
  // some point in this session. Populated from the session-wide
  // cook_session_steps subscription. Lets the Timer derive
  // `initialDone` for steps the user advanced past — startStep
  // inserts a fresh empty row when revisiting, but the original
  // finished row's timer_state still lives in the DB. We OR over
  // every row that matches the base step id; fresh empty rows
  // contribute nothing, the finished row carries the "ended" flag.
  const [stepsWithEndedTimer, setStepsWithEndedTimer] = useState(() => new Set());
  const [justAdded, setJustAdded] = useState(0);
  const [cardIng, setCardIng] = useState(null); // { ingredientId, fallbackName, fallbackEmoji }
  // Pre-cook servings adjuster — surfaced on the overview screen
  // (the "main cook page" before START COOKING). The recipe ships
  // at recipe.serves; the user can scale up/down via +/- and the
  // ingredient list + nutrition rollup follow. null = use the
  // recipe's authored serves count. Scope reset whenever the
  // recipe slug changes so opening a fresh recipe doesn't carry
  // last session's pick.
  const [servingsOverride, setServingsOverride] = useState(null);
  // Where the current override came from — the precedence ladder is
  // seed < session < user:
  //   "seed"    — upstream default (recipe._initialServings, stamped
  //               by book COOK NOW / scheduled-meal taps). WEAKEST:
  //               a schedule tap re-entering an active cook carries
  //               schedule-TIME servings, which must not clobber a
  //               newer mid-cook pick saved on the session.
  //   "session" — restored from cook_sessions.servings on adoption.
  //   "user"    — a stepper tap in THIS mount. The only EXPLICIT
  //               intent; it alone writes to the session row.
  const servingsSourceRef = useRef(null);
  // Reset on recipe change, seeding from a caller-provided pick.
  // Seeding the OVERRIDE — rather than receiving a pre-scaled recipe
  // copy — keeps step-prose scaling honest: the prose scaleRatio
  // derives from base serves vs override, which a pre-scaled copy
  // collapses to 1.
  useEffect(() => {
    const seed = Number(recipe?._initialServings);
    if (Number.isFinite(seed) && seed > 0) {
      servingsSourceRef.current = "seed";
      setServingsOverride(clampServings(seed));
    } else {
      servingsSourceRef.current = null;
      setServingsOverride(null);
    }
  }, [recipe?.slug, recipe?._initialServings]);
  // The resume-sync effect — which reads `telemetry.session.servings`
  // — has to live AFTER `useCookTelemetry()` runs to avoid a TDZ
  // ReferenceError. Defined further down in the function (search
  // for "Resume sync"). Keeping it co-located with the telemetry
  // hook makes the dependency obvious.

  // Scale ingredient amounts when the user adjusted servings.
  // Steps stay verbatim — scaleRecipe doesn't touch them. The
  // tokenizer in effectiveRecipe handles the byte-mismatch on
  // step prose at scale-time the same way it handles unit
  // conversions.
  const scaledRecipe = useMemo(() => {
    if (!recipe) return null;
    const baseServes = Math.max(1, Number(recipe.serves) || 1);
    if (servingsOverride == null || servingsOverride === baseServes) return recipe;
    return scaleRecipe(recipe, servingsOverride) || recipe;
  }, [recipe, servingsOverride]);
  // UnitPicker state — user-chosen unit overrides for ingredient
  // amounts in the "FOR THIS STEP" list. Keyed by ingredientId so
  // the override carries across every step that references the
  // same canonical (pick tbsp for butter once, every step shows
  // butter in tbsp). `unitPicker` holds the row currently open
  // in the modal.
  const [unitOverrides, setUnitOverrides] = useState({});
  const [unitPicker, setUnitPicker] = useState(null);
  // FOR-THIS-STEP ingredient list collapse. Default COLLAPSED — the
  // measurements live below the prose now, and the cook reads the
  // step then peeks at amounts only when needed. The user's choice
  // persists across step transitions so a cook who expands on step 1
  // stays expanded for the rest of the cook (and vice versa).
  const [stepIngredientsOpen, setStepIngredientsOpen] = useState(false);
  // "On your counter" evolving-list tile — open by default; it's the
  // primary mise-en-place surface during the cook.
  const [counterOpen, setCounterOpen] = useState(true);
  // Subscribe to preference changes so a Settings toggle re-renders
  // amounts in-place without a page reload.
  useUnitPrefsVersion();
  // Shared cook-time session state (src/lib/useCookSession.js). Owns
  // per-ingredient overrides (pantryItemId swaps, shopping promotions,
  // skip flags) and user-added extras. Passed as a prop to
  // CookComplete so the "What did you use?" screen reads the same
  // swaps the user made on cook-prep. Previously each screen kept
  // its own parallel override shape and overrides vanished on screen
  // transition; session fixes that permanently.
  // persistKey opts the session into localStorage persistence so an
  // app kill / crash mid-cook doesn't lose check-offs, swaps, and
  // extras — the banner resume now restores them alongside the step
  // index. The hook also handles recipe-prop swaps without remount
  // (CreateMenu and Plan both keep CookMode alive across some
  // transitions): a changed slug rehydrates-or-resets internally, so
  // the old mount-time resetSession() effect is gone — it used to be
  // the reason a stale session couldn't leak between recipes, but it
  // would also wipe the freshly-hydrated state on every mount.
  const cookSession = useCookSession(recipe?.slug || null);
  const { session, setOverride, clearOverride, resetSession } = cookSession;

  // Web Push subscription for this device. We don't AUTO-prompt on
  // mount (browsers blacklist you for that). Instead, render an inline
  // "enable" card inside the cook view the first time a user starts
  // a timer on an unsubscribed device. Rationale: without a push
  // subscription, the server-side cron drain that fires
  // cook_step_notifications has nobody to deliver to — lock-screen
  // rings never arrive. Opt-in is the difference between "the timer
  // works" and "the timer only works when I'm looking at it."
  const webPush = useWebPush(userId);

  // ── Cook telemetry + timer pushes (migration 0136 / 0137) ─────────
  // Records per-step durations and queues mid-cook timer notifications
  // so a user who locks their phone during a 30-min braise still gets
  // pinged when it's time to act. All side-effects fire-and-forget;
  // failures inside useCookTelemetry log + swallow.
  const telemetry = useCookTelemetry(userId);

  // Backgrounded timers for the "still cooking" floating banner —
  // any of THIS user's open step rows in the active session whose
  // timer is still ticking on a step OTHER than the active one.
  // Drives the trust signal that lets the cook leave a passive step
  // without losing the timer. See src/lib/useBackgroundTimers.js.
  const backgroundTimers = useBackgroundTimers({
    sessionId:    telemetry?.session?.id || null,
    userId,
    activeStepId: recipe?.steps?.[activeStep]?.id ?? null,
  });

  // Team-cook ownership context — feeds the per-step "is this MINE
  // to run?" check for the auto-fire startStep guard and the Timer
  // render gate. Returns no-ops when the session is solo.
  const team = useTeamCookSession({
    userId,
    cookSessionId: telemetry?.session?.id || null,
  });
  const sessionLeadId = telemetry?.session?.user_id || null;

  // Profile cache for participants. cook_session_participants only
  // carries (user_id, color_tag, role) — name + avatar live in the
  // profiles row. We fetch the lot on participant change so the rail
  // can render owner names + avatars per timer segment.
  const [cookProfilesById, setCookProfilesById] = useState(() => new Map());
  useEffect(() => {
    // ALWAYS include the local user's id — solo cooks have no
    // participants row, but the Live Activity ticker still needs
    // the local user's name + avatar to render with identity
    // (instead of falling back to the "C" / Cook initial). When
    // there's a team, this is a no-op (already in participants).
    const ids = Array.from(
      new Set([
        ...(userId ? [userId] : []),
        ...((team?.participants || [])
          .map((p) => p.user_id)
          .filter(Boolean)),
      ])
    );
    if (ids.length === 0) {
      if (cookProfilesById.size > 0) setCookProfilesById(new Map());
      return;
    }
    let alive = true;
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, name, avatar_url")
        .in("id", ids);
      if (!alive) return;
      if (error || !Array.isArray(data)) {
        console.warn("[CookMode] cookProfilesById fetch failed", error);
        return;
      }
      setCookProfilesById((prev) => {
        const next = new Map(prev);
        let changed = false;
        for (const row of data) {
          const cur = next.get(row.id);
          if (!cur || cur.name !== row.name || cur.avatar_url !== row.avatar_url) {
            next.set(row.id, { name: row.name || "", avatar_url: row.avatar_url || "" });
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    })();
    return () => { alive = false; };
    // cookProfilesById intentionally omitted — we re-fetch on participant
    // identity changes, not on every map mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.participants]);
  // Per-step ownership resolver. Returns:
  //   null         — no active assignment; anyone can claim
  //   { mine: true, name, userId }                 — caller owns it
  //   { other: true, name, userId, assignmentId }  — someone else owns it
  // Read by the auto-fire gate (skip if other-owned) and the render
  // logic (read-only card + Take Over CTA if other-owned).
  const stepOwnershipFor = useCallback((stepId) => {
    if (!stepId || !team?.assignmentsByStep) return null;
    const list = team.assignmentsByStep.get(String(stepId)) || [];
    // Pick any assignment that's not done/skipped — pending /
    // accepted / in_progress / declined / overdue all count as
    // "this step has an owner story we should reflect in the UI."
    // Done/skipped means the assignment is closed.
    const active = list.find((a) =>
      a.status === "pending"
      || a.status === "accepted"
      || a.status === "in_progress"
      || a.status === "declined"
      || a.status === "overdue"
    );
    if (!active) return null;
    const profile = team.participantsById?.get(active.assigned_to);
    const name = profile?.name || "Someone";
    if (active.assigned_to === userId) {
      return { mine: true, name, userId, assignment: active };
    }
    return {
      other: true,
      name,
      userId: active.assigned_to,
      assignmentId: active.id,
      assignment: active,
    };
  }, [team?.assignmentsByStep, team?.participantsById, userId]);
  // Resume sync: when CookMode mounts on a recipe that has an
  // existing cook_sessions row carrying a servings value (banner
  // re-open, refresh mid-cook), pull that value into local state so
  // the display matches what the cook chose at START time. Only
  // applies when the user hasn't manually adjusted in this mount
  // (servingsOverride still null).
  const sessionServings = telemetry?.session?.servings;
  useEffect(() => {
    if (sessionServings == null) return;
    // Session beats seed: a scheduled-meal tap re-entering an active
    // cook seeds schedule-time servings, but the session row carries
    // the cook's latest saved pick (possibly bumped mid-cook, possibly
    // by the lead on another device). Only an in-mount USER pick
    // outranks it.
    if (servingsOverride != null && servingsSourceRef.current === "user") return;
    const n = Number(sessionServings);
    if (Number.isFinite(n) && n > 0 && n !== servingsOverride) {
      servingsSourceRef.current = "session";
      setServingsOverride(n);
    }
  }, [sessionServings, servingsOverride]);
  // Mid-cook servings changes persist onto the live session row —
  // without this they lived only in local state and vanished on app
  // close (the startCook patch fires on remount only). Gated to
  // source === "user": seeds and session echoes must never write.
  // Debounced so rapid stepper taps coalesce into one write (egress
  // rule); deps are the STABLE pieces (session id + the useCallback)
  // — depending on the telemetry object itself restarted the debounce
  // on every render and could defer the write forever mid-cook.
  const telemetrySessionId = telemetry?.session?.id;
  const telemetryUpdateServings = telemetry?.updateServings;
  // Pending-write ref + latest-fn ref so the unmount flush below can
  // fire a debounced value that hadn't landed yet — a stepper tap
  // followed by a quick minimize (< 600ms) used to silently cancel
  // the write via the cleanup's clearTimeout.
  const pendingServingsWriteRef = useRef(null);
  const updateServingsFnRef = useRef(null);
  updateServingsFnRef.current = telemetryUpdateServings;
  useEffect(() => {
    if (servingsOverride == null || !telemetrySessionId
        || servingsSourceRef.current !== "user") {
      pendingServingsWriteRef.current = null;
      return undefined;
    }
    pendingServingsWriteRef.current = servingsOverride;
    const t = setTimeout(() => {
      pendingServingsWriteRef.current = null;
      updateServingsFnRef.current?.(servingsOverride);
    }, 600);
    return () => clearTimeout(t);
  }, [servingsOverride, telemetrySessionId, telemetryUpdateServings]);
  // Unmount flush — a pending user pick beats losing it. Safe: it
  // only ever writes this user's own chosen value, and updateServings
  // no-ops on equality and is lead-gated.
  useEffect(() => () => {
    if (pendingServingsWriteRef.current != null) {
      updateServingsFnRef.current?.(pendingServingsWriteRef.current);
    }
  }, []);
  const lastStartedStepRef = useRef(null);
  // Guards the unmount-cleanup so a user who finalized the cook
  // doesn't ALSO get a competing endCook(abandoned) racing with
  // their endCook(finished) write.
  const finalizedRef = useRef(false);
  // Captured at the "DONE! LOG IT" moment so CookComplete can stamp
  // cook_log_id onto the session row AFTER it creates the cook_log.
  // endCook is async + sets session → null on re-render; grabbing the
  // id synchronously on the button click keeps the thread-back correct
  // even if the endCook write is still in flight when CookComplete
  // mounts.
  const [completingSessionId, setCompletingSessionId] = useState(null);

  // Open a cook_sessions row the first time the user enters the live
  // cook view. Gated on the session not already existing so toggling
  // back to overview and forward again doesn't open duplicates.
  // ALSO gated on finalizedRef — when the user explicitly exits or
  // finishes the cook, telemetry.session goes null. Without the
  // finalizedRef gate, this effect re-fires during the 220ms
  // AnimatePresence exit animation (view="cook", recipe.slug still
  // set, session=null) and immediately INSERTS a new active session.
  // That ghost row was the actual cause of "cook is right back there
  // on refresh" — endCook flipped the original row to abandoned, but
  // this effect created a fresh one milliseconds later.
  useEffect(() => {
    if (view !== "cook" || !recipe?.slug || telemetry.session || finalizedRef.current) return;
    // Seed-settle gate: on a mount that lands directly in the cook
    // view with an _initialServings seed, this effect's first run
    // still sees the pre-seed closure (override null, scaledRecipe at
    // base serves) — firing startCook here INSERTs base serves, and
    // the corrected re-run gets swallowed by the in-flight dedupe.
    // Wait one commit for the seed effect to land; the deps re-run us
    // with the seeded values.
    const seed = Number(recipe?._initialServings);
    if (Number.isFinite(seed) && seed > 0 && servingsOverride == null) return;
    // Persist the cook's chosen servings on the cook_sessions row so
    // resume-from-banner re-applies the same scaling. `servings` is
    // EXPLICIT intent only (the override, including upstream seeds);
    // `defaultServings` covers the fresh-insert fallback. Passing the
    // fallback as `servings` was the resume clobber: a banner remount
    // resolves the BASE recipe, and the latest-intent-wins patch
    // overwrote the session's saved 72 with the base 4 before the
    // resume-sync effect could read it.
    const baseServes = Math.max(1, Number(recipe.serves) || 1);
    telemetry.startCook({
      recipeSlug:  recipe.slug,
      recipeTitle: recipe.title,
      recipeEmoji: recipe.emoji,
      // Explicit intent = an in-mount USER pick only. Seeds ride into
      // fresh inserts via defaultServings (scaledRecipe reflects the
      // seed), but never patch an existing session — a schedule tap's
      // stale seed must not beat the session's saved pick.
      servings:        servingsSourceRef.current === "user" ? servingsOverride : null,
      defaultServings: scaledRecipe?.serves ?? baseServes,
      // Per-cook tweaks live on the cook (migration 0273), so resume
      // restores the exact augmented cook instead of re-deriving from the
      // central recipe + fragile localStorage. Doneness is the first such
      // tweak; servings (above) + swaps (cook_session_steps) already follow
      // this model.
      cookParams:  Object.keys(donenessAnswers).length > 0 ? { doneness: donenessAnswers } : undefined,
      // Inline step snapshot (migration 0235) — lands on cook_sessions
      // .step_summary so CookActivityCard can render total time + step
      // labels without resolving the recipe via findRecipe / user_recipes.
      // Send unscaled recipe.steps; servings scaling is a display concern.
      steps:       Array.isArray(recipe.steps) ? recipe.steps : null,
      // Team-cook join: when set, telemetry adopts that specific
      // session (the lead's) instead of looking up / creating one.
      joinSessionId,
      // Lapsed-resume adoption — see prop doc above.
      resumeSessionId,
      // Finish-nudge schedule — the server-side "Did you finish X?"
      // push fires when this cook's live window lapses, the same
      // moment the in-app banner flips to the lapsed prompt. Window
      // scales with the recipe's own time (see windowForRecipe).
      nudgeAfterMs: windowForRecipe(recipe),
    });
    // Prime the Web Audio context on this explicit user action so
    // subsequent Timer chimes land on an unsuspended context — mobile
    // browsers suspend until a gesture. Safe to call repeatedly.
    primeCookAudio();
  }, [view, recipe?.slug, recipe?.title, recipe?.emoji, recipe?.serves, scaledRecipe?.serves, servingsOverride, telemetry]);

  // Each time activeStep changes (and we're inside a live session),
  // start a new cook_session_steps row + queue a timer push when the
  // step has a nominal timer. The hook auto-finishes the previous
  // step + cancels its pending push, so back-and-forth nav between
  // steps stays clean.
  useEffect(() => {
    if (view !== "cook" || !telemetry.session) return;
    const recipeSteps = recipe?.steps || [];
    const step = recipeSteps[activeStep];
    if (!step) return;
    const key = `${telemetry.session.id}:${step.id}`;
    if (lastStartedStepRef.current === key) return;
    // Ownership gate (migration 0215). If this step is owned by
    // another active assignee, don't auto-fire startStep — we'd be
    // racing a parallel timer the assignee is already running. The
    // user is browsing, not claiming. Take-over flips ownership via
    // the explicit "Take Over" CTA, which calls take_over_step RPC.
    // Lead-on-own-step always proceeds (session-owner privilege).
    const ownership = stepOwnershipFor(step.id);
    if (ownership?.other) {
      lastStartedStepRef.current = key;
      return;
    }
    lastStartedStepRef.current = key;
    // Stacked-timer steps (step.timers array) register with telemetry
    // PER STAGE on user tap, not auto-on-step-entry — see the
    // onStageStart callback wired into TimerStack below. Pre-registering
    // the whole-step sum here would queue a single push at the end of
    // the cumulative duration, which doesn't match the user's actual
    // per-stage cadence (they tap START, sear 4 min, push fires;
    // tap START on toss-and-cook stage, fires 3 min later). Auto-fire
    // only when the step has a SINGLE timer or none — those use the
    // <Timer> path that does want a single deadline pinned.
    if (Array.isArray(step.timers) && step.timers.length > 0) {
      return;
    }
    // Match the render-side resolution exactly (resolveStepTimers): if
    // this step surfaces as a multi-stage TimerStack — whether from an
    // explicit array OR from 2+ prose cues that beat a rolled-up
    // single step.timer — each stage registers + fires on user tap.
    // Auto-firing a single push at the cumulative sum here would race
    // that per-stage cadence.
    const resolved = resolveStepTimers(step);
    if (resolved?.timers && resolved.timers.length > 0) {
      return;
    }
    const nominalSeconds = resolved?.timer || totalStepSeconds(step);
    telemetry.startStep({
      stepId:         step.id,
      stepTitle:      step.title,
      nominalSeconds,
      timerBody:      step.title
        ? `Step ${step.id}: ${step.title} — timer's up`
        : null,
      // Auto-start safety net: lazy-create the cook_sessions row if no
      // one tapped START first (helper landing from a notification,
      // remote assignment-done arriving before the lead's overview-tap
      // effect ran). useCookTelemetry.startStep handles the idempotent
      // create when sessionRef is null.
      recipeSlug:  recipe?.slug,
      recipeTitle: recipe?.title,
      recipeEmoji: recipe?.emoji,
      servings:        servingsSourceRef.current === "user" ? servingsOverride : null,
      defaultServings: scaledRecipe?.serves ?? Math.max(1, Number(recipe?.serves) || 1),
    });
  }, [view, activeStep, telemetry, recipe, servingsOverride, scaledRecipe?.serves, stepOwnershipFor]);

  // Cook-session Live Activity orchestration. ONE activity for the
  // entire cook so the lock-screen card spans every step, with each
  // step a milestone, the active timer's countdown reading off
  // endsAt, and the cook's position visualized along the bar.
  //
  // Lifecycle (TIMER-CENTRIC):
  //   telemetry.session set         → BoodaTimer.startSession with
  //                                   flat timers[] derived from
  //                                   recipe.steps (one entry per
  //                                   single-Timer step, N per stack)
  //   activeStep changes            → sync currentStepIndex/Title
  //                                   so headline reflects user's
  //                                   actual position even on no-
  //                                   timer steps
  //   Timer onTimerStart            → set activeTimerIndex (resolved
  //                                   from stepIndex+label) + endsAt
  //                                   + startedAt
  //   Timer onTimerPause/Resume     → toggle isPaused / pausedRemaining
  //   Timer onTimerEnd              → push idx to completed, clear
  //                                   active-timer fields
  //   telemetry.session changes     → endSession on prior + reset ref
  const cookActivityIdRef = useRef(null);
  const cookActivityStateRef = useRef({
    currentStepIndex: 0,
    currentStepTitle: "",
    activeTimerIndex: -1,
    activeTimerEndsAt: null,
    activeTimerStartedAt: null,
    isPaused: false,
    pausedRemaining: null,
    completedTimerIndices: [],
    helpers: [],
  });
  // Held in a ref so pushCookActivityState can merge the latest
  // ownership snapshot into every state push without taking ownership
  // as a useCallback dep (would re-create the closure on every owner
  // change → break Timer's onTimerStart/onTimerEnd identity).
  const cookActivityOwnersRef = useRef([]);
  // Same pattern for the recipe's hero image — `recipe` identity flips
  // as useUserRecipes loads / realtime updates. Without a ref the
  // empty-deps pushCookActivityState closure captures the stale recipe
  // and pushes goalImageURL=null forever. Updated below in a useEffect
  // so the latest hero URL flows through on every push.
  const cookActivityGoalImageRef = useRef(null);
  useEffect(() => {
    cookActivityGoalImageRef.current = recipe?.goalImageURL || recipe?.heroImageURL || null;
  }, [recipe?.goalImageURL, recipe?.heroImageURL]);
  const pushCookActivityState = useCallback(() => {
    const cid = cookActivityIdRef.current;
    if (!cid) {
      console.log("[BoodaTimer] push skipped — no active session yet");
      return;
    }
    const snap = {
      ...cookActivityStateRef.current,
      timerOwners: cookActivityOwnersRef.current,
      // Heartbeat — bump every push so the widget body re-renders even
      // when no other state field changed. Without this the avatar's
      // .offset computation freezes between real state changes (timer
      // start, step advance, etc.) and the pin sticks while the bar
      // appears to advance via Apple's auto-tick on the ProgressView.
      lastTickAt: new Date().toISOString(),
      // Push-driven pulse — flips 0 → 1 → 0 → 1 each push. Widget
      // uses this to drive an animated halo around the active pin.
      // Continuous-animation primitives (TimelineView, phaseAnimator,
      // .symbolEffect) are all gated in Live Activities; this is the
      // only path that produces visible pulsing motion.
      pulseFrame: Math.floor(Date.now() / 1000) % 2,
      // Recipe hero image — pushed through state (attributes are
      // immutable post-start, so already-running activities can
      // only update images via state pushes). Read from ref so the
      // empty-deps useCallback closure doesn't get stuck on stale
      // recipe (the recipe field arrives async via useUserRecipes).
      goalImageURL: cookActivityGoalImageRef.current,
    };
    console.log("[BoodaTimer] push", cid, "idx=" + snap.activeTimerIndex, "endsAt=" + snap.activeTimerEndsAt, "startedAt=" + snap.activeTimerStartedAt, "completed=" + JSON.stringify(snap.completedTimerIndices), "owners=" + snap.timerOwners.length, "goalImg=" + (snap.goalImageURL ? snap.goalImageURL.slice(0, 60) : "null"));
    BoodaTimer.updateSession({
      clientId: cid,
      state: snap,
    }).catch((e) => console.warn("[CookMode] BoodaTimer.updateSession failed:", e));
  }, []);
  // Apply mutator UNCONDITIONALLY — even before the session activity
  // exists. React runs child effects before parent effects, so the
  // first Timer's onTimerStart fires BEFORE startSession's useEffect
  // has set cookActivityIdRef. If we bailed here on null cid, the
  // first mutation (carrying activeTimerEndsAt + activeTimerStartedAt)
  // would be dropped on the floor and the activity would render with
  // activeTimerIndex=-1 forever. Mutating the ref unconditionally
  // means startSession picks up whatever's currently there as initial
  // content — Timer's first mutation is preserved.
  const updateCookActivity = useCallback((mutator) => {
    mutator(cookActivityStateRef.current);
    pushCookActivityState();
  }, [pushCookActivityState]);

  // Flat timers list derived from recipe.steps — mirrors the SAME
  // effective-timer resolution the in-app Timer UI uses (CookMode's
  // render path around line 2502). For each step:
  //   1) if raw step.timers[] non-empty → use those
  //   2) elif raw step.timer → 1 entry from that
  //   3) elif inferTimersFromInstruction returns {timers: [...]} → use those
  //   4) elif inferTimersFromInstruction returns {timer: N} → 1 entry
  //   5) else → step contributes 0 milestones
  // Without (3) and (4), AI-generated recipes (which often skip the
  // explicit step.timer field but write "let it simmer for 20 min"
  // in the instruction) showed fewer dots on the lock screen than
  // the in-app UI surfaced — the activity was lying about how many
  // timers the cook would actually hit.
  const cookActivityTimersData = useMemo(() => {
    const arr = recipe?.steps || [];
    const timers = [];
    const stepLabelMap = {};
    let droppedZero = 0;
    let inferredCount = 0;
    arr.forEach((s, stepIdx) => {
      const stepTitle = s.title || `Step ${stepIdx + 1}`;
      // Same resolution as the in-app render (resolveStepTimers) so the
      // lock-screen rail shows the EXACT timers the cook will hit —
      // including the prose-split that beats a rolled-up single timer.
      const resolved = resolveStepTimers(s);
      const effectiveTimers = resolved?.timers || null;
      const effectiveTimer = !effectiveTimers ? (resolved?.timer || null) : null;
      // Raw-vs-inferred flags drive the `inferredCount` diagnostic only.
      const hasRawTimers = Array.isArray(s.timers) && s.timers.length > 0;
      const hasRawTimer = typeof s.timer === "number" && s.timer > 0;

      // Classify this step's cooking action once per step — the
      // result rides each flat timer derived from this step so the
      // Dynamic Island compact-leading slot can show the right SF
      // Symbol (sear/boil/bake/etc.). Cheap regex; cached at memo
      // level via the parent useMemo's [recipe?.steps] dep.
      const stepKind = inferStepKind(stepTitle, s.instruction);
      if (Array.isArray(effectiveTimers) && effectiveTimers.length > 0) {
        if (!hasRawTimers) inferredCount += effectiveTimers.length;
        effectiveTimers.forEach((t, tIdx) => {
          const total = Math.max(0, Math.trunc(Number(t?.seconds) || 0));
          if (total <= 0) { droppedZero++; return; }
          const label = String(t?.label ?? "");
          const flatIdx = timers.length;
          timers.push({
            id: `${s.id ?? stepIdx}-${tIdx}`,
            stepIndex: stepIdx,
            stepTitle,
            label,
            totalSeconds: total,
            stepKind,
          });
          if (!stepLabelMap[stepIdx]) stepLabelMap[stepIdx] = [];
          stepLabelMap[stepIdx].push({ label, flatIdx });
        });
      } else if (effectiveTimer) {
        if (!hasRawTimer) inferredCount += 1;
        const total = Math.max(0, Math.trunc(Number(effectiveTimer) || 0));
        if (total <= 0) {
          droppedZero++;
        } else {
          const flatIdx = timers.length;
          timers.push({
            id: `${s.id ?? stepIdx}-single`,
            stepIndex: stepIdx,
            stepTitle,
            label: "",
            totalSeconds: total,
            stepKind,
          });
          if (!stepLabelMap[stepIdx]) stepLabelMap[stepIdx] = [];
          stepLabelMap[stepIdx].push({ label: "", flatIdx });
        }
      }
    });
    console.log(
      `[BoodaTimer] timers built — kept=${timers.length} (inferred=${inferredCount}) droppedZero=${droppedZero} from steps=${arr.length} (slug=${recipe?.slug})`
    );
    return { timers, stepLabelMap };
  }, [recipe?.steps, recipe?.slug]);

  // Parallel array to cookActivityTimersData.timers — one TimerOwner
  // per flat-timer index, used to color + label each segment of the
  // lock-screen rail with the helper actually running that step.
  //
  // Resolution per step:
  //   1. Find the active assignment for step.id in team.assignmentsByStep
  //      (statuses pending/accepted/in_progress count as "owned").
  //   2. Owner user_id = assignment.assigned_to OR sessionLeadId.
  //   3. Pull color_tag from team.participantsById, hex from paletteEntry().
  //   4. Pull first-name + avatar_url from cookProfilesById.
  //
  // Solo cooks: every timer resolves to the lead. Multi-stage steps
  // (step.id like "<sid>-s<i>") inherit the parent step's owner — the
  // assignment is keyed by the parent id.
  const cookActivityTimerOwners = useMemo(() => {
    const timers = cookActivityTimersData.timers || [];
    if (timers.length === 0) return [];
    const recipeSteps = recipe?.steps || [];
    const assignmentsByStep = team?.assignmentsByStep || null;
    const participantsById = team?.participantsById || null;
    // Local-user identity always wins on segments they own — solo
    // cooks have no participants/color_tag, which used to fall back
    // to slate/grey + "C" initial. Every Live Activity should
    // surface MY avatar in green for MY segments regardless of
    // whether anyone else is in the cook.
    return timers.map((t) => {
      const step = recipeSteps[t.stepIndex];
      const stepId = step?.id != null ? String(step.id) : null;
      let ownerId = null;
      if (stepId && assignmentsByStep) {
        const list = assignmentsByStep.get(stepId) || [];
        const active = list.find((a) =>
          a.status === "accepted" ||
          a.status === "in_progress" ||
          a.status === "pending"
        );
        if (active?.assigned_to) ownerId = active.assigned_to;
      }
      if (!ownerId) ownerId = sessionLeadId;
      if (!ownerId) return null;
      const profile = cookProfilesById.get(ownerId);
      const fullName = profile?.name || "";
      const firstName = fullName ? fullName.split(/\s+/)[0] : "";
      const isLocal = ownerId === userId;
      // Local user → boodaLeaf teal. Other participants → their
      // assigned palette ring.
      const participant = participantsById?.get(ownerId);
      const colorHex = isLocal
        ? "#2F8F83"
        : paletteEntry(participant?.color_tag || "slate").ring;
      return {
        userId: ownerId,
        name: firstName || (isLocal ? "You" : "Cook"),
        colorHex,
        avatarURL: absoluteAvatarURL(profile?.avatar_url),
      };
    });
  }, [
    cookActivityTimersData,
    recipe?.steps,
    team?.assignmentsByStep,
    team?.participantsById,
    sessionLeadId,
    cookProfilesById,
    userId,
  ]);

  // Mirror cookActivityTimerOwners into the ref pushCookActivityState
  // reads, and push the activity whenever ownership changes (helper
  // accepts / declines / hands off mid-cook). Push only fires when
  // cookActivityIdRef is non-null — i.e. after startSession has armed
  // the activity.
  useEffect(() => {
    cookActivityOwnersRef.current = cookActivityTimerOwners || [];
    if (cookActivityIdRef.current) {
      pushCookActivityState();
    }
    // Also pre-cache each owner's avatar PNG into the App Group
    // container so the widget can render the photo synchronously
    // from a local file. AsyncImage from inside the widget extension
    // hangs on arbitrary network URLs — the cached file path is
    // Apple's recommended pattern.
    const seen = new Set();
    for (const o of (cookActivityTimerOwners || [])) {
      if (!o?.userId || !o?.avatarURL) continue;
      if (seen.has(o.userId)) continue;
      seen.add(o.userId);
      BoodaTimer.cacheAvatar({ url: o.avatarURL, key: o.userId })
        .then(() => console.info("[BoodaTimer] avatar cached", { userId: o.userId }))
        .catch((e) => console.warn("[BoodaTimer] avatar cache failed", e?.message || e));
    }
  }, [cookActivityTimerOwners, pushCookActivityState]);

  // Pre-cache the recipe hero image into the App Group container so
  // the widget's BoodaTimerDishHero reads it synchronously from a
  // local file. Mirrors the avatar caching above. Key is the URL's
  // last-path-component stem (mushroom-risotto.png → "mushroom-risotto");
  // the widget derives the same key on its side. After the cache
  // lands, push the activity state once so the running widget
  // re-renders and picks up the now-present local file.
  useEffect(() => {
    let cancelled = false;
    const cacheHero = (url) => {
      const key = BoodaTimer.recipeImageCacheKey(url);
      if (!key) return;
      BoodaTimer.cacheRecipeImage({ url, key })
        .then(() => {
          console.info("[BoodaTimer] recipe image cached", { key });
          if (cookActivityIdRef.current) pushCookActivityState();
        })
        .catch((e) => console.warn("[BoodaTimer] recipe image cache failed", e?.message || e));
    };
    const url = recipe?.goalImageURL || recipe?.heroImageURL || null;
    if (url) { cacheHero(url); return; }
    // No image on the CLIENT copy — but goal images now land SERVER-
    // side 30-60s after the recipe row (background generation), so a
    // cached recipe object can say "no image" long after the DB has
    // one. The Live Activity started this way shows the grey fallback
    // square for the whole cook. Re-read the single row at cook time
    // (one row, negligible egress); if the image landed, cache it,
    // stamp the ref, and push state so the running Activity picks it
    // up mid-cook.
    if (!userId || !recipe?.slug) return;
    (async () => {
      try {
        const { data } = await supabase
          .from("user_recipes")
          .select("recipe")
          .eq("user_id", userId)
          .eq("recipe->>slug", recipe.slug)
          .order("created_at", { ascending: false })
          .limit(1);
        const freshUrl = data?.[0]?.recipe?.goalImageURL || null;
        if (cancelled || !freshUrl) return;
        console.info("[BoodaTimer] goal image found on fresh row (client copy was stale)");
        cookActivityGoalImageRef.current = freshUrl;
        cacheHero(freshUrl);
      } catch (e) {
        console.warn("[BoodaTimer] fresh goal-image lookup failed:", e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [recipe?.goalImageURL, recipe?.heroImageURL, recipe?.slug, userId, pushCookActivityState]);



  /// Map (stepIndex, label) → flat-timer-index. Empty label
  /// disambiguates to the only candidate when the step has just one
  /// timer; multi-stage steps need exact-label match.
  const findActivityTimerIndex = useCallback((stepIndex, label) => {
    const candidates = cookActivityTimersData.stepLabelMap[stepIndex];
    if (!Array.isArray(candidates) || candidates.length === 0) return -1;
    if (candidates.length === 1) return candidates[0].flatIdx;
    const lower = String(label || "").toLowerCase();
    const exact = candidates.find(
      (c) => String(c.label || "").toLowerCase() === lower
    );
    return exact ? exact.flatIdx : -1;
  }, [cookActivityTimersData]);

  // Start the activity when the cook session begins. Skipped for
  // recipes with zero timers — there's nothing milestone-shaped to
  // surface, and no point burning a Live Activity slot.
  useEffect(() => {
    const sessionId = telemetry?.session?.id;
    if (!sessionId || !recipe?.slug) return;
    if (cookActivityTimersData.timers.length === 0) {
      console.log("[BoodaTimer] startSession SKIPPED — recipe has 0 timers (slug=", recipe.slug, ")");
      return;
    }
    const cid = `cook-${sessionId}`;
    cookActivityIdRef.current = cid;
    // Claim the session for this device so useTeamLiveActivity (the
    // cross-device mirror hook running at App level) defers to us
    // for Live Activity ownership. Released on unmount + on session
    // change. The hook still mirrors on OTHER devices the same user
    // is signed in on — e.g. lead cooks from desktop, phone mirrors.
    claimCookSession(sessionId);
    console.log("[BoodaTimer] startSession", cid, "timers:", cookActivityTimersData.timers.length, "initial:", JSON.stringify(cookActivityStateRef.current));
    // DON'T reset cookActivityStateRef here — Timer's mount effect
    // ran before this parent effect and may have already populated
    // activeTimerIndex/endsAt/startedAt for the active step. Wiping
    // would lose that. End-of-prior-session resets the ref when
    // transitioning between cooks; this effect just ships whatever
    // the ref currently holds.
    BoodaTimer.startSession({
      clientId: cid,
      recipeName: recipe.title || "",
      goalImageURL: recipe.goalImageURL || recipe.heroImageURL,
      timers: cookActivityTimersData.timers,
      initial: {
        ...cookActivityStateRef.current,
        timerOwners: cookActivityOwnersRef.current,
      },
    })
      .then(() => console.log("[BoodaTimer] startSession OK"))
      .catch((e) => console.warn("[CookMode] BoodaTimer.startSession failed:", e));
    // Cleanup: when CookMode unmounts (user backs out, navigates,
    // app closes), release the session claim so the mirror hook can
    // take over driving this device's Live Activity off Supabase
    // realtime. The Live Activity itself stays running — only the
    // ownership flag flips.
    return () => { releaseCookSession(sessionId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telemetry?.session?.id, recipe?.slug]);

  // Sync currentStepIndex + currentStepTitle whenever activeStep
  // changes. Headline anchors to the user's current step (even on
  // no-timer steps); without this the lock-screen would jump ahead
  // to "Up next: Step N" which lies about position.
  useEffect(() => {
    const steps = recipe?.steps || [];
    const stepTitle = steps[activeStep]?.title || `Step ${activeStep + 1}`;
    updateCookActivity((s) => {
      s.currentStepIndex = activeStep;
      s.currentStepTitle = stepTitle;
    });
  }, [activeStep, recipe?.steps, updateCookActivity]);

  // ── Cross-helper sync (completion + live helper timers) ────────────
  // Two outputs from one realtime subscription on cook_session_steps:
  //
  //   (1) completedTimerIndices — flat indices for steps stamped
  //       finished_at by any participant. The Live Activity rail draws
  //       those segments solid + plants a completion avatar.
  //
  //   (2) helpers[] — for every OPEN step row owned by SOMEONE OTHER
  //       than the lead, project a Participant entry the widget reads
  //       inside `activeOwnerForSegment`. Without this, Marissa starting
  //       step 2 invisible on Trevor's lock screen — his bar only knew
  //       about his own activeTimerIndex.
  //
  // Both refire on any cook_session_steps change. Single channel, single
  // re-fetch. The fetch returns the full open + finished snapshot so we
  // don't have to reason about INSERT vs UPDATE deltas.
  useEffect(() => {
    const sid = telemetry?.session?.id;
    if (!sid) return undefined;
    const timers = cookActivityTimersData?.timers || [];
    if (timers.length === 0) return undefined;
    const recipeSteps = recipe?.steps || [];
    let alive = true;

    const apply = (rows) => {
      if (!alive) return;
      // Stage-aware completion. Each finished cook_session_steps row
      // resolves to ONE specific flat-timer index (single-Timer step
      // → its only flat index; TimerStack stage `<id>-s<N>` → the Nth
      // entry from stepLabelMap[stepIndex]). Pre-fix: we just stripped
      // the -sN suffix and matched ALL flat timers for that step's
      // baseId, so finishing stage 0 marked stages 1 and 2 done too —
      // bar showed three completed segments after the user only did
      // one stage.
      const finishedFlatIndices = new Set();
      // Set of base step ids whose timer has ENDED at any point in
      // this session (ANY row for the base id has ended=true OR an
      // endsAt in the past). OR across all rows — when the user
      // revisits a finished step, a fresh empty row is inserted by
      // startStep but the prior finished row still has the ended
      // flag, so the step stays in this set. The Timer reads this
      // to keep DONE state across navigation.
      const endedStepIds = new Set();
      // Dedupe by COMPOSITE KEY (userId + stepIndex). Collapses
      // TimerStack stage rows for the same step under one entry per
      // user, but KEEPS separate entries when the same user has
      // parallel timers across different steps (e.g. Trevor on step 1
      // still ticking AND step 3 ticking after navigating ahead).
      // Self-rows ARE projected too: the widget's per-segment ticking
      // walks helpers[] looking for any active range matching a
      // segment, so without local-user entries the bar can only show
      // ONE local timer at a time (the scalar activeTimerIndex). With
      // self-projection + composite-key, parallel local timers each
      // produce their own segment fill.
      const helpersByKey = new Map(); // `${userId}:${stepIdx}` -> {score, startedMs, entry}
      const stepLabelMap = cookActivityTimersData.stepLabelMap || {};
      const nowMs = Date.now();
      const markFinished = (stepIdx, stageIdx) => {
        const stages = stepLabelMap[stepIdx];
        if (Array.isArray(stages) && stages.length > 0) {
          const pick = stages.length === 1 ? stages[0] : stages[stageIdx];
          if (pick && Number.isInteger(pick.flatIdx)) {
            finishedFlatIndices.add(pick.flatIdx);
          }
        }
      };
      for (const r of (rows || [])) {
        const rawId = String(r.step_id || "");
        const stageMatch = rawId.match(/-s(\d+)$/);
        const stageIdx = stageMatch ? Number(stageMatch[1]) : 0;
        const baseId = stageMatch ? rawId.slice(0, stageMatch.index) : rawId;
        const stepIdx = recipeSteps.findIndex((s) => String(s.id) === baseId);
        if (stepIdx < 0) continue;
        // Detect "timer rang" for THIS user's single-Timer steps and
        // OR into the set. Stage rows (-sN) handled separately by
        // TimerStack's own hydration path.
        if (!stageMatch && r.user_id === userId) {
          const rowTs = r.timer_state || null;
          const rowEndsMs = rowTs?.endsAt ? new Date(rowTs.endsAt).getTime() : null;
          const rowEnded = rowTs?.ended === true
            || (Number.isFinite(rowEndsMs) && rowEndsMs <= nowMs);
          if (rowEnded) endedStepIds.add(baseId);
        }
        // A segment counts as COMPLETE when:
        //   • finished_at is set (explicit Done / voice "done"), OR
        //   • timer_state.ended is true (the timer naturally rang), OR
        //   • timer_state.endsAt is in the past (the timer would have
        //     rung even if no client was foreground to fire onTimerEnd —
        //     e.g., user navigated away before the bell, then app backgrounded)
        // Without #2 + #3 the segment would revert to "unstarted faint"
        // once the user navigated past, since NEXT STEP no longer stamps
        // finished_at.
        const ts = r.timer_state || null;
        const tsEndsMs = ts?.endsAt ? new Date(ts.endsAt).getTime() : null;
        const segmentDone = !!r.finished_at
          || !!ts?.ended
          || (Number.isFinite(tsEndsMs) && tsEndsMs <= nowMs);
        if (segmentDone) {
          markFinished(stepIdx, stageIdx);
          // Closed in DB → drop entirely.
          if (r.finished_at) continue;
          // SELF-row whose timer has rung (ended/expired) → drop
          // from helpers[] projection. Without this, the bar
          // rendered an extra avatar at the old step that moved
          // in sync with the user's CURRENT step's pin — the
          // "step 1 inherits step 2's motion" bug. The step's
          // completion is already conveyed by the checkmark on
          // the bar (driven by completedTimerIndices). OTHER
          // users' rang timers DO still project so multi-cook
          // strip below the bar continues to show them.
          if (r.user_id === userId) continue;
          // Other users' rang-but-not-finished rows keep going
          // through to the strip — e.g. helper finished their
          // last step but is still in the cook session.
        }
        // Project EVERY remaining open row (including self with
        // an ACTIVELY ticking timer). Self-projection on actively-
        // ticking rows preserves the rare parallel-timers case
        // (one user, two simultaneous ticking timers on different
        // steps) since the scalar activeTimerIndex only covers
        // one of them.
        if (!r.user_id) continue;
        const stepTitle = r.step_title || recipeSteps[stepIdx]?.title || `Step ${stepIdx + 1}`;
        let startedAt = null;
        if (ts?.endsAt && r.nominal_seconds) {
          const endsMs = new Date(ts.endsAt).getTime();
          startedAt = new Date(endsMs - Number(r.nominal_seconds) * 1000).toISOString();
        } else if (r.started_at) {
          startedAt = r.started_at;
        }
        // Resolve the EXACT flat-timer segment this row owns. For
        // single-Timer steps stepLabelMap[stepIdx] has one entry —
        // pick it. For TimerStack stages the Nth entry corresponds
        // to stage N (the suffix on step_id), so index directly by
        // stageIdx. Drops to -1 only when the recipe shape doesn't
        // align with the row (recipe edited mid-cook, etc.); widget
        // falls back to currentStepIndex-only match in that case.
        const stagesForStep = cookActivityTimersData.stepLabelMap[stepIdx];
        const flatTimerIndex = (Array.isArray(stagesForStep) && stagesForStep.length > 0)
          ? (stagesForStep.length === 1
              ? stagesForStep[0].flatIdx
              : (stagesForStep[stageIdx]?.flatIdx ?? stagesForStep[0].flatIdx))
          : -1;
        const entry = {
          userId:               r.user_id,
          name:                 (cookProfilesById.get(r.user_id)?.name || "").split(/\s+/)[0] || "Helper",
          currentStepIndex:     stepIdx,
          currentStepTitle:     stepTitle,
          activeTimerEndsAt:    ts?.endsAt || null,
          activeTimerStartedAt: startedAt,
          isPaused:             !!ts?.isPaused,
          pausedRemaining:      ts?.isPaused ? (Number(ts?.pausedRemainingSec) || 0) : null,
          flatTimerIndex,
        };
        // Score per row, higher wins. Dedupe key is now
        // (userId, flatTimerIndex) so multi-stage parallel work
        // within the SAME step produces MULTIPLE entries (one per
        // stage) instead of collapsing into one. Falls back to
        // (userId, stepIdx) when flatTimerIndex is -1 (recipe-shape
        // mismatch) so we don't drop the row entirely. Score order:
        //   3 — currently ticking (endsAt future, not paused)
        //   2 — paused with endsAt set
        //   1 — has any timer_state (ended / READY / weird)
        //   0 — open row, no timer_state at all
        const score = (() => {
          if (ts?.endsAt) {
            const endsMs = new Date(ts.endsAt).getTime();
            if (Number.isFinite(endsMs) && endsMs > nowMs) {
              return ts.isPaused ? 2 : 3;
            }
          }
          return ts ? 1 : 0;
        })();
        const startedMs = r.started_at ? new Date(r.started_at).getTime() : 0;
        const key = flatTimerIndex >= 0
          ? `${r.user_id}:f${flatTimerIndex}`
          : `${r.user_id}:s${stepIdx}`;
        const prevEntry = helpersByKey.get(key);
        const winsBetter = !prevEntry
          || score > prevEntry.score
          || (score === prevEntry.score && startedMs > prevEntry.startedMs);
        if (winsBetter) {
          helpersByKey.set(key, { score, startedMs, entry });
        }
      }
      const helpers = Array.from(helpersByKey.values()).map((v) => v.entry);
      console.log("[CookMode:helperSync] rows=" + (rows?.length ?? 0)
        + " finishedFlatIndices=" + JSON.stringify(Array.from(finishedFlatIndices))
        + " helpers=" + JSON.stringify(helpers.map((h) => ({
            u: h.userId.slice(0, 8),
            step: h.currentStepIndex,
            endsAt: h.activeTimerEndsAt,
            paused: h.isPaused,
          }))));
      updateCookActivity((s) => {
        const prev = new Set(s.completedTimerIndices || []);
        for (const idx of finishedFlatIndices) prev.add(idx);
        s.completedTimerIndices = Array.from(prev).sort((a, b) => a - b);
        s.helpers = helpers;
      });
      setStepsWithEndedTimer(endedStepIds);
    };

    const fetchOnce = async () => {
      const { data } = await supabase
        .from("cook_session_steps")
        .select("step_id, step_title, user_id, started_at, finished_at, nominal_seconds, timer_state")
        .eq("cook_session_id", sid)
        .order("started_at", { ascending: false });
      apply(data);
    };
    fetchOnce();

    const ch = supabase
      .channel(`rt:cookmode-stepsync:${sid}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_steps",
          filter: `cook_session_id=eq.${sid}` },
        () => { if (alive) fetchOnce(); },
      )
      .subscribe();
    return () => {
      alive = false;
      try { supabase.removeChannel(ch); } catch { /* ignore */ }
    };
  }, [telemetry?.session?.id, recipe?.steps, cookActivityTimersData, updateCookActivity, userId, cookProfilesById]);

  // End the prior session's activity when telemetry.session changes
  // (finalize → null, or different recipe → new id).
  const prevSessionIdRef = useRef(null);
  useEffect(() => {
    const sid = telemetry?.session?.id || null;
    if (prevSessionIdRef.current && prevSessionIdRef.current !== sid) {
      const priorCid = `cook-${prevSessionIdRef.current}`;
      BoodaTimer.endSession({ clientId: priorCid }).catch(() => {});
      releaseCookSession(prevSessionIdRef.current);
      if (cookActivityIdRef.current === priorCid) {
        cookActivityIdRef.current = null;
      }
      // Wipe ref so the next session's startSession effect doesn't
      // inherit stale activeTimerIndex / endsAt from the prior cook.
      cookActivityStateRef.current = {
        currentStepIndex: 0,
        currentStepTitle: "",
        activeTimerIndex: -1,
        activeTimerEndsAt: null,
        activeTimerStartedAt: null,
        isPaused: false,
        pausedRemaining: null,
        completedTimerIndices: [],
        helpers: [],
      };
    }
    prevSessionIdRef.current = sid;
  }, [telemetry?.session?.id]);

  // Stable callbacks for Timer / TimerStack timer-state hooks.
  // (stepIndex, label) → flat-timer-index resolution lives here so
  // the Timer components stay agnostic of the activity's index space.
  const handleTimerStart = useCallback((deadlineMs, totalSec, label, stepRowId) => {
    const idx = findActivityTimerIndex(activeStep, label || "");
    console.log("[BoodaTimer] onTimerStart activeStep=" + activeStep + " label=" + label + " resolvedIdx=" + idx + " stepRowId=" + (stepRowId || "(activeStepRef)"));
    // Sync timer_state to the active cook_session_steps row so
    // participants (kids-mode + Live Activity mirrors) tick in sync
    // with the lead's actual run. Fire-and-forget — failures don't
    // block the local Live Activity update.
    //
    // stepRowId is threaded through from TimerStack/ManualTimer when
    // the caller knows the exact row id (avoids the activeStepRef
    // race during stage-start: startStep is async, the ManualTimer
    // mount fires onTimerStart before setActiveStep commits, and
    // without an explicit id the write lands on the PRIOR step's
    // row — exactly the "step 2 start fires step 1's timer" bug).
    // Single Timer path (no stack) doesn't pass it; falls back to
    // activeStepRef inside writeTimerState.
    try { telemetry?.markTimerStarted?.({ endsAtMs: deadlineMs, stepRowId }); } catch {}
    updateCookActivity((s) => {
      // Starting a new timer does NOT mark the previous one
      // completed. "Completed" requires explicit Done OR a natural
      // handleTimerEnd; otherwise the previous timer is just
      // abandoned (faint segment, no avatar). Auto-marking on skip
      // was confusing — Marissa's step looked finished the moment
      // Trevor moved on, even though she'd never tapped Done.
      // (Cross-helper sync at line 924 still picks up real finishes
      //  from DB finished_at — that's the source of truth.)
      //
      // If the timer being started is currently marked completed
      // (user reset it / re-started after onTimerEnd fired), drop it
      // out of completed so the dot flips back to "active" instead
      // of staying solid.
      if (idx >= 0 && s.completedTimerIndices.includes(idx)) {
        s.completedTimerIndices = s.completedTimerIndices.filter((i) => i !== idx);
      }
      s.activeTimerIndex = idx;
      s.activeTimerEndsAt = new Date(deadlineMs).toISOString();
      // startedAt = the IMPLIED original start, derived from
      // (deadline - totalSec). On a fresh start that equals now;
      // on a resume-after-disconnect (Timer remounts with the
      // persisted deadline + the recipe's original totalSec), it
      // equals the original wall-clock start time. Previously this
      // wrote `new Date()` unconditionally, which made the iOS
      // Live Activity ProgressView's timerInterval collapse to the
      // REMAINING duration after every reload — bar drained 4× as
      // fast as it should because total was now 10min instead of
      // 40min. boodaGradientColor on the iOS side keeps using
      // (endsAt - startedAt) as total; with the original startedAt
      // restored, the gradient hue scales over the full original
      // duration the way the user expects.
      const totalMs = (Number.isFinite(totalSec) && totalSec > 0)
        ? totalSec * 1000
        : Math.max(0, deadlineMs - Date.now()); // fallback: best-effort
      s.activeTimerStartedAt = new Date(deadlineMs - totalMs).toISOString();
      s.isPaused = false;
      s.pausedRemaining = null;
    });
  }, [updateCookActivity, activeStep, findActivityTimerIndex]);
  const handleTimerPause = useCallback((remainingMs) => {
    updateCookActivity((s) => {
      s.isPaused = true;
      s.pausedRemaining = Math.max(0, Number(remainingMs) || 0) / 1000;
    });
    try { telemetry?.markTimerPaused?.({ remainingMs }); } catch {}
  }, [updateCookActivity, telemetry]);
  const handleTimerResume = useCallback((newDeadlineMs) => {
    updateCookActivity((s) => {
      s.isPaused = false;
      s.pausedRemaining = null;
      s.activeTimerEndsAt = new Date(newDeadlineMs).toISOString();
      s.activeTimerStartedAt = new Date().toISOString();
    });
    try { telemetry?.markTimerResumed?.({ endsAtMs: newDeadlineMs }); } catch {}
  }, [updateCookActivity, telemetry]);
  const handleTimerEnd = useCallback(() => {
    updateCookActivity((s) => {
      const idx = s.activeTimerIndex;
      if (idx >= 0 && !s.completedTimerIndices.includes(idx)) {
        s.completedTimerIndices = [...s.completedTimerIndices, idx];
      }
      s.activeTimerIndex = -1;
      s.activeTimerEndsAt = null;
      s.activeTimerStartedAt = null;
      s.isPaused = false;
      s.pausedRemaining = null;
    });
    try { telemetry?.markTimerEnded?.(); } catch {}
  }, [updateCookActivity, telemetry]);
  const handleTimerReset = useCallback(() => {
    // Snap participants back to "READY · <nominal>" by clearing
    // timer_state. The local Live Activity also goes back to a
    // pre-start state — activeTimerIndex stays so the rail keeps
    // showing where the cook is in the recipe, just without an
    // active deadline.
    updateCookActivity((s) => {
      s.activeTimerEndsAt = null;
      s.activeTimerStartedAt = null;
      s.isPaused = false;
      s.pausedRemaining = null;
    });
    try { telemetry?.markTimerReset?.(); } catch {}
  }, [updateCookActivity, telemetry]);

  // NOTE: we intentionally DO NOT mark the session abandoned when
  // CookMode unmounts. Before, any back-button / parent-close path
  // flipped status → 'abandoned', which made the "resume your cook"
  // banner impossible — the row we'd key off was torched before the
  // user even noticed they'd navigated away. Now unmounts are treated
  // as "stepping out of the kitchen for a moment"; the session stays
  // active for up to 2h (enforced by useCookTelemetry.startCook's
  // resume window) and surfaces as a pinned top banner via
  // useActiveCookSession. Explicit abandonment is still reachable —
  // it happens when the user starts a different recipe (startCook
  // creates a fresh row) or when the 2h window lapses.

  // Wake lock — keep the screen on while CookMode is visible.
  // navigator.wakeLock is supported on Chrome/Edge/Android; silently
  // no-ops on Safari and older browsers. Re-acquires on visibility
  // change because the browser drops the lock whenever the tab is
  // backgrounded.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return undefined;
    let sentinel = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        sentinel = await navigator.wakeLock.request("screen");
      } catch (e) {
        // Battery-saver mode or permissions policy — not fatal.
        console.warn("[cookMode] wake lock denied:", e?.message || e);
      }
    };
    acquire();
    const onVis = () => {
      if (!cancelled && document.visibilityState === "visible" && (!sentinel || sentinel.released)) {
        acquire();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      try { sentinel && sentinel.release && sentinel.release(); } catch { /* ignore */ }
    };
  }, []);

  // Re-push the current activity state whenever the app comes back to
  // foreground. iOS Live Activity widgets render lazily between
  // explicit activity.update calls — without a fresh push, the lock
  // screen can render a stale snapshot even though
  // ProgressView(timerInterval:) and Text(timerInterval:) are supposed
  // to auto-tick. A no-op-shaped re-push of the current ref state
  // forces a re-render with the latest values, "waking up" the bar.
  useEffect(() => {
    const onVisRefresh = () => {
      if (document.visibilityState !== "visible") return;
      if (!cookActivityIdRef.current) return;
      pushCookActivityState();
    };
    document.addEventListener("visibilitychange", onVisRefresh);
    return () => document.removeEventListener("visibilitychange", onVisRefresh);
  }, [pushCookActivityState]);

  const [swapOpenIdx, setSwapOpenIdx] = useState(null);
  const [swapSearch, setSwapSearch] = useState("");
  const openSwapPicker = (idx) => { setSwapOpenIdx(idx); setSwapSearch(""); };
  // Non-null while the 4-phase celebration/rating/notes flow is on screen.
  // Mounts CookComplete; on finish we hand off to parent's onDone.
  const [completing, setCompleting] = useState(false);

  // Single exit-sheet entry point. The corner ✕ opens this; the
  // sheet has two explicit options:
  //   MINIMIZE → onExit (banner stays, cook keeps running, can resume)
  //   END COOK → exitCook (drops the session, no banner, no return)
  // Replaced four separate exit/minimize affordances + a two-tap
  // confirm dance that nobody could keep straight.
  const [exitSheetOpen, setExitSheetOpen] = useState(false);

  // 1Hz local-plugin tick — push the activity's content state every
  // second so the lock-screen widget re-renders and the avatar's
  // offset advances at fine granularity while the app is foreground.
  // Background path (server cron) runs at 5s; the widget's avatar
  // .animation modifier covers a 1.05s gap, which keeps foreground
  // motion smooth without stalling between server pushes when the
  // app is killed. Skip when no activity OR when the exit sheet is
  // open. Lives down here BELOW the exitSheetOpen useState because
  // referencing it in the dep array hits a temporal-dead-zone
  // ReferenceError otherwise — that crash took down the whole
  // CookMode mount.
  useEffect(() => {
    let count = 0;
    const id = setInterval(() => {
      if (!cookActivityIdRef.current) return;
      if (exitSheetOpen) return;
      pushCookActivityState();
      count += 1;
      if (count === 1 || count % 10 === 0) {
        console.info("[BoodaTimer] tick interval firing", { count, cid: cookActivityIdRef.current });
      }
    }, 1000);
    return () => clearInterval(id);
  }, [pushCookActivityState, exitSheetOpen]);

  const exitCook = async () => {
    finalizedRef.current = true;
    setExitSheetOpen(false);
    // Kill the lock-screen activity BEFORE telemetry.endCook fires
    // (which often unmounts CookMode immediately via the parent's
    // onAbandon handler). If we relied on the session-id-change
    // useEffect to clean up, the unmount can outrace it and the
    // activity would orphan on the lock screen — the next cook would
    // start a new activity stacked on top of the old one. Belt +
    // suspenders: explicit endSession here, plus endAllSessions as a
    // safety net to also kill any sibling activities from prior
    // process restarts.
    const cid = cookActivityIdRef.current;
    if (cid) {
      try { await BoodaTimer.endSession({ clientId: cid }); }
      catch (e) { console.warn("[cook-exit] BoodaTimer.endSession failed:", e?.message || e); }
      cookActivityIdRef.current = null;
    }
    // NOTE: endAllSessions used to fire here as a "safety net" to
    // catch ghost activities — but that's actively harmful in the
    // multi-cook era. With a sibling cook running on the same device
    // (different recipe), endAllSessions would drop its lock-screen
    // card too. Trust the per-session endSession above; ghosts get
    // swept by useTeamLiveActivity's once-per-process orphan sweep.
    const abandonedSessionId = telemetry?.session?.id || null;
    try { await telemetry.endCook({ status: "abandoned" }); } catch (e) {
      console.warn("[cook-exit] endCook threw:", e?.message || e);
    }
    // Abandon = the cook is over; drop the persisted mid-cook session
    // so check-offs / swaps don't resurrect on a future cook.
    resetSession();
    if (onAbandon) await onAbandon(abandonedSessionId);
    else onExit?.();
  };
  // Finish the cook → open the post-meal "what did you use?" dialogue
  // (CookComplete). Extracted from the final-step DONE button so it can
  // be triggered from ANY step via a persistent "Finish & log" button
  // and from the exit sheet. The dialogue used to be reachable ONLY by
  // tapping through to the very last step, which is why it "barely
  // fired" — finish whenever the food's actually done.
  const finishCook = () => {
    const sid = telemetry.session?.id || null;
    finalizedRef.current = true;
    const cid = cookActivityIdRef.current;
    if (cid) {
      BoodaTimer.endSession({ clientId: cid }).catch(() => {});
      cookActivityIdRef.current = null;
    }
    BoodaTimer.endAllSessions({ immediate: true }).catch(() => {});
    telemetry.finishStep({});
    telemetry.endCook({ status: "finished" });
    setCompletingSessionId(sid);
    setCompleting(true);
    setExitSheetOpen(false);
  };
  // Lapsed-cook fast path: the user tapped FINISH & LOG on a cook
  // they already know by heart — they never wanted the step list,
  // just the logging screen. Once telemetry has adopted the resumed
  // session, jump straight into the post-cook flow. Single-shot.
  const finishNowFiredRef = useRef(false);
  // Re-arm the single-shot whenever a NEW lapsed-finish intent arrives.
  // The App-level CookMode key now includes resumeSessionId/finishNow
  // so this normally remounts clean, but this reset is belt-and-braces
  // for any reuse path: without it a second FINISH & LOG found the ref
  // still true and the post-cook screen never appeared.
  useEffect(() => { finishNowFiredRef.current = false; }, [resumeSessionId, initialFinishNow]);
  useEffect(() => {
    if (!initialFinishNow || finishNowFiredRef.current) return;
    if (!telemetry.session?.id) return;   // wait for session adoption
    finishNowFiredRef.current = true;
    finishCook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFinishNow, telemetry.session?.id]);
  // Helper: skip Chadwick voice entirely on narrated recipes (the
  // cuisine-accented TTS narrator owns the audio experience for
  // those). Computed inline so this function doesn't depend on the
  // isNarrated const that lives further down the component.
  const recipeIsNarrated = () => !!(
    recipe?.audio?.intro?.url ||
    (recipe?.audio?.steps && Object.keys(recipe.audio.steps).length > 0)
  );

  const minimizeCook = () => {
    setExitSheetOpen(false);
    if (!recipeIsNarrated()) playChadwickMinimize();
    onExit?.();
  };

  // Wrapper for the top-bar minimize button (and any other user-tap
  // minimize that isn't the exit sheet's). Plays a Chadwick "I'll be
  // here" line, then defers to the parent's onExit.
  //
  // Logged: there's an open report of "minimize doesn't work after
  // resuming an old cook." The flow LOOKS right from code (onExit →
  // closeCookMode → setCookModeRecipe(null) → unmount), but something
  // is dropping the close on the resume path. Logs here will surface
  // whether onExit fires, whether playChadwickMinimize throws, and
  // what telemetry/session state was at click time.
  const handleUserMinimize = () => {
    console.log("[CookMode] handleUserMinimize TAP", {
      hasOnExit:        typeof onExit === "function",
      telemetrySession: !!telemetry?.session?.id,
      sessionId:        telemetry?.session?.id || null,
      activeStep:       telemetry?.activeStep?.step_id || null,
      view,
      joinSessionId,
      recipeTitle:      recipe?.title,
    });
    if (!recipeIsNarrated()) {
      try { playChadwickMinimize(); }
      catch (e) { console.warn("[CookMode] playChadwickMinimize threw:", e); }
    }
    try {
      onExit?.();
      console.log("[CookMode] handleUserMinimize onExit fired OK");
    } catch (e) {
      console.error("[CookMode] handleUserMinimize onExit THREW:", e);
    }
  };

  // Author + share state for THIS recipe. If the viewer owns a
  // user_recipes row matching the recipe's slug, the overview
  // exposes a SHARE WITH FAMILY toggle (otherwise no toggle — you
  // can't share someone else's recipe, and the bundled library
  // isn't share-able either). The row lookup is cheap; useUserRecipes
  // already subscribes to realtime so share state flips in place
  // after the server confirms.
  const { recipes: userRecipesList, setSharing: setRecipeSharing } = useUserRecipes(userId);
  const ownRecipeRow = useMemo(
    () => userRecipesList.find(r => r.userId === userId && r.slug === recipe?.slug) || null,
    [userRecipesList, userId, recipe?.slug],
  );
  const canShare = !!ownRecipeRow;
  const isShared = ownRecipeRow?.shared === true;
  const toggleShare = async () => {
    if (!ownRecipeRow) return;
    try {
      await setRecipeSharing(ownRecipeRow.id, { shared: !isShared });
    } catch (e) {
      console.error("[cookMode] toggleShare failed:", e);
    }
  };

  // Share sheet — the "↗ SHARE" button in the overview top bar opens
  // a picker listing FAMILY + FRIENDS. Tap a person → insert a
  // notifications row so they get pinged that this recipe was
  // shared with them. For family taps, we also flip shared=true on
  // the user_recipes row so they can actually OPEN the recipe; for
  // friends, user_recipes RLS doesn't grant visibility today, so
  // the notification plus a native-share fallback (Web Share API)
  // is the honest v1. Local `sentIds` tracks which recipients have
  // been pinged this session so the button flips to "✓ SENT" —
  // prevents accidental double-pings.
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [sentIds, setSentIds] = useState(new Set());
  const openShareSheet = () => setShareSheetOpen(true);
  const closeShareSheet = () => setShareSheetOpen(false);
  // Sender display name for the share row — lazy one-row profile
  // fetch, cached for the session so repeat shares don't re-query.
  const senderNameRef = useRef(undefined);
  const getSenderName = async () => {
    if (senderNameRef.current !== undefined) return senderNameRef.current;
    const { data } = await supabase
      .from("profiles").select("name").eq("id", userId).maybeSingle();
    senderNameRef.current = data?.name || null;
    return senderNameRef.current;
  };
  const sendShareTo = async (recipientId, isFamilyMember) => {
    if (!recipientId || !recipe || sentIds.has(recipientId)) return;
    // Optimistic mark-as-sent so the UI flips before the server round-trip.
    setSentIds(prev => new Set(prev).add(recipientId));
    try {
      // Auto-share to family so they can actually read the recipe.
      // Fire-and-forget — a failure here shouldn't block the send.
      if (isFamilyMember && canShare && !isShared && ownRecipeRow) {
        setRecipeSharing(ownRecipeRow.id, { shared: true }).catch(e =>
          console.warn("[cookMode] auto-share on send failed:", e?.message || e),
        );
      }
      // Mint a real recipe_shares row (snapshot copy) — the recipient
      // gets a push whose tap opens the accept/decline card, same as
      // the Recipe Book share path.
      await createRecipeShare({
        recipe,
        senderName: await getSenderName(),
        recipientId,
      });
    } catch (e) {
      // Roll back the optimistic sent-mark on failure.
      setSentIds(prev => {
        const next = new Set(prev);
        next.delete(recipientId);
        return next;
      });
      console.error("[cookMode] share-to-person failed:", e);
    }
  };
  // Text/email/link for "everyone else" — mint an open link share and
  // hand booda.app/r/<token> to the OS share sheet (Capacitor plugin;
  // navigator.share is unreliable inside WKWebView). Clipboard
  // fallback so the link is never lost after the row was minted.
  const [copiedLink, setCopiedLink] = useState(false);
  const mintingLinkRef = useRef(false);
  const shareViaNative = async () => {
    if (!recipe || mintingLinkRef.current) return;
    mintingLinkRef.current = true;
    try {
      const { url } = await createRecipeShare({
        recipe,
        senderName: await getSenderName(),
      });
      const title = recipe.title || "Recipe";
      try {
        await Share.share({
          title,
          text: `${title} — a recipe for you on Booda`,
          url,
        });
      } catch (shareErr) {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          setCopiedLink(true);
          setTimeout(() => setCopiedLink(false), 1800);
        }
      }
    } catch (e) {
      console.error("[cookMode] share-link failed:", e);
    } finally {
      mintingLinkRef.current = false;
    }
  };

  // Nutrition rollup for the recipe — drives the calorie tile in the
  // meta row below. Pulls from the full resolver chain (pantry
  // override → brand_nutrition → ingredient_info → bundled canonical
  // fallback) so the number reflects the user's actual stocked items
  // whenever the recipe references something they've scanned. Hooks
  // must run unconditionally; kept above the `if (!recipe)` guard.
  const ingredientInfo = useIngredientInfo();
  const { get: getBrandNutrition } = useBrandNutrition();
  const brandNutrition = useMemo(
    () => ({ get: (k) => getBrandNutrition?.(k) || null }),
    [getBrandNutrition],
  );
  // Recipe view that has cook-session swaps APPLIED — every ingredient
  // whose canonical the user swapped reads back with the SWAP'S
  // ingredientId, not the recipe's original slot. Used as the input
  // to nutrition resolvers so an ingredient swapped from milk →
  // smoked sausage gets sausage macros, not milk's. Without this
  // derivation, the inline "≈ N kcal" estimate and the totals card
  // both stay pinned to the recipe's ORIGINAL canonicals while every
  // other surface (pair text, swap chip, step prose) shows the swap.
  const recipeForNutrition = useMemo(
    () => {
      const base = scaledRecipe || recipe;
      return base ? applyCookSessionToRecipe(base, session, pantry) : null;
    },
    [scaledRecipe, recipe, session, pantry],
  );
  const nutritionSummary = useMemo(
    () => recipeForNutrition ? recipeNutrition(recipeForNutrition, { pantry, getInfo: ingredientInfo?.getInfo, brandNutrition }) : null,
    [recipeForNutrition, pantry, ingredientInfo, brandNutrition],
  );
  // Per-ingredient kcal map for the inline calorie display in the
  // overview list. Walks recipeNutritionBreakdown so each row in the
  // list can show its own contribution. Reads from recipeForNutrition
  // (the swap-applied view) so swapped ingredients show their swap's
  // calories, not the original slot's. Order preserved from
  // scaledRecipe so the array index still aligns with the rendered
  // ingredient rows.
  const perIngredientKcal = useMemo(() => {
    if (!recipeForNutrition) return [];
    const b = recipeNutritionBreakdown(recipeForNutrition, { pantry, getInfo: ingredientInfo?.getInfo, brandNutrition });
    return (b.items || []).map(it => Math.round(it.kcal || 0));
  }, [recipeForNutrition, pantry, ingredientInfo, brandNutrition]);

  // Diagnostic: expose a function on window so the user can inspect
  // why specific ingredients aren't contributing to the rollup.
  // In console: __cookDiag() returns per-ingredient breakdown with
  // a `reason` string when a row didn't resolve. Use console.table
  // for a clean view: console.table(__cookDiag().items, ["name","canonicalId","source","reason","kcal"])
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__cookDiag = () => {
      if (!scaledRecipe) return { error: "no recipe loaded" };
      return recipeNutritionBreakdown(scaledRecipe, {
        pantry,
        getInfo: ingredientInfo?.getInfo,
        brandNutrition,
      });
    };
  }, [scaledRecipe, pantry, ingredientInfo, brandNutrition]);

  // Defensive: if no recipe was passed, render nothing. Parent owns selection.
  if (!recipe) return null;

  // Project the cook session's swaps/skips into an "effective" view of
  // the recipe so step-level renders see what the overview swap UI
  // already knows about. See src/lib/effectiveRecipe.js — pure
  // derivation, never persisted.
  //
  // MEMOIZED — was unmemoized prior to 2026-05-02. Without the memo,
  // every parent re-render produced a fresh `effectiveRecipe` object
  // (the helper clones the recipe), which produced a fresh
  // `step.timers` array reference. MultiStepTimer's reset check is
  // `prevTimersRef.current !== timers` (reference equality) — so the
  // active timer reset on EVERY parent state change. Planning a meal
  // touched App-level state, App re-rendered, CookMode received the
  // same props but re-derived effectiveRecipe, the new step.timers
  // array tripped the ref check, and the timer snapped back to the
  // step's full duration. Memoizing on (scaledRecipe || recipe,
  // session, pantry) keeps the reference stable across unrelated
  // state changes; only mutations to those three inputs re-clone.
  // Pre-cook doneness questionnaire. Thickness-driven proteins in the
  // recipe (steak / chop / fillet) get a thickness + target-doneness
  // picker on the overview; the answers retune the cook step's timer and
  // bake the target internal temp into its doneCue. See src/lib/doneness.js.
  //
  // Doneness answers PERSIST so the steak's thickness + doneness survive
  // a minimize → resume. Keyed by recipe TITLE (stable; slugs drift). On
  // a fresh overview cook we auto-seed defaults; on a resume we restore
  // whatever the cook set and re-apply it (the SAME answers reproduce the
  // SAME step structure, so the resumed timer hydrates cleanly).
  const isDonenessResume = initialView === "cook" || !!joinSessionId || !!resumeSessionId;
  const donenessProteins = useMemo(
    () => donenessProteinsInRecipe(recipe, findIngredient),
    [recipe],
  );
  const donenessKey = recipe?.title ? `cookDoneness:${recipe.title}` : null;
  const [donenessAnswers, setDonenessAnswers] = useState(() => {
    try {
      const raw = donenessKey ? localStorage.getItem(donenessKey) : null;
      return raw ? (JSON.parse(raw) || {}) : {};
    } catch { return {}; }
  });
  // Persist on every change so the next mount (resume) restores it.
  // localStorage is the fast local cache; the cook session (cook_params)
  // is the authoritative cross-device source — see the restore effect.
  useEffect(() => {
    if (!donenessKey) return;
    try {
      if (donenessAnswers && Object.keys(donenessAnswers).length > 0) {
        localStorage.setItem(donenessKey, JSON.stringify(donenessAnswers));
      }
    } catch { /* private mode / quota — non-fatal */ }
  }, [donenessKey, donenessAnswers]);
  // AUTHORITATIVE restore: the cook's stored tweaks live on the cook
  // session (cook_params.doneness, migration 0273). When that arrives
  // (async on resume), adopt any protein not already set this session —
  // this is what makes resume restore the steak size reliably, instead of
  // depending on localStorage (per-device, lost on reinstall). The recipe
  // stays the immutable source; the cook carries its own tweaks.
  const sessionDoneness = telemetry?.session?.cook_params?.doneness;
  useEffect(() => {
    if (!sessionDoneness || typeof sessionDoneness !== "object") return;
    if (Object.keys(sessionDoneness).length === 0) return;
    setDonenessAnswers((prev) => {
      let next = prev;
      for (const [cid, ans] of Object.entries(sessionDoneness)) {
        if (!ans) continue;
        // Session is the source of truth — override local when it differs.
        // On a fresh cook the session was written FROM these answers, so
        // they're equal and this is a no-op (no render churn).
        if (JSON.stringify(next[cid] ?? null) === JSON.stringify(ans)) continue;
        if (next === prev) next = { ...prev };
        next[cid] = ans;
      }
      return next;
    });
  }, [sessionDoneness]);
  // Seed defaults only for a FRESH overview cook (never on resume — a
  // resumed cook already restored its answers above; auto-seeding would
  // retune a cook that never opened the questionnaire). Never clobbers an
  // answer the cook (or the restore) already set.
  useEffect(() => {
    if (isDonenessResume || donenessProteins.length === 0) return;
    setDonenessAnswers((prev) => {
      let next = prev;
      for (const p of donenessProteins) {
        if (next[p.canonicalId]) continue;
        if (next === prev) next = { ...prev };
        next[p.canonicalId] = {
          thicknessIn: defaultThicknessIn(p.canonicalId),
          donenessId: defaultDonenessFor(p.canonicalId)?.id || null,
        };
      }
      return next;
    });
  }, [donenessProteins, isDonenessResume]);
  const updateDonenessAnswer = useCallback((canonicalId, answer) => {
    setDonenessAnswers((prev) => ({ ...prev, [canonicalId]: answer }));
  }, []);

  // AUTHORITATIVE persist: once a session is live, any change to the
  // doneness/thickness tweaks must reach cook_params — startCook only
  // writes them at creation, so without this an edit made while the cook
  // is underway (or one the creation race missed) is lost on resume, and
  // the stale session value clobbers it via the restore effect above.
  // updateCookParams no-ops when nothing changed, so the restore→state→
  // persist round-trip doesn't loop or churn writes.
  const updateCookParams = telemetry.updateCookParams;
  const sessionId = telemetry.session?.id;
  useEffect(() => {
    if (!sessionId) return;
    if (!donenessAnswers || Object.keys(donenessAnswers).length === 0) return;
    updateCookParams({ doneness: donenessAnswers });
  }, [sessionId, donenessAnswers, updateCookParams]);

  const effectiveRecipe = useMemo(
    () => applyDonenessToRecipe(
      applyCookSessionToRecipe(scaledRecipe || recipe, session, pantry),
      donenessAnswers,
    ),
    [scaledRecipe, recipe, session, pantry, donenessAnswers],
  );
  const swapCount = countActiveSwaps(session);
  // Recipe-wide swap list for the per-step prose banner + inline
  // tokenizer. Built once per render; derived from
  // effectiveRecipe.ingredients so every swap — even ones the AI
  // omitted from step.uses — can surface in prose that mentions the
  // original name.
  const allSwaps = recipeSwapSummary(effectiveRecipe);
  // Brand-upgrade rewrites — same-canonical pantry rows whose branded
  // display name we lift into the recipe ("butter" → "Kerrygold
  // Butter"). Applied to prose as a plain rename BEFORE the swap
  // tokenizer runs, so branded replacements never collide with
  // strike-through markup.
  const allBrandUpgrades = recipeBrandUpgrades(effectiveRecipe);

  const steps    = effectiveRecipe.steps || [];
  const step     = steps[activeStep];
  const AnimComp = AnimationMap[step?.animation];
  const progress = steps.length ? (completedSteps.size / steps.length) * 100 : 0;

  // Live "what's physically on the counter" derivation. Walks the
  // recipe's combine-steps the cook has completed, collapsing consumed
  // ingredients into named intermediates ("Wet mixture" → "Batter") and
  // grouping whatever's left into wet / dry. Pure derivation from
  // (effectiveRecipe, completedSteps, session) — see recipeComponents.js.
  const counterComponents = useMemo(
    () => deriveCounterComponents(effectiveRecipe, {
      completedSteps,
      isAdded: (idx) => isAddedToBowl(session, idx),
    }),
    [effectiveRecipe, completedSteps, session],
  );
  // Tap-to-check an ingredient off as it goes into the bowl. Ephemeral
  // (cook-session only) — never persisted, zero DB writes.
  const toggleAddedToBowl = useCallback((idx) => {
    setOverride(idx, { addedToBowl: !isAddedToBowl(session, idx) });
  }, [session, setOverride]);
  // Display amount for a counter row — honors the user's one-off unit
  // pick (unitOverrides) then their saved preference. Same resolution as
  // the FOR THIS STEP list, so the two stay in sync.
  const counterAmountFor = (ing) => {
    const key = prefKeyForIngredient(ing);
    const override = key ? unitOverrides[key] : null;
    return override || applyPreferredUnit(ing.amount, ing, DISPLAY_CONTEXT.COOK) || ing.amount || "—";
  };
  // Tap the amount pill to change measurement units — opens the same
  // UnitPicker the FOR THIS STEP list uses.
  const onCounterPickUnit = (ing) => {
    const key = prefKeyForIngredient(ing);
    if (!key) return;
    setUnitPicker({
      key,
      ingredientId: ing.ingredientId,
      itemName: ing.item,
      amountString: String(counterAmountFor(ing)),
    });
  };

  // Bucket recipe ingredients by pantry status. Ingredients tagged with an
  // Live pairing pass against CURRENT pantry — intentionally
  // computed fresh every render, NEVER persisted on the recipe.
  // The recipe stores canonical + dietaryClaims intent only; which
  // specific pantry row backs each ingredient is a live decision
  // so brand/availability drift a month from now doesn't fossilize
  // a stale pair.
  //
  // Swaps/skips are already baked into effectiveRecipe.ingredients
  // (via applyCookSessionToRecipe) — swapped entries carry
  // pantryItemId for the Tier 0 short-circuit. Passing
  // ingredientInfo lets the pairer pull DB-overlay substitutions
  // (admin-promoted synthetic canonicals) on top of the bundled
  // INGREDIENT_INFO[id].substitutions table.
  const ingredientPairings = pairRecipeIngredients(
    effectiveRecipe.ingredients || [],
    pantry || [],
    { ingredientInfo },
  );

  // Status checklist derives from the SAME pairing pass — no parallel
  // findInPantry / sameCanonicalFamily walk that contradicts the
  // italic pairing line below. Effective recipe is used so cook-time
  // swaps (session.overrides.pantryItemId) reflect into the badge,
  // not just the italic.
  const ingredientStatus = (effectiveRecipe.ingredients || [])
    .map((ing, i) => statusFor(ing, ingredientPairings[i] || null));

  // Look up a pairing by an ingredient identity (canonical id first,
  // normalized name second). Same key strategy applyCookSessionToRecipe
  // uses internally, so step.uses entries (which carry their own
  // ingredientId from the recipe) line up with effectiveRecipe.ingredients
  // pairings. Built here so the step-render scope can reach matched
  // pantry rows without re-running pairRecipeIngredients per step.
  const pairingByKey = new Map();
  (effectiveRecipe.ingredients || []).forEach((ing, i) => {
    const p = ingredientPairings[i];
    if (!p) return;
    if (ing.ingredientId) pairingByKey.set(`id:${ing.ingredientId}`, p);
    const nm = normalizeForMatch(ing.item || "");
    if (nm) pairingByKey.set(`nm:${nm}`, p);
  });
  const findPairing = (ing) => {
    if (!ing) return null;
    if (ing.ingredientId) {
      const hit = pairingByKey.get(`id:${ing.ingredientId}`);
      if (hit) return hit;
    }
    const nm = normalizeForMatch(ing.item || "");
    if (nm) return pairingByKey.get(`nm:${nm}`) || null;
    return null;
  };

  // Per-ingredient amount substitutions for the prose tokenizer:
  // when the user picks a different unit on the FOR-THIS-STEP chip
  // (or has a saved preference that resolves to a different unit),
  // the displayed amount on the chip drifts from the recipe's
  // authored amount string. Push the (authored → displayed) pairs
  // into the prose tokenizer so the inline instruction's measurements
  // track the chip the cook is reading from. Skipped when the chip
  // matches the recipe (no rewrite needed) and when the recipe author
  // omitted ing.amount (nothing to find/replace).
  //
  // Servings scaling + unit preference are COMPOSED into a single
  // pair per ingredient: from = the ORIGINAL authored amount (what
  // step prose carries verbatim), to = the FINAL displayed amount
  // (servings-scaled, then run through the user's unit preference).
  // effectiveRecipe.ingredients[i].amount is already servings-scaled
  // (effectiveRecipe is built from scaledRecipe) and is 1:1 with
  // baseIngredients[i] (both shape-preserving maps).
  //
  // Why ONE composed pair instead of two sequential lists: the prose
  // tokenizer does a single, non-re-scanning replace. Two separate
  // global passes (original→scaled, then scaled→preferred) let one
  // ingredient's scaled string get re-matched by another ingredient's
  // original-amount key — e.g. butter "2 tbsp"→"4 tbsp" then re-caught
  // by oil's "4 tbsp"→"8 tbsp", rendering butter as 8 tbsp. Composing
  // to {original → final} collapses the chain so each authored string
  // maps straight to its end state in one shot.
  const baseIngredients = recipe?.ingredients || [];
  const allAmountReplacements = (effectiveRecipe.ingredients || [])
    .map((ing, i) => {
      const original = baseIngredients[i];
      if (!original || !original.amount || !ing.amount) return null;
      const overrideKey = prefKeyForIngredient(ing);
      const overrideAmount = overrideKey ? unitOverrides[overrideKey] : null;
      const displayed = overrideAmount
        || applyPreferredUnit(ing.amount, ing, DISPLAY_CONTEXT.COOK)
        || ing.amount;
      const fromStr = String(original.amount).trim();
      const toStr   = String(displayed).trim();
      if (!fromStr || !toStr || fromStr === toStr) return null;
      return { from: fromStr, to: toStr };
    })
    .filter(Boolean);

  // Convert-anchored prose targets: shared helper so AIRecipe's
  // preview render and ReheatMode use identical rewrite rules. We
  // pre-resolve each ingredient's chip-displayed amount (transient
  // session override > saved unit pref > authored) and hand the list
  // off — the helper does the canonical lookup, unit parsing, and
  // anchor-name assembly. Drives the "4 tbsp of butter (out of 4
  // sticks total)" → "½ stick of butter" rewrite when the cook
  // flips butter's display unit.
  const unitConversionTargets = buildUnitConversionTargets(
    (effectiveRecipe.ingredients || []).map(ing => {
      const overrideKey = prefKeyForIngredient(ing);
      const overrideAmount = overrideKey ? unitOverrides[overrideKey] : null;
      const displayedAmount = overrideAmount
        || applyPreferredUnit(ing.amount, ing, DISPLAY_CONTEXT.COOK)
        || ing.amount;
      return { ...ing, amount: displayedAmount };
    }),
  );

  const missingIngs    = ingredientStatus.filter(s => s.status === "missing");
  const lowIngs        = ingredientStatus.filter(s => s.status === "low");
  const wrongStateIngs = ingredientStatus.filter(s => s.status === "wrong-state");
  const okCount        = ingredientStatus.filter(s => s.status === "ok").length;
  const trackedCount   = ingredientStatus.filter(s => s.status !== "skip").length;
  // Items with no canonical ID that the pairing pass still found to be absent from
  // the pantry (name-match failed). statusFor returns "skip" for these so they
  // don't inflate the PANTRY N/M counter, but they still need shopping affordances.
  const untrackedMissing = ingredientStatus
    .map((s, i) => ({ ...s, pairing: ingredientPairings[i] }))
    .filter(s => s.status === "skip" && s.pairing?.status === "missing");
  const allMissing = [...missingIngs, ...untrackedMissing];

  const addMissingToShoppingList = () => {
    if (!setShoppingList) return;
    const toAdd = [...allMissing, ...lowIngs];
    if (toAdd.length === 0) return;
    setShoppingList(prev => {
      // de-dupe by ingredientId when present, else by name
      const existing = new Set(prev.map(i => i.ingredientId || i.name.toLowerCase()));
      const next = [...prev];
      toAdd.forEach(({ ing, row }) => {
        // Canonical backfill: Claude may leave ing.ingredientId null
        // for "staples it assumed" or newly-introduced ingredients
        // (per generate-recipe/index.ts:447). Run the item text
        // through inferCanonicalFromName to hit any bundled canonical
        // that substring-matches — that stamps ingredientId on the
        // shopping-list row so the downstream receipt-scan bias
        // (+30 for ingredientId match) fires, not just the weaker
        // +20 name-substring tier. No AI call — cheap local lookup.
        const candidateName = row?.name || ing.item;
        const resolvedId = ing.ingredientId
          || inferCanonicalFromName(candidateName)
          || null;
        const canonical = findIngredient(resolvedId);
        const key = resolvedId || candidateName.toLowerCase();
        if (existing.has(key)) return;
        existing.add(key);
        next.push({
          id: crypto.randomUUID(),
          ingredientId: resolvedId,
          name: canonical?.name || row?.name || ing.item,
          emoji: canonical?.emoji || row?.emoji || "🥫",
          amount: ing.qty?.amount ?? 1,
          unit: ing.qty?.unit ?? (row?.unit || "unit"),
          category: canonical?.category || row?.category || "pantry",
          source: "recipe",
        });
      });
      return next;
    });
    setJustAdded(toAdd.length);
    setTimeout(() => setJustAdded(0), 3500);
  };

  // Single-row "+ SHOP" — mirrors addMissingToShoppingList's canonical-
  // backfill logic but scoped to one ingredient at a time. Marks the
  // row as added locally so the button flips to ✓ without a re-render
  // race on the parent's shopping list.
  const addOneToShoppingList = (ing, row, idx) => {
    if (!setShoppingList) return;
    if (session.overrides[idx]?.promotedToShopping) return;
    setShoppingList(prev => {
      const existing = new Set(prev.map(i => i.ingredientId || i.name.toLowerCase()));
      const candidateName = row?.name || ing.item;
      const resolvedId = ing.ingredientId
        || inferCanonicalFromName(candidateName)
        || null;
      const canonical = findIngredient(resolvedId);
      const key = resolvedId || candidateName.toLowerCase();
      if (existing.has(key)) return prev;
      return [...prev, {
        id: crypto.randomUUID(),
        ingredientId: resolvedId,
        name: canonical?.name || row?.name || ing.item,
        emoji: canonical?.emoji || row?.emoji || "🥫",
        amount: ing.qty?.amount ?? 1,
        unit: ing.qty?.unit ?? (row?.unit || "unit"),
        category: canonical?.category || row?.category || "pantry",
        source: "recipe",
      }];
    });
    setOverride(idx, { promotedToShopping: true });
  };

  // Rank every pantry row by closeness to a recipe ingredient. Same
  // signal stack as AIRecipe's swap picker: exact canonical (+1000),
  // same hub (+500), same category (+100), plus token overlap. When
  // the user is typing we narrow to substring-matches on the pantry
  // row name so the list feels like a live search, not a rank.
  //
  // Returns { pantry, library } — pantry is the existing "rows the cook
  // already owns" ranking; library surfaces bundled canonicals the cook
  // can legitimately substitute even if they're not in the pantry
  // (2% ↔ whole milk, carrot ↔ celery, chicken ↔ steak). Sources:
  //   1. Author-curated substitutions on the canonical's info.
  //   2. Same-hub siblings (milk_hub → 2%, whole, skim, oat).
  // Library candidates that collide with a pantry canonical are pruned
  // so the user sees one row per ingredient, not two.
  const rankSwapCandidates = (ing, query) => {
    const targetCanon = ing.ingredientId ? findIngredient(ing.ingredientId) : null;
    const targetCat  = targetCanon?.category || null;
    const targetSlug = targetCanon?.id || null;
    const targetHub  = targetCanon?.parentId || targetCanon?.id || null;
    const targetTokens = new Set(
      normalizeForMatch(ing.item || ing.name || targetCanon?.name || "")
        .split(/\s+/).filter(Boolean),
    );
    const q = (query || "").trim().toLowerCase();
    const pantrySeen = new Set();
    const pantryScored = [];
    for (const p of pantry || []) {
      if (!p) continue;
      const canon = p.ingredientId ? findIngredient(p.ingredientId) : null;
      const key = canon?.id || (p.name || "").toLowerCase() || p.id;
      if (pantrySeen.has(key)) continue;
      pantrySeen.add(key);
      if (q && !(p.name || "").toLowerCase().includes(q)) continue;
      let score = 0;
      if (canon?.id && targetSlug && canon.id === targetSlug) score += 1000;
      const pHub = canon?.parentId || canon?.id;
      if (pHub && targetHub && pHub === targetHub) score += 500;
      if (canon?.category && targetCat && canon.category === targetCat) score += 100;
      const pTokens = new Set(
        normalizeForMatch(p.name || "").split(/\s+/).filter(Boolean),
      );
      let overlap = 0;
      for (const t of targetTokens) if (pTokens.has(t)) overlap++;
      score += overlap * 10;
      pantryScored.push({ row: p, score });
    }
    pantryScored.sort((a, b) => b.score - a.score);

    // Library candidates — curated substitutions + hub siblings. Score
    // scale mirrors pantry: hub sibling +500, curated entry +400 (below
    // hub so same-hub alternates top the list even without author
    // curation), category match +100, token overlap +10 each. No +1000
    // tier — a library entry with the target's own canonical would be
    // "swap X for X," which is nonsense; we filter those out below.
    const librarySeen = new Set(pantrySeen); // don't re-surface pantry canonicals as library
    if (targetSlug) librarySeen.add(targetSlug);
    const libraryMap = new Map(); // canonId → { canonical, score, note }
    const addLibraryCandidate = (canonical, baseScore, note = null) => {
      if (!canonical?.id || librarySeen.has(canonical.id)) return;
      if (q && !canonical.name.toLowerCase().includes(q)) return;
      let score = baseScore;
      if (canonical.category && targetCat && canonical.category === targetCat) score += 100;
      const cTokens = new Set(
        normalizeForMatch(canonical.name || "").split(/\s+/).filter(Boolean),
      );
      let overlap = 0;
      for (const t of targetTokens) if (cTokens.has(t)) overlap++;
      score += overlap * 10;
      const prev = libraryMap.get(canonical.id);
      if (!prev || prev.score < score) {
        libraryMap.set(canonical.id, { canonical, score, note: note || prev?.note || null });
      }
    };

    // 1. Curated substitutions from the ingredient's info. These are
    //    author-vetted and carry a short note ("accept that the result
    //    will be more assertive", etc.) — the best-quality signal.
    if (targetCanon) {
      const dbInfo = ingredientInfo?.getInfo ? ingredientInfo.getInfo(targetCanon.id) : null;
      const info = getIngredientInfo(targetCanon, dbInfo);
      for (const sub of info?.substitutions || []) {
        if (!sub?.id) continue;
        const subCanon = findIngredient(sub.id);
        if (!subCanon) continue;
        addLibraryCandidate(subCanon, 400, sub.note || null);
      }
    }
    // 2. Same-hub siblings (milk variants, onion variants, etc.) —
    //    automatic coverage for ladders like 2%/whole/skim even when
    //    nobody authored a substitutions entry.
    if (targetSlug) {
      for (const sib of siblingsInHub(targetSlug)) {
        addLibraryCandidate(sib, 500, null);
      }
    }
    // 3. Free-text search over the full bundled registry when the cook
    //    types a query. Without this, typing "celery" on a carrot
    //    wouldn't surface celery unless an author had already curated
    //    that link — which is exactly the "swap is extremely limited"
    //    complaint. Low base score (50) keeps curated / hub matches
    //    on top when both apply; same-category adds +100 so e.g.
    //    searching "steak" from chicken surfaces sirloin above
    //    off-category collisions. Only fires with a query to avoid
    //    dumping 387 candidates into the picker on open.
    if (q && q.length >= 2) {
      // Both tiers — bundled + synthetic (dbCanonicals) — so a cook
      // can swap in a user-minted canonical (spam, gjetost) the same
      // as any bundled one.
      for (const ing of allCanonicals()) {
        if (!ing?.name || !ing.name.toLowerCase().includes(q)) continue;
        addLibraryCandidate(ing, 50, null);
      }
    }

    const libraryScored = Array.from(libraryMap.values())
      .sort((a, b) => b.score - a.score);

    return { pantry: pantryScored, library: libraryScored };
  };

  const applySwap = (idx, pantryItemId) => setOverride(idx, {
    pantryItemId,
    // A pantry swap clears any prior library swap on the same slot so
    // the override shape stays single-valued. effectiveRecipe.js would
    // otherwise see both set and have to pick a precedence.
    swapCanonicalId: undefined,
    swapCanonicalName: undefined,
    swapCanonicalEmoji: undefined,
  });
  // Library swap — cook picked a canonical that isn't in pantry. We
  // carry the display name + emoji on the override so the cook-surface
  // renderer can show the ingredient without re-resolving the canonical
  // every render. effectiveRecipe.js applies this by producing a
  // swapped ingredient slot that has no pantryItemId — pairing stays
  // "missing" against the pantry (correct: cook said "I'm using
  // celery" but didn't stock any), while steps / ingredient lists
  // reflect the substitution. See applyCookSessionToRecipe.
  const applyLibrarySwap = (idx, canonical) => setOverride(idx, {
    swapCanonicalId:    canonical.id,
    swapCanonicalName:  canonical.name,
    swapCanonicalEmoji: canonical.emoji,
    pantryItemId: undefined,
  });
  const clearSwap = (idx) => clearOverride(idx, [
    "pantryItemId", "swapCanonicalId", "swapCanonicalName", "swapCanonicalEmoji",
  ]);

  const markDone = () => {
    console.log(`[CookMode:markDone] entry activeStep=${activeStep} stepsLen=${steps.length}`);
    setCompletedSteps(s => new Set([...s, activeStep]));
    // Stamp the step as finished + cancel any pending timer push.
    // No-op if telemetry isn't tracking this step (e.g. session
    // creation hasn't landed yet on a fast-tap user).
    try { telemetry.finishStep({}); } catch (e) { console.warn("[CookMode:markDone] telemetry.finishStep threw:", e); }
    // Stop this step's timer(s) when it's an ACTIVE (hands-on) step —
    // a sear/whisk/sauté you've finished shouldn't keep ticking in the
    // background banner or the Live Activity. PASSIVE steps (preheat,
    // chill, rest, proof, marinate) deliberately KEEP running: advancing
    // past them is the "your timer is safe, start the next prep" model.
    // finishStep only stamps finished_at; the timer display keys on
    // timer_state.ended, so we must explicitly end active timers here.
    try {
      const curStep = steps[activeStep];
      const curText = `${curStep?.title || ""} ${curStep?.instruction || curStep?.body || curStep?.text || ""}`;
      const PASSIVE_RE = /\b(rise|proof|prove|ferment|bake|roast|simmer|rest|cool|let\s+(sit|stand|cool|rest)|braise|smoke|chill|steep|marinate|set\s+aside|hold|reduce\s+to\s+a\s+simmer)\b/i;
      const CONTINUOUS_RE = /\b(stir|whisk|fold|knead|flip|baste|toss)\s+(constantly|continuously|frequently|every\s+\d+|until)/i;
      const isPassive = PASSIVE_RE.test(curText) && !CONTINUOUS_RE.test(curText);
      if (curStep && !isPassive && telemetry.endStepTimers) {
        Promise.resolve(telemetry.endStepTimers(curStep.id))
          .catch((e) => console.warn("[CookMode:markDone] endStepTimers threw:", e));
      }
    } catch (e) { console.warn("[CookMode:markDone] active-timer stop threw:", e); }
    // Satisfying tactile confirmation the step was logged — quieter
    // than the timer chime so rapid step-taps don't sound like a
    // malfunctioning alarm clock.
    try { playStepCompleteChime(); } catch (e) { console.warn("[CookMode:markDone] playStepCompleteChime threw:", e); }
    if (activeStep < steps.length - 1) {
      console.log(`[CookMode:markDone] scheduling advance ${activeStep} → ${activeStep + 1} in 300ms`);
      setTimeout(() => {
        console.log(`[CookMode:markDone] advancing now (functional setActiveStep)`);
        setActiveStep(s => s + 1);
      }, 300);
    } else {
      console.log(`[CookMode:markDone] last step — not advancing`);
    }
  };

  // Tell BackgroundMusic that CookMode owns the airwaves while
  // mounted. Suppresses ad scheduling AND stops any in-flight ad so
  // Chadwick is the only voice the cook hears mid-recipe. Also
  // Recipes that have TTS narration generated (admin tapped "🎙
  // Generate narration") get a different audio experience: every
  // step plays its own ElevenLabs MP3 via the inline <audio> element.
  // Chadwick's pre-recorded step lines + idle chit-chat would
  // collide ("yapping" over the cuisine-accented narrator), so we
  // suppress all of them when the recipe is narrated. Gate on the
  // PRESENCE of audio.intro or any audio.steps entry — covers both
  // intro-only and step-narrated recipes uniformly.
  const isNarrated = !!(
    recipe?.audio?.intro?.url ||
    (recipe?.audio?.steps && Object.keys(recipe.audio.steps).length > 0)
  );

  // arms the idle chit-chat scheduler — Chadwick fires a random
  // idle line every 90s-3min between step transitions, like the
  // radio ads but cook-mode-only and non-colliding with the actual
  // step audio. Fires once on mount, once on unmount.
  // Skipped entirely on narrated recipes — see isNarrated above.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.dispatchEvent(new CustomEvent("cookmode:active"));
    if (!isNarrated) startIdleChitChat();
    return () => {
      if (!isNarrated) stopIdleChitChat();
      window.dispatchEvent(new CustomEvent("cookmode:inactive"));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNarrated]);

  // Chadwick voice line on every step change (and on first entry to
  // cook view). Each step has a per-folder rotation pool in
  // /public/mp3/chadwick-cooking/steps/step-N/; the picker draws
  // randomly so the same line doesn't get stale. Final step swaps to
  // a random `final-plate` line — Chadwick's signoff about the dish.
  // Fire-and-forget; failures are silent.
  //
  // Session-scoped dedupe: passing telemetry.session.id makes the
  // helper fire AT MOST ONCE per (sessionId, stepIndex) pair, so
  // re-visiting a step (PREV button, banner-resume on the same step)
  // doesn't re-narrate. A fresh cook resets because sessionId is new.
  //
  // GATE on a real sessionId: on resume-from-banner, CookMode mounts
  // with view="cook" + activeStep=N before telemetry.startCook has
  // adopted the existing session row (the row exists; the hook just
  // hasn't fetched it yet). If we fire here with sessionId=null, the
  // dedupe inside chadwickAudio is bypassed (the helper only dedupes
  // when sessionId is truthy) and the step audio replays — exactly the
  // "minimizing retriggers step audio" symptom. Waiting until the id
  // resolves means dedupe sees heard.has(N)===true and correctly
  // suppresses. Same gate also fixes the fresh-cook step-0 double-play.
  //
  // No cleanup-stop — when the user advances steps, the next
  // playChadwickStep call handles the interrupt internally (same-
  // kind audio swap inside chadwickAudio.js). Letting the cleanup
  // hard-stop here also killed the minimize voice line on unmount,
  // which is what the user actually wants to hear as the cook
  // recedes to the banner.
  const cookSessionId = telemetry?.session?.id || null;
  useEffect(() => {
    if (view !== "cook") return undefined;
    if (!Array.isArray(steps) || steps.length === 0) return undefined;
    if (!cookSessionId) return undefined;
    // Narrated recipes get the inline TTS player handling step audio
    // entirely — Chadwick stays quiet so two voices don't talk over
    // each other. See isNarrated above.
    if (isNarrated) return undefined;
    playChadwickStep(activeStep, steps.length, recipe, cookSessionId);
    return undefined;
  }, [view, activeStep, steps?.length, recipe, cookSessionId, isNarrated]);

  // Hands-free voice commands. Mic only opens when there's an ACTIVE
  // cook session AND the user has hands-free toggled on in Settings.
  // Wake word is "Hey Booda" — that's the entire vocabulary. Each
  // wake-detection advances the cook by one step. The post-wake STT
  // pipeline was removed 2026-05-06 because it never worked reliably
  // and the second mic stream was disrupting the wake-word listener.
  const handsFreeOn = isHandsFreeEnabled();
  const voiceEnabled = handsFreeOn && !!telemetry?.session && view === "cook";
  // Gate diagnostic — the persistent voice pill + recognizer only mount
  // when voiceEnabled is true. If voice "isn't working" and no pill shows,
  // this line names which of the three inputs is false (vs. the recognizer
  // itself being dead, which would show the pill RED). Logged on change
  // only so it doesn't flood the console on every timer tick.
  useEffect(() => {
    console.log(`[CookMode:voice] gate handsFreeOn=${handsFreeOn} hasSession=${!!telemetry?.session} view=${view} → voiceEnabled=${voiceEnabled}`);
  }, [handsFreeOn, voiceEnabled, view, telemetry?.session]);
  const handleVoiceCommand = (cmd) => {
    console.log(`[CookMode:voice] received cmd=${cmd} activeStep=${activeStep} stepsLen=${steps.length} isLastStep=${activeStep >= steps.length - 1}`);
    if (cmd === "NEXT") {
      console.log(`[CookMode:voice] calling markDone()`);
      markDone();
    } else if (cmd === "BACK") {
      // Mirror the manual ← PREV button. Bottom-clamps at step 0.
      setActiveStep((s) => Math.max(0, s - 1));
    } else if (cmd === "REPEAT") {
      // Branch on whether THIS step has narration audio, not the
      // global isNarrated flag — a recipe with intro-only narration
      // has isNarrated=true but no per-step <audio> mounted to
      // receive the replay event, which silently no-ops.
      const currentStep = steps[activeStep];
      const stepNarrationUrl = currentStep ? recipe?.audio?.steps?.[currentStep.id]?.url : null;
      console.log(`[CookMode:voice] REPEAT isNarrated=${isNarrated} stepNarrationUrl=${stepNarrationUrl ? "yes" : "no"}`);
      if (stepNarrationUrl) {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("cookmode:request-narration-replay"));
        }
      } else {
        try { replayChadwickStep(activeStep, steps.length); }
        catch (e) { console.warn("[CookMode] repeat failed:", e); }
      }
    } else if (cmd === "TIMER_START" || cmd === "TIMER_PAUSE" || cmd === "TIMER_RESET") {
      // Three synthetic-tap events for the active Timer. Mirrors
      // the `cookmode:timer-started` channel already in use —
      // dispatched by voice, consumed by the Timer instance.
      if (typeof window !== "undefined") {
        const eventName = {
          TIMER_START: "cookmode:request-timer-start",
          TIMER_PAUSE: "cookmode:request-timer-pause",
          TIMER_RESET: "cookmode:request-timer-reset",
        }[cmd];
        window.dispatchEvent(new CustomEvent(eventName));
      }
    }
  };
  const voice = useVoiceCommands({ enabled: voiceEnabled, onCommand: handleVoiceCommand });

  // Track narration state so the voice pill can distinguish "engine
  // genuinely stopped" from "engine intentionally suspended because
  // Booda is speaking." Step 1 frequently shows up here — the intro/
  // step-1 narration is playing, the listener is suspended, and the
  // user otherwise can't tell why the mic is off.
  const [narrating, setNarrating] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onStart = () => setNarrating(true);
    const onEnd = () => setNarrating(false);
    window.addEventListener("chadwick:speak-start", onStart);
    window.addEventListener("chadwick:speak-end", onEnd);
    return () => {
      window.removeEventListener("chadwick:speak-start", onStart);
      window.removeEventListener("chadwick:speak-end", onEnd);
    };
  }, []);

  // Cook-mode chat sheet — "ASK ABOUT THIS STEP." Closed by default;
  // never auto-fires. Per the egress rule, the edge function is only
  // hit when the user submits a question or taps a quick-question chip.
  const [chatOpen, setChatOpen] = useState(false);

  const timeLabel = `${totalTimeMin(recipe)} min`;
  const diffLabel = difficultyLabel(recipe.difficulty);

  if (view === "overview") return (
    <div style={{
      // Top inset honors iOS safe area (viewport-fit=cover extends the
      // page under the status bar / Dynamic Island). Without it, the
      // back arrow and SHARE button get clipped by the system clock.
      padding:"calc(20px + env(safe-area-inset-top)) 24px 40px",
      maxWidth:480, margin:"0 auto",
    }}>
      {/* Top bar — left: ↑ MINIMIZE when a cook is active (banner
          stays alive, can resume), or ← back when no session yet
          (just closes the recipe). Right: ↗ share-arrow for the
          native share sheet. Cancel-cook lives as a subtle link at
          the bottom; it's a real action, not a corner accessory. */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
        {onExit ? (
          telemetry.session ? (
            <button
              onClick={handleUserMinimize}
              title="Step out — timer keeps running"
              aria-label="Minimize cook"
              style={{
                display:"inline-flex", alignItems:"center", gap:8,
                background:COLOR.soil, border:`1px solid ${COLOR.border}`, color:COLOR.dim,
                borderRadius:20, padding:"8px 14px",
                fontFamily:FONT.mono, fontSize:10, letterSpacing:"0.14em",
                cursor:"pointer",
              }}
            >
              <span style={{ fontSize:14, lineHeight:1 }}>↑</span>
              MINIMIZE
            </button>
          ) : (
            <button
              onClick={onExit}
              title="Back"
              aria-label="Back"
              style={{
                background:"none", border:"none", color:COLOR.muted, fontSize:22,
                cursor:"pointer", padding:0,
              }}
            >←</button>
          )
        ) : <span />}
        <button
          onClick={openShareSheet}
          title="Share recipe"
          aria-label="Share recipe"
          style={{
            display:"inline-flex", alignItems:"center", gap:6,
            background: COLOR.goldDeep,
            border: `1px solid ${COLOR.goldDim}`,
            color: COLOR.gold,
            borderRadius:20, padding:"10px 16px",
            fontFamily:FONT.mono, fontSize:12, fontWeight:700,
            letterSpacing:"0.12em", cursor:"pointer",
          }}
        >
          <span style={{ fontSize:16, lineHeight:1 }}>↗</span>
          SHARE
        </button>
      </div>
      <div style={{ marginTop:12 }}>
        <div style={{ fontFamily:FONT.mono, fontSize:11, color:COLOR.gold, letterSpacing:"0.15em", marginBottom:8 }}>
          {(recipe.cuisine || "").toUpperCase()} · {(recipe.category || "").toUpperCase()}
        </div>
        <h1 style={{ fontFamily:FONT.itemTitle, fontSize:42, fontWeight:300, lineHeight:1.05, letterSpacing:"-0.03em" }}>{recipe.title}</h1>
        {recipe.subtitle && (
          <p style={{ fontFamily:FONT.forma, fontSize:16, color:COLOR.dim, marginTop:6, lineHeight:1.45 }}>{recipe.subtitle}</p>
        )}
        {/* Technique-check receipt. The orchestrator re-runs at
            read-time and the badge renders ONLY when a sub-validator
            made a real correction this pass (autoFixed.length > 0).
            Clean recipes show no badge — absence is the signal that
            nothing needed adjustment. Avoids the prior "trust sticker
            on every recipe" noise. */}
        <TechniqueCheckBadge diagnostics={techniqueDiagnostics} />

        {/* SHARE WITH FAMILY — a proper flippable on/off switch, not
            a click-to-toggle pill. Visual language matches the
            notification-opt-in toggles in SchedulePicker so the two
            family-facing controls feel like siblings. Only rendered
            when the viewer owns this recipe; bundled and family-
            authored recipes skip this row entirely. The whole row is
            tappable (label included) so the target area is generous
            on touch. */}
        {canShare && (
          <button
            onClick={toggleShare}
            title={isShared ? "Tap to make private" : "Tap to share with family"}
            style={{
              marginTop: 14, width: "100%",
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 14px",
              background: isShared ? COLOR.leafDeep : COLOR.ground,
              border: `1px solid ${isShared ? COLOR.leafDim : COLOR.edge}`,
              borderRadius: 12, cursor: "pointer",
            }}
          >
            <div style={{
              width: 42, height: 24, borderRadius: 12, flexShrink: 0,
              background: isShared ? COLOR.leafMuted : COLOR.border,
              position: "relative", transition: "background 0.2s",
            }}>
              <div style={{
                position: "absolute", top: 2, left: isShared ? 20 : 2,
                width: 20, height: 20, borderRadius: "50%",
                background: isShared ? COLOR.leafDeep : COLOR.dim,
                transition: "left 0.2s",
              }} />
            </div>
            <div style={{ flex: 1, textAlign: "left" }}>
              <div style={{
                fontFamily: FONT.forma, fontSize: 14,
                color: isShared ? COLOR.ink : COLOR.dim,
              }}>
                Share with family
              </div>
              <div style={{
                fontFamily: FONT.mono, fontSize: 10,
                color: COLOR.muted, letterSpacing: "0.06em", marginTop: 2,
              }}>
                {isShared
                  ? "Family can cook this recipe"
                  : "Only you can see this recipe"}
              </div>
            </div>
          </button>
        )}
      </div>
      <div style={{ display:"flex", gap:12, marginTop:24 }}>
        {[["⏱", timeLabel],["📊", diffLabel],["👥",`Serves ${recipe.serves}`]].map(([icon,val])=>(
          <div key={val} style={{ flex:1, background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:10, padding:"12px 8px", textAlign:"center" }}>
            <div style={{ fontSize:18, marginBottom:4 }}>{icon}</div>
            <div style={{ fontFamily:FONT.mono, fontSize:11, color:COLOR.dim }}>{val}</div>
          </div>
        ))}
      </div>
      {/* Pre-cook servings stepper. The recipe ships at recipe.serves
          (Claude's typical default for AI; the author's choice for
          bundled / custom). Cook can adjust before tapping START
          COOKING — ingredient amounts scale via scaleRecipe(), and
          the nutrition rollup below recomputes from the scaled
          recipe so per-serving + total kcal both reflect the
          chosen size. Setting persists for this CookMode session
          only; reopens reset to recipe.serves. */}
      <div style={{
        marginTop: 16, display: "flex", alignItems: "center", gap: 10,
        padding: "8px 12px",
        background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
        borderRadius: 10,
      }}>
        <span style={{
          flex: 1, fontFamily: FONT.forma, fontSize: 12, fontWeight: 500, color: COLOR.dim,
        }}>
          Cooking for
        </span>
        <ServingsStepper
          value={servingsOverride ?? Math.max(1, Number(recipe.serves) || 1)}
          // A tap here is the one EXPLICIT servings intent in CookMode
          // — it outranks seeds and session restores, and it's the
          // only source the debounced session patch writes for.
          onChange={(n) => { servingsSourceRef.current = "user"; setServingsOverride(n); }}
          palette={{
            surface: COLOR.soil, btnBg: COLOR.deep, btnBorder: COLOR.border,
            accent: COLOR.gold, accentSoft: COLOR.goldDeep,
            ink: COLOR.ink, muted: COLOR.muted,
            fontLabel: FONT.forma, fontNum: FONT.forma,
          }}
        />
      </div>
      {/* Calorie + macros rollup — per-serving + total + coverage.
          Reads from recipeForNutrition (the swap-applied view), so
          when the user swaps an ingredient the totals + coverage
          count update to match the swap's macros instead of staying
          pinned to the recipe's original slot. */}
      <RecipeNutritionLine
        recipe={recipeForNutrition || scaledRecipe || recipe}
        pantry={pantry}
        ingredientInfo={ingredientInfo}
        brandNutrition={brandNutrition}
      />
      {/* Pre-cook doneness questionnaire — thickness + target doneness
          for any steak / chop / fillet in the recipe. Adjusts the cook
          step's timer + bakes the target internal temp into its doneCue.
          Renders nothing when the recipe has no thickness-driven protein. */}
      <CookDonenessGate
        proteins={donenessProteins}
        answers={donenessAnswers}
        onChange={updateDonenessAnswer}
      />
      {/* Minute-by-minute step breakdown so the cook can see exactly
          how the time will be spent before tapping START COOKING.
          Reads the doneness-adjusted effectiveRecipe so retuned sear
          timers show here too. Renders nothing when steps lack timers. */}
      <StepTimeline recipe={effectiveRecipe} />
      <div style={{ marginTop:28 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, gap:10, flexWrap:"wrap" }}>
          <div style={{ fontFamily:FONT.mono, fontSize:11, color:COLOR.muted, letterSpacing:"0.12em" }}>INGREDIENTS</div>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            {/* Active-swap badge — reassurance chip. Tells the user the
                ephemeral cook-session layer is doing its job: swaps/skips
                are active on this cook but the saved recipe is still
                intact. Hidden when no swaps, so the overview stays clean
                for first-pass cooks. */}
            {swapCount > 0 && (
              <div
                title="Your swaps only apply to this cook. The saved recipe is unchanged."
                style={{ fontFamily:FONT.mono, fontSize:10, color:CHIP_TONES.canonical.fg, background:COLOR.goldDeep, border:"1px solid #2f2820", padding:"3px 8px", borderRadius:6, letterSpacing:"0.1em", fontWeight:600 }}
              >
                {swapCount} SWAP{swapCount === 1 ? "" : "S"} · ORIGINAL PRESERVED
              </div>
            )}
            {trackedCount > 0 && (
              <div style={{ fontFamily:FONT.mono, fontSize:11, color: missingIngs.length===0?COLOR.leaf:COLOR.amber, letterSpacing:"0.12em" }}>
                PANTRY {okCount}/{trackedCount}
              </div>
            )}
          </div>
        </div>

        {/* Pantry availability summary */}
        {(trackedCount > 0 || allMissing.length > 0) && (
          <div style={{
            marginBottom:14, padding:"12px 14px", borderRadius:12,
            background: allMissing.length===0 ? COLOR.leafDeep : COLOR.goldDeep,
            border: `1px solid ${allMissing.length===0 ? COLOR.leafDim : COLOR.goldDim}`,
          }}>
            {allMissing.length === 0 && lowIngs.length === 0 ? (
              <div style={{ fontFamily:FONT.forma, fontSize:13, color:COLOR.leaf }}>
                ✓ You have everything you need. Let's cook.
              </div>
            ) : (
              <>
                <div style={{ fontFamily:FONT.forma, fontSize:13, color: allMissing.length>0 ? COLOR.amber : "#eab308", marginBottom: (allMissing.length + lowIngs.length) > 0 ? 10 : 0 }}>
                  {allMissing.length > 0 && <>⚠ Missing {allMissing.length} ingredient{allMissing.length>1?"s":""}</>}
                  {allMissing.length > 0 && lowIngs.length > 0 && " • "}
                  {lowIngs.length > 0 && <>{lowIngs.length} running low</>}
                </div>
                <button
                  onClick={addMissingToShoppingList}
                  style={{ width:"100%", padding:"10px", background:COLOR.goldDeep, border:`1px solid ${COLOR.goldDim}`, borderRadius:10, fontFamily:FONT.mono, fontSize:11, color:COLOR.amber, cursor:"pointer", letterSpacing:"0.08em", fontWeight:600 }}
                >
                  ADD MISSING TO SHOPPING LIST →
                </button>
                {justAdded > 0 && (
                  <div style={{ marginTop:10, fontFamily:FONT.forma, fontSize:12, color:COLOR.leaf, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span>✓ Added {justAdded} item{justAdded>1?"s":""} to shopping list</span>
                    {onGoToShopping && (
                      <button onClick={onGoToShopping} style={{ background:"none", border:"none", color:COLOR.leaf, fontFamily:FONT.mono, fontSize:10, cursor:"pointer", letterSpacing:"0.08em", textDecoration:"underline" }}>
                        VIEW →
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <style>{`
          @keyframes pairFadeIn {
            0%   { opacity: 0; transform: translateY(-2px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes badgeSettleIn {
            0%   { opacity: 0; transform: scale(0.92); }
            100% { opacity: 1; transform: scale(1); }
          }
          @media (prefers-reduced-motion: reduce) {
            [data-pair-anim] { animation: none !important; }
          }
        `}</style>
        <div style={{ background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:12, overflow:"hidden" }}>
          {(() => { const renderPrepRow = ({ ing, status, row, candidates }, i) => {
            const badge = status === "ok"          ? { label:"IN KITCHEN", color:COLOR.leaf, bg:COLOR.leafDeep }
                        : status === "low"         ? { label:"LOW",        color:COLOR.amber, bg:COLOR.goldDeep }
                        : status === "missing"     ? { label:"MISSING",    color:COLOR.rose, bg:COLOR.roseDeep }
                        : status === "wrong-state" ? { label:"WRONG FORM", color:COLOR.sky, bg:COLOR.skyDeep }
                        : status === "substitute"  ? { label:"SUBSTITUTE", color:COLOR.amber, bg:COLOR.goldDeep }
                        :                            null;
            const pairing = ingredientPairings[i] || null;
            const pairDescribe = describePairing(pairing);
            const pairTone = pairDescribe?.tone === "gray"  ? COLOR.leafMuted
                           : pairDescribe?.tone === "sky"   ? COLOR.sky
                           : pairDescribe?.tone === "amber" ? COLOR.amber
                           : pairDescribe?.tone === "red"   ? COLOR.rose
                           : COLOR.dim;
            const lostClaims = pairing?.lostClaims || [];
            // Curated substitution surfacing — tier chip + cooking
            // note pulled from INGREDIENT_INFO[id].substitutions.
            // Surfaces as a small chip + italic note under the
            // pairing line so the cook sees authored guidance
            // ("Sharper, saltier — cut the salt by a pinch") in
            // place of a generic "closest match" message.
            const subTier  = pairing?.subTier || null;
            const subNote  = pairing?.subNote || null;
            // Only rows with a canonical ingredientId are tappable — everything
            // else ("to taste" salt, decorative herbs) just renders static.
            const tappable = !!ing.ingredientId;
            const swapOpen = swapOpenIdx === i;
            const swapped  = !!(session.overrides[i]?.pantryItemId || session.overrides[i]?.swapCanonicalId);
            // SWAP available on every canonical-tagged row — user
            // might want to override the default pair on an IN KITCHEN
            // row (maybe they want to use a different pack that's
            // closer to expiry) or on a WRONG FORM row (their thigh
            // for the recipe's breast). Gating it to just missing /
            // substitute was too narrow — user reported "it won't let
            // me choose any substitutions" on a screen of mostly IN
            // KITCHEN + WRONG FORM rows.
            // SHOP still only on missing — adding a row you already
            // have to the shopping list is almost always a mistake.
            const showSwap = !!ing.ingredientId;
            const showShop = (status === "missing" || (status === "skip" && pairing?.status === "missing")) && !!setShoppingList;
            const shopDone = !!session.overrides[i]?.promotedToShopping;
            return (
              <div
                key={i}
                style={{
                  padding:"12px 16px",
                  borderBottom: i<ingredientStatus.length-1 ? `1px solid ${COLOR.edge}` : "none",
                }}
              >
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10 }}>
                  <button
                    onClick={tappable ? () => {
                      // When the user has swapped this ingredient (via
                      // the SWAP picker), open the detail card on the
                      // SWAPPED canonical, not the recipe's original.
                      // Without this, the card + its nutrition block
                      // stayed pinned to the original (white_wine,
                      // 2_percent_milk, etc.) while the cooking surface
                      // displayed the swap (chicken_stock, smoked_sausage)
                      // — user saw the swap's name with the original's
                      // nutrition. The pair text used the right canonical
                      // already; the detail tap was the lone gap.
                      const swapCanonicalId = session.overrides[i]?.swapCanonicalId || null;
                      const effectiveId = swapCanonicalId || ing.ingredientId;
                      setCardIng({
                        ingredientId: effectiveId,
                        fallbackName: row?.name || ing.item,
                        fallbackEmoji: row?.emoji,
                      });
                    } : undefined}
                    disabled={!tappable}
                    style={{
                      flex: 1, minWidth: 0, textAlign:"left",
                      background:"transparent", border:"none", padding: 0,
                      cursor: tappable ? "pointer" : "default", color:"inherit",
                    }}
                  >
                    <span style={{ fontFamily:FONT.itemTitle, fontStyle:"italic", fontWeight:500, color:COLOR.ink, fontSize:16, lineHeight:1.25, display:"inline-flex", alignItems:"center", gap:6 }}>
                      {ing.item}
                      {tappable && <span style={{ color:COLOR.sub, fontSize:11 }}>ⓘ</span>}
                    </span>
                  </button>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2, flexShrink:0 }}>
                    <span style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.gold, fontWeight:500 }}>
                      {applyPreferredUnit(ing.amount, ing, DISPLAY_CONTEXT.COOK) || ing.amount}
                    </span>
                    {perIngredientKcal[i] > 0 && (
                      <span style={{ fontFamily:FONT.forma, fontSize:10, fontWeight:500, color:COLOR.muted, letterSpacing:"0.04em" }}>
                        ≈ {perIngredientKcal[i].toLocaleString()} kcal
                      </span>
                    )}
                  </div>
                </div>
                {badge && (
                  <div style={{ marginTop:6, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <span
                      key={badge.label}
                      data-pair-anim
                      style={{
                        fontFamily:FONT.mono, fontSize:9, color:badge.color, background:badge.bg,
                        border:`1px solid ${badge.color}44`, padding:"2px 7px", borderRadius:4,
                        letterSpacing:"0.08em",
                        animation:"badgeSettleIn 200ms ease-out both",
                      }}>
                      {badge.label}
                    </span>
                    {row && (
                      <span style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.sub }}>
                        have {Math.round(row.amount*10)/10} {unitLabel(findIngredient(row.ingredientId), row.unit)}
                      </span>
                    )}
                    {status === "wrong-state" && candidates && candidates.length > 0 && (
                      <span style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.sub }}>
                        have {Math.round((candidates[0].amount || 0)*10)/10} {unitLabel(findIngredient(candidates[0].ingredientId), candidates[0].unit)} as {candidates[0].state || "(other form)"}
                      </span>
                    )}
                  </div>
                )}
                {pairDescribe && (
                  <div
                    key={`${pairDescribe.text}|${subTier || ""}`}
                    data-pair-anim
                    style={{
                      marginTop:6, lineHeight:1.4,
                      animation: "pairFadeIn 220ms ease-out both",
                    }}
                  >
                    <div style={{
                      display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap",
                      fontFamily:FONT.forma, fontSize:11, fontStyle:"italic",
                      color: pairTone,
                    }}>
                      {subTier && (
                        <span style={{
                          fontFamily:FONT.mono, fontSize:9, fontStyle:"normal",
                          letterSpacing:"0.10em", padding:"2px 6px", borderRadius:4,
                          color: pairTone, background: COLOR.goldDeep,
                          border:`1px solid ${pairTone}33`,
                          flexShrink:0,
                        }}>
                          {(subTierLabel(subTier) || "SUB").toUpperCase()}
                        </span>
                      )}
                      <span>{pairDescribe.text}</span>
                      {lostClaims.length > 0 && (
                        <span style={{ fontFamily:FONT.mono, fontSize:10, fontStyle:"normal", letterSpacing:"0.04em", color: COLOR.rose }}>
                          ⚠ NO LONGER {lostClaims.map(c => c.toUpperCase()).join(" / ")}
                        </span>
                      )}
                    </div>
                    {subNote && (
                      <div style={{
                        marginTop:3,
                        fontFamily:FONT.forma, fontSize:11, fontStyle:"italic",
                        color: COLOR.muted, lineHeight:1.4,
                      }}>
                        {subNote}
                      </div>
                    )}
                  </div>
                )}
                {(showSwap || showShop) && (
                  <div style={{ marginTop:8, display:"flex", gap:6 }}>
                    {showSwap && (
                      <button
                        onClick={() => swapOpen ? openSwapPicker(null) : openSwapPicker(i)}
                        style={swapOpen ? cookSwapBtnActive : cookSwapBtn}
                      >
                        ⇌ SWAP
                      </button>
                    )}
                    {showShop && (
                      <button
                        onClick={() => addOneToShoppingList(ing, row, i)}
                        disabled={shopDone}
                        style={shopDone ? cookShopBtnDone : cookShopBtn}
                      >
                        {shopDone ? "✓ ON LIST" : "+ SHOP"}
                      </button>
                    )}
                    {showShop && onAddToPantry && !shopDone && (
                      <button
                        onClick={() => onAddToPantry({
                          name:         ing.item || ing.name || null,
                          ingredientId: ing.ingredientId || null,
                          amount:       ing.amount,
                        })}
                        style={cookShopBtn}
                        title="I have this — add to pantry instead of shopping"
                      >
                        + ADD
                      </button>
                    )}
                  </div>
                )}
                {swapOpen && (() => {
                  const q = swapSearch.trim();
                  const { pantry: rankedPantry, library: rankedLibrary } =
                    rankSwapCandidates(ing, swapSearch);
                  // Pantry: keep the legacy "top 3 > 0 without query"
                  // pattern so the picker doesn't spam unrelated rows
                  // when the user hasn't typed yet.
                  const shownPantry = q
                    ? rankedPantry.slice(0, 8).map(r => r.row)
                    : rankedPantry.filter(r => r.score > 0).slice(0, 3).map(r => r.row);
                  // Library: broader on open (top 6) because the whole
                  // point is "what else could I reasonably use?" — the
                  // curated substitutions + hub siblings stack stays
                  // small per ingredient, so we can afford to show more
                  // of it. Typing narrows via the in-function filter.
                  const shownLibrary = q
                    ? rankedLibrary.slice(0, 8)
                    : rankedLibrary.slice(0, 6);
                  const hasAny = shownPantry.length > 0 || shownLibrary.length > 0;
                  return (
                    <div style={{
                      marginTop:10, paddingTop:10,
                      borderTop:`1px dashed ${COLOR.border}`,
                      display:"flex", flexDirection:"column", gap:6,
                    }}>
                      <div style={{ fontFamily:FONT.mono, fontSize:9, color:COLOR.dim, letterSpacing:"0.08em" }}>
                        SWAP {String(ing.item).toUpperCase()} FOR:
                      </div>
                      <input
                        type="text"
                        value={swapSearch}
                        onChange={e => setSwapSearch(e.target.value)}
                        placeholder="Search pantry or library…"
                        style={cookSwapSearchInput}
                        autoFocus
                      />
                      {!hasAny && (
                        <div style={{ fontFamily:FONT.forma, fontSize:11, color:COLOR.muted, fontStyle:"italic" }}>
                          {q ? "Nothing matches that search." : "No close matches — try typing to search."}
                        </div>
                      )}
                      {shownPantry.length > 0 && (
                        <>
                          <div style={{ fontFamily:FONT.mono, fontSize:9, color:COLOR.leafMuted, letterSpacing:"0.08em", marginTop:2 }}>
                            FROM YOUR PANTRY
                          </div>
                          {shownPantry.map(c => (
                            <button
                              key={`pantry-${c.id}`}
                              onClick={() => { applySwap(i, c.id); openSwapPicker(null); }}
                              style={cookSwapOptionBtn}
                            >
                              <span style={{ fontSize:16 }}>{c.emoji || "🥫"}</span>
                              <span style={{ flex:1, textAlign:"left", fontFamily:FONT.forma, fontSize:13, color:COLOR.ink }}>
                                {c.name}
                              </span>
                              <span style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.dim }}>
                                {c.amount}{c.unit ? ` ${c.unit}` : ""}
                              </span>
                            </button>
                          ))}
                        </>
                      )}
                      {shownLibrary.length > 0 && (
                        <>
                          <div style={{ fontFamily:FONT.mono, fontSize:9, color:COLOR.gold, letterSpacing:"0.08em", marginTop:6 }}>
                            OR TRY — NOT IN PANTRY
                          </div>
                          {shownLibrary.map(({ canonical, note }) => (
                            <button
                              key={`library-${canonical.id}`}
                              onClick={() => { applyLibrarySwap(i, canonical); openSwapPicker(null); }}
                              style={cookSwapOptionBtn}
                              title={note || undefined}
                            >
                              <span style={{ fontSize:16, opacity:0.85 }}>{canonical.emoji || "🥫"}</span>
                              <span style={{ flex:1, textAlign:"left", fontFamily:FONT.forma, fontSize:13, color:"#e8dfc8" }}>
                                {canonical.name}
                                {note && (
                                  <span style={{ display:"block", fontFamily:FONT.forma, fontSize:10, color:COLOR.muted, fontStyle:"italic", marginTop:1, lineHeight:1.3 }}>
                                    {note}
                                  </span>
                                )}
                              </span>
                              <span style={{ fontFamily:FONT.mono, fontSize:9, color:COLOR.muted, letterSpacing:"0.06em" }}>
                                LIBRARY
                              </span>
                            </button>
                          ))}
                        </>
                      )}
                      {swapped && (
                        <button onClick={() => { clearSwap(i); openSwapPicker(null); }} style={cookSwapClearBtn}>
                          ↺ REVERT TO ORIGINAL ({ing.item})
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          };
          // Group the prep list wet / dry while preserving each row's
          // ORIGINAL index — session.overrides, pairings, and kcal all
          // key off it, so we sort into groups but pass the original i
          // back into renderPrepRow.
          const _wet = [], _dry = [];
          ingredientStatus.forEach((e, gi) => {
            (classifyWetDry(e.ing) === "wet" ? _wet : _dry).push({ e, gi });
          });
          const _secs = [["Wet", _wet], ["Dry", _dry]].filter(([, a]) => a.length);
          const _showHeaders = _secs.length > 1;
          return _secs.map(([label, arr]) => (
            <div key={label}>
              {_showHeaders && (
                <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.muted, letterSpacing:"0.16em", textTransform:"uppercase", padding:"10px 16px 4px" }}>
                  {label}
                </div>
              )}
              {arr.map(({ e, gi }) => renderPrepRow(e, gi))}
            </div>
          ));
        })()}
        </div>

        {/* TOOLS — equipment the recipe needs. Surfaces what the
            AI (or bundled recipe author) specified so the user can
            do a mise-en-place scan before starting: "do I have a
            12\" cast iron? a microplane? a fine-mesh strainer?".
            Was previously emitted into the persisted recipe but
            never rendered. Hidden when the recipe carries no
            tools (older drafts, bundled recipes that omitted it). */}
        {Array.isArray(recipe.tools) && recipe.tools.length > 0 && (
          <div style={{ marginTop:24 }}>
            <div style={{ fontFamily:FONT.mono, fontSize:11, color:COLOR.muted, letterSpacing:"0.12em", marginBottom:10 }}>
              TOOLS
            </div>
            <div style={{ background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:12, padding:"10px 14px" }}>
              <ul style={{ margin:0, padding:0, listStyle:"none", display:"flex", flexDirection:"column", gap:6 }}>
                {recipe.tools.map((t, i) => (
                  <li
                    key={i}
                    style={{
                      fontFamily:FONT.forma, fontSize:13,
                      color:COLOR.dim, display:"flex", alignItems:"center", gap:8,
                    }}
                  >
                    <span style={{ color:COLOR.sub, fontSize:10 }}>▸</span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
      {/* Team panel — invite cooks + pre-assign steps from preview.
          Lead can divvy up the cook before START so helpers open
          on their assigned task in parallel ("Marissa, you're on
          the onions while I do the pasta"). */}
      <CookPreviewAssigner
        userId={userId}
        recipe={recipe}
        family={family}
        friends={friends}
      />

      {/* Bottom action row — SCHEDULE + START COOKING. Cancel-cook
          lives as a small link below this row, so the primary
          action stays clean. */}
      <div style={{ display:"flex", gap:10, marginTop:32, alignItems:"stretch" }}>
        {onSchedule && (
          <button
            onClick={onSchedule}
            style={{
              flex:1, padding:"18px 12px",
              background:COLOR.soil, color:COLOR.dim,
              border:`1px solid ${COLOR.border}`, borderRadius:14,
              fontFamily:FONT.mono, fontSize:12, fontWeight:600,
              letterSpacing:"0.06em", cursor:"pointer",
            }}
          >
            SCHEDULE
          </button>
        )}
        <button
          onClick={() => setView("cook")}
          style={{
            flex:2, padding:"18px 24px",
            background:COLOR.gold, color:COLOR.night,
            border:"none", borderRadius:14,
            fontFamily:FONT.mono, fontSize:14, fontWeight:600,
            letterSpacing:"0.08em", cursor:"pointer",
            boxShadow:"0 0 40px #f5c84233",
          }}
        >
          {/* Mid-cook the overview doubles as the recipe preview
              (reachable via ← RECIPE on step 1) — the CTA must say
              it RESUMES rather than restarts. activeStep survives
              the round trip. */}
          {telemetry.session ? `BACK TO COOKING — STEP ${activeStep + 1} →` : "START COOKING →"}
        </button>
      </div>
      {/* Cancel-cook — only when there's actually a cook to cancel.
          Subtle, italic, muted. Opens a small confirm sheet so an
          accidental tap doesn't kill an in-progress session. */}
      {telemetry.session && (
        <div style={{ marginTop:18, textAlign:"center" }}>
          <button
            type="button"
            onClick={() => setExitSheetOpen(true)}
            style={{
              background:"transparent", border:"none",
              color:COLOR.muted,
              fontFamily:FONT.forma,
              fontSize:13, cursor:"pointer",
              padding:"8px 12px",
              textDecoration:"underline",
              textUnderlineOffset:3,
              textDecorationColor:COLOR.edge,
            }}
          >
            Cancel this cook
          </button>
        </div>
      )}
      {cardIng && (
        <IngredientCard
          ingredientId={cardIng.ingredientId}
          fallbackName={cardIng.fallbackName}
          fallbackEmoji={cardIng.fallbackEmoji}
          pantry={pantry}
          currentRecipeSlug={recipe.slug}
          onClose={() => setCardIng(null)}
        />
      )}
      {shareSheetOpen && (
        <ShareRecipeSheet
          recipe={recipe}
          family={family}
          friends={friends}
          sentIds={sentIds}
          onSend={sendShareTo}
          onShareNative={shareViaNative}
          copiedLink={copiedLink}
          onClose={closeShareSheet}
        />
      )}
    </div>
  );

  return (
    <div style={{
      // Top inset honors iOS safe area so the MINIMIZE pill clears the
      // status bar / Dynamic Island under viewport-fit=cover.
      padding:"calc(16px + env(safe-area-inset-top)) 24px 40px",
      maxWidth:480, margin:"0 auto",
    }}>
      {/* Voice state overlay — full-screen glow when listening,
          green flash when a command fires. pointer-events: none. */}
      {voiceEnabled && <VoiceListeningOverlay state={voice.state} />}
      {/* MINIMIZE button pinned top-left — rendered BEFORE the team
          cook overlay / multi-timer strip / any other content so it's
          always visible at the top of CookMode regardless of what
          else is mounted. ALL COOKS button sits to its right. */}
      {onExit && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
          <button
            onClick={handleUserMinimize}
            title="Step out — timer keeps running"
            aria-label="Minimize cook"
            style={{
              display:"inline-flex", alignItems:"center", gap:8,
              background:COLOR.soil, border:`1px solid ${COLOR.border}`, color:COLOR.dim,
              borderRadius:20, padding:"8px 14px",
              fontFamily:FONT.mono, fontSize:10, letterSpacing:"0.14em",
              cursor:"pointer",
            }}
          >
            <span style={{ fontSize:14, lineHeight:1 }}>↑</span>
            MINIMIZE
          </button>
          {onOpenFamilyKitchen && (
            <button
              onClick={onOpenFamilyKitchen}
              title="See every cook running across the family"
              aria-label="View all cooks"
              style={{
                display:"inline-flex", alignItems:"center",
                background: COLOR.leaf, border: "none", color: "#FFFFFF",
                borderRadius:20, padding:"8px 14px",
                fontFamily:FONT.mono, fontSize:10, letterSpacing:"0.14em",
                fontWeight: 800,
                cursor:"pointer",
              }}
            >
              ALL COOKS
            </button>
          )}
        </div>
      )}
      {/* Team-cook overlay — sticky strip rendering participants +
          per-step assignment affordances. Self-hides when there are no
          participants for the session (single-user cook). */}
      {telemetry.session?.id && recipe?.steps?.[activeStep] && (
        <TeamCookOverlay
          userId={userId}
          cookSessionId={telemetry.session.id}
          currentStep={recipe.steps[activeStep]}
          recipeSteps={recipe.steps}
          scheduledFor={recipe?.scheduled_for || null}
          family={family}
          friends={friends}
        />
      )}
      {/* Multi-timer strip — shows every OTHER active timer running
          across the team AND across the viewer's other steps in this
          same cook (post-`stepRowId` race fix the user's own parallel
          timers on prior/upcoming steps surface here too). Auto-hides
          when there's nothing parallel to display (solo cook with
          exactly one ticking timer, or no ticking timers at all).
          Tap a pill to jump to that step. */}
      {telemetry.session?.id && recipe?.steps?.[activeStep] && (
        <CookOtherTimersStrip
          userId={userId}
          cookSessionId={telemetry.session.id}
          currentStepId={recipe.steps[activeStep]?.id}
          onJumpToStep={(stepId) => {
            const idx = recipe.steps.findIndex((s) => String(s.id) === String(stepId).replace(/-s\d+$/, ""));
            if (idx >= 0) setActiveStep(idx);
          }}
        />
      )}
      {/* Live wake-word diagnostic chip. Shows the rolling peak
          classifier score so the user can see whether the model is
          actually hearing them; the wake itself fires a haptic +
          chime when triggered. */}
      {voiceEnabled && (() => {
        // Debug pill — vivid, non-overlapping colors so a glance tells
        // you exactly what state the voice pipeline is in:
        //   RED    = engine genuinely stopped (no narration, no engine)
        //   AMBER  = suspended waiting for narration to finish
        //   GREEN  = engine running, idle and waiting for wake
        //   CYAN   = wake word heard, listening for command
        //   PINK   = command fired (~900ms flash)
        const debug = (!voice.listening && narrating)
          ? { color: "#FFB300", text: "WAITING (BOODA TALKING)" }
          : !voice.listening
          ? { color: "#FF1744", text: "STOPPED" }
          : voice.state === "firing"
          ? { color: "#FF1493", text: "FIRING " + (voice.lastCommand || "—") }
          : voice.state === "listening"
          ? { color: "#00E5FF", text: "LISTENING" }
          : { color: "#00E676", text: "READY  ·  HEY BOODA, NEXT" };
        return (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            marginTop: 8,
            padding: "6px 13px",
            background: "rgba(0,0,0,0.45)",
            border: `1px solid ${debug.color}`,
            borderRadius: 999,
            fontFamily: FONT.mono, fontSize: 11, fontWeight: 700,
            color: debug.color, letterSpacing: "0.1em",
            boxShadow: `0 0 12px ${debug.color}66`,
          }}>
            <span style={{
              display: "inline-block", width: 14, height: 14, borderRadius: 7,
              background: debug.color,
              boxShadow: `0 0 10px ${debug.color}`,
              transition: "background 0.15s, box-shadow 0.15s",
            }} />
            {debug.text}
          </div>
        );
      })()}
      {/* ASK ABOUT THIS STEP — opens the cook chat sheet. Visible
          throughout the cook view (no voice-enabled gate); the sheet
          itself never fires the LLM until the user submits. */}
      {step && (
        <button
          type="button"
          onClick={() => setChatOpen(true)}
          className="mcm-focusable"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            marginTop: 8, marginLeft: voiceEnabled ? 8 : 0,
            padding: "5px 11px",
            background: COLOR.skyDeep,
            border: `1px solid ${COLOR.skyBorder}`,
            borderRadius: 999,
            fontFamily: FONT.mono, fontSize: 9, fontWeight: 500,
            color: COLOR.sky, letterSpacing: "0.1em",
            cursor: "pointer",
            touchAction: "manipulation",
          }}
        >
          <span style={{
            display: "inline-block", width: 6, height: 6, borderRadius: 3,
            background: COLOR.sky,
          }} />
          ASK ABOUT THIS STEP
        </button>
      )}
      {chatOpen && step && (
        <CookChatSheet
          key={step.id}
          recipe={recipe}
          step={step}
          onClose={() => setChatOpen(false)}
        />
      )}
      <div style={{ height:3, background:COLOR.edge, borderRadius:2, marginTop:16, overflow:"hidden" }}>
        <div style={{ height:"100%", background:COLOR.gold, borderRadius:2, width:`${progress}%`, transition:"width 0.5s ease" }} />
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
        <span style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.sub }}>STEP {activeStep+1} OF {steps.length}</span>
        <span style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.sub }}>{completedSteps.size} DONE</span>
      </div>

      {/* Background-timer banner — surfaces every still-running
          timer on steps OTHER than the active one so the cook
          trusts that advancing past a passive step didn't lose
          the timer. Tap a row to jump back to that step. Hidden
          when no background timers are alive. Per the split-step
          "while X cooks" model: passive steps emit their timer
          and the cook moves forward; this banner is the trust
          signal that makes the move-forward feel safe. */}
      {backgroundTimers.length > 0 && (
        <div style={{
          marginTop: 12,
          padding: "10px 12px",
          background: `${COLOR.leaf}14`,
          border: `1px solid ${COLOR.leaf}55`,
          borderRadius: 10,
          display: "flex", flexDirection: "column", gap: 6,
        }}>
          <div style={{
            fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
            letterSpacing: "0.14em", color: COLOR.leaf,
          }}>
            STILL COOKING
          </div>
          {backgroundTimers.map((bt) => {
            const mm = Math.floor(bt.secondsRemaining / 60);
            const ss = bt.secondsRemaining % 60;
            const stamp = `${mm}:${String(ss).padStart(2, "0")}`;
            return (
              <div key={bt.stepRowId} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => {
                    const idx = (recipe?.steps || []).findIndex((s) => String(s.id) === String(bt.stepId));
                    if (idx !== -1) setActiveStep(idx);
                  }}
                  style={{
                    flex: 1, minWidth: 0,
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px",
                    background: "transparent",
                    border: `1px solid ${COLOR.edge}`,
                    borderRadius: 8,
                    fontFamily: FONT.forma, fontSize: 13,
                    color: COLOR.ink,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {bt.stepTitle}
                  </span>
                  <span style={{
                    fontFamily: FONT.mono, fontWeight: 700, fontSize: 13,
                    color: COLOR.leaf, letterSpacing: "0.05em",
                  }}>
                    {stamp}
                  </span>
                  <span style={{ color: COLOR.dim, fontFamily: FONT.mono, fontSize: 14 }}>←</span>
                </button>
                {/* Stop — explicitly end this background timer (writes
                    timer_state.ended), dropping it from the banner and
                    the Live Activity. The cook owns the timer; they can
                    kill it without jumping back to the step. */}
                <button
                  type="button"
                  aria-label={`Stop the ${bt.stepTitle} timer`}
                  onClick={() => {
                    Promise.resolve(telemetry.endStepTimers?.(bt.stepId))
                      .catch((e) => console.warn("[CookMode] background-timer stop threw:", e));
                  }}
                  style={{
                    flexShrink: 0,
                    width: 30, height: 30, borderRadius: 15,
                    background: "transparent",
                    border: `1px solid ${COLOR.edge}`,
                    color: COLOR.dim,
                    fontSize: 14, lineHeight: 1, padding: 0, cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    touchAction: "manipulation",
                  }}
                >✕</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Push-enable prompt — only renders when:
          - this device CAN do push (supported flag from useWebPush)
          - user hasn't already subscribed
          - user hasn't explicitly denied (respect the blacklist)
          - this step has a timer (otherwise the value-add is unclear;
            no timer step = no push benefit, no reason to nag)

          The copy emphasizes the lock-screen case because that's the
          pain point: an in-app countdown can't wake a locked phone,
          a push can. One tap calls webPush.enable() which handles
          permission prompt, SW register, and push_subscriptions
          upsert in one go. */}
      {webPush.supported && !webPush.enabled && webPush.permission !== "denied" && totalStepSeconds(step) > 0 && (
        <div style={{
          marginTop: 16,
          padding: "14px 16px",
          background: "linear-gradient(180deg,#1e1408 0%,#170d05 100%)",
          border: "1px solid #3a2a0a",
          borderRadius: 12,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>🔔</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily:FONT.mono, fontSize:10, fontWeight:700,
              color:COLOR.gold, letterSpacing:"0.14em", marginBottom:3,
            }}>
              RING ME ON THE LOCK SCREEN
            </div>
            <div style={{
              fontFamily:FONT.forma, fontSize:12,
              color:COLOR.dim, lineHeight:1.4,
            }}>
              Timers only reach your phone when it's locked if notifications are enabled.
            </div>
          </div>
          <button
            onClick={() => { primeCookAudio(); webPush.enable(); }}
            disabled={webPush.busy}
            style={{
              flexShrink: 0,
              padding: "10px 14px",
              background: COLOR.gold, color: COLOR.night,
              border: "none", borderRadius: 10,
              fontFamily:FONT.mono, fontSize:10, fontWeight:700,
              letterSpacing:"0.08em", cursor: webPush.busy ? "not-allowed" : "pointer",
              opacity: webPush.busy ? 0.5 : 1,
            }}
          >
            {webPush.busy ? "…" : "ENABLE"}
          </button>
        </div>
      )}
      {/* Step-progress dots — visual ONLY. Tapping was originally a
          jump-to-step shortcut, but the cook would knock them with
          the side of their hand mid-cook and skip ahead, losing
          their place. Step navigation lives at the bottom-of-page
          PREV / NEXT buttons; this row is a status indicator, not
          a control surface. */}
      <div style={{ display:"flex", gap:8, marginTop:16, justifyContent:"center" }}>
        {steps.map((_,i)=>(
          <div key={i} aria-hidden style={{ width: completedSteps.has(i)||i===activeStep?24:8, height:8, borderRadius:4, background: completedSteps.has(i)?COLOR.leaf:i===activeStep?COLOR.sky:COLOR.border, transition:"all 0.3s" }} />
        ))}
      </div>
      {/* Recipe-title kicker — anchors the cook in WHICH recipe is
          live. Easy to lose track when the cook minimizes, opens a
          shopping list, then comes back; this is a one-line
          orientation cue at the top-left of every step view. */}
      {recipe?.title && (
        <div style={{
          marginTop: 20,
          fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
          fontSize: 18, color: COLOR.dim, lineHeight: 1.2,
          textAlign: "left",
        }}>
          {recipe.title}
        </div>
      )}
      {/* Per-step swap banner + inline prose rewrite.
          Why the swap list comes from recipeSwapSummary, not
          stepSwapSummary: step.uses is a CURATED subset of the
          recipe — "FOR THIS STEP" — and bundled recipes routinely
          omit ingredients the prose mentions (e.g. uses lists
          butter/salt/pepper but the instruction reads "Add the
          milk, pesto, and capers"). A step-scoped swap summary
          would miss swaps on those prose-only mentions. Filtering
          through relevantSwapsForStep keeps banner noise low —
          only swaps that actually appear in this step's prose or
          uses surface. The prose below is tokenized with the same
          filtered list: word-boundary regex, deterministic, no AI
          call. The banner is the backstop for cases regex can't
          reach (plural / possessive forms the simple word-boundary
          match misses). */}
      {(() => {
        const stepSwaps = relevantSwapsForStep(step, allSwaps);
        if (stepSwaps.length === 0) return null;
        return (
          <div style={{ marginTop:12, padding:"10px 14px", background:COLOR.goldDeep, border:"1px solid #2f2820", borderRadius:10, display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontSize:14, flexShrink:0, color:CHIP_TONES.canonical.fg }}>↔</span>
            <div style={{ fontFamily:FONT.forma, fontSize:12, color:CHIP_TONES.canonical.fg, lineHeight:1.5 }}>
              {stepSwaps.map((s, i) => (
                <div key={i}>
                  {s.skipped
                    ? <>Skipping <strong>{s.from}</strong> for this step.</>
                    : <>Using <strong>{s.to}</strong> instead of <strong>{s.from}</strong> for this step.</>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div
        key={`step-card-${step.id}`}
        style={{
          position:"relative",
          marginTop:8, padding:"22px 18px 18px",
          background:COLOR.ground,
          border:`1px solid ${COLOR.border}`,
          borderRadius:14,
        }}
      >
        {/* Corner flourishes — four 28×28 SVG curls anchored to each
            corner of the card. Stroke-drawn from the outside in on
            mount; the card's own border stays visible underneath as
            a quiet baseline so the flourish ornament reads as
            decoration, not the only frame. Path length ~60 → use a
            comfortable 70 dash for the offset animation. */}
        {(() => {
          const C = COLOR.ink;
          const corners = [
            { d: "M 28 4 L 12 4 Q 4 4, 4 12 L 4 28",  pos: { top: 6, left: 6 } },
            { d: "M 0 4 L 16 4 Q 24 4, 24 12 L 24 28", pos: { top: 6, right: 6 } },
            { d: "M 28 24 L 12 24 Q 4 24, 4 16 L 4 0", pos: { bottom: 6, left: 6 } },
            { d: "M 0 24 L 16 24 Q 24 24, 24 16 L 24 0", pos: { bottom: 6, right: 6 } },
          ];
          return corners.map((c, i) => (
            <svg
              key={i}
              aria-hidden="true"
              width="28" height="28" viewBox="0 0 28 28"
              style={{ position:"absolute", ...c.pos, pointerEvents:"none" }}
            >
              <path
                d={c.d}
                fill="none"
                stroke={C}
                strokeWidth="1.2"
                strokeLinecap="round"
                style={{
                  opacity: 0.45,
                  strokeDasharray: 70,
                  strokeDashoffset: 70,
                  animation: `stepCornerDraw 640ms ${60 + i * 70}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
                }}
              />
            </svg>
          ));
        })()}

        {/* Merged step header — kicker + animated flourish + title +
            optional animation, all in the same card as the prose so
            the cook reads "Step 3 → Build the Soffritto → prose" as
            one continuous unit. Each child carries its own staggered
            fade-up so the whole card "blooms" in on step transition. */}
        <div style={{ textAlign:"center", marginBottom:14 }}>
          {/* Animation source priority:
                1. step.lottie  — admin-attached Lottie on a course
                                  (lazy-loaded, network-fetched JSON).
                2. AnimComp     — bundled in-code SVG/JS animation
                                  via step.animation key.
              Course Lottie wins because it's the curated path; the
              admin signed off on it. AnimComp is the legacy fallback. */}
          {step?.lottie?.url ? (
            <div style={{
              marginBottom:12,
              opacity:0,
              animation:"stepFadeUp 520ms 100ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
            }}>
              <StepLottie
                url={step.lottie.url}
                loop={step.lottie.loop !== false}
                speed={step.lottie.speed || 1.0}
              />
            </div>
          ) : AnimComp && (
            <div style={{
              marginBottom:12,
              opacity:0,
              animation:"stepFadeUp 520ms 100ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
            }}>
              <AnimComp />
            </div>
          )}
          <div style={{
            fontFamily:FONT.forma, fontStyle:"italic", fontSize:18, fontWeight:500,
            color:COLOR.ink, letterSpacing:"0.01em", marginBottom:6,
            opacity:0,
            animation:"stepFadeUp 520ms 200ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
          }}>
            Step {String(step.id).replace(/^step/i, "")}
          </div>
          {/* Elegant flourish — a single flowing serpentine on each
              side of a centered diamond. Two cubic-bezier S-curves
              per side give a calligraphic ribbon feel rather than
              the choppy four-segment wave we had before. Path length
              ≈ 240 per side → 280 dash for the offset animation. */}
          <svg
            aria-hidden="true"
            width="172" height="18" viewBox="0 0 172 18"
            style={{
              display:"inline-block", marginBottom:10,
              opacity: 0,
              animation:"stepFadeUp 360ms 320ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
              maxWidth:"75%",
            }}
          >
            <path
              d="M 4 9 C 14 1, 28 17, 44 9 S 70 1, 78 9 M 94 9 C 102 17, 124 1, 138 9 S 158 17, 168 9"
              fill="none"
              stroke={COLOR.ink}
              strokeWidth="1.1"
              strokeLinecap="round"
              style={{
                opacity: 0.6,
                strokeDasharray: 280,
                strokeDashoffset: 280,
                animation: "stepFlourishDraw 880ms 380ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
              }}
            />
            <path
              d="M 86 4 L 90 9 L 86 14 L 82 9 Z"
              fill={COLOR.ink}
              style={{
                opacity: 0,
                transformOrigin: "86px 9px",
                animation: "stepFlourishDiamond 380ms 920ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
              }}
            />
          </svg>
          <style>{`
            @keyframes stepFlourishDraw{to{stroke-dashoffset:0}}
            @keyframes stepFlourishDiamond{0%{opacity:0;transform:scale(0)}100%{opacity:0.7;transform:scale(1)}}
            @keyframes stepCornerDraw{to{stroke-dashoffset:0}}
            @keyframes stepFadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
          `}</style>
          <h2 style={{
            fontFamily:FONT.forma, fontSize:22, fontWeight:600,
            letterSpacing:"-0.01em", lineHeight:1.15, margin:0,
            opacity:0,
            animation:"stepFadeUp 520ms 520ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
          }}>{step.title}</h2>
        </div>
        <p style={{
          fontFamily:FONT.forma, fontSize:18, lineHeight:1.55, color:COLOR.ink,
          fontWeight:500, letterSpacing:"0.012em", margin:0,
          opacity:0,
          animation:"stepFadeUp 520ms 720ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
        }}>
          {(() => {
            // Unified tokenizer — handles both same-canonical brand
            // upgrades (plain tan rewrite, no strikethrough) and
            // different-canonical substitutes (strike + replacement).
            // Brand upgrades surface MY ingredient's branded name
            // inline in the recipe prose — "Kerrygold Butter" renders
            // wherever "butter" was, colored tan so the provenance
            // reads at a glance. Grammar caveat: word-boundary regex
            // dodges "buttered" naturally but "butter the dish"
            // becomes "Kerrygold Butter the dish" — slightly awkward,
            // worth the tradeoff to carry branded names throughout.
            const tokens = tokenizeSwappedInstruction(
              step.instruction,
              allSwaps,
              allBrandUpgrades,
              allAmountReplacements,
              {
                // Smart anchored-amount scaling: when the cook bumps
                // servings, prose like "4 wrapped franks" needs to
                // become "2 wrapped franks" even though the byte-
                // identical pass can't see the modifier-bearing form.
                // Only fires when ratio !== 1.
                scaleRatio: (() => {
                  const baseServes = Math.max(1, Number(recipe?.serves) || 1);
                  const targetServes = Math.max(1, Number(scaledRecipe?.serves) || baseServes);
                  return targetServes / baseServes;
                })(),
                ingredientNames: (recipe?.ingredients || [])
                  .map(ing => ing?.item)
                  .filter(s => typeof s === "string" && s.trim().length > 0),
                // Convert-anchored prose targets — see assembly above.
                // Drives the partial-usage rewrite ("4 tbsp of butter"
                // → "½ stick of butter") when the chip's display unit
                // differs from the unit appearing in prose.
                unitConversionTargets,
                // Combine id → label map so slug-shaped mixture ids
                // the model leaked into prose ("garlic_herb_crust
                // mixture") render as their human labels.
                mixtureLabels: (recipe?.steps || [])
                  .map(s => s?.combine)
                  .filter(c => c?.id && c?.label)
                  .map(c => ({ id: c.id, label: c.label })),
              },
            );
            if (tokens.length === 1 && tokens[0].text === step.instruction) {
              return step.instruction;
            }
            return tokens.map((t, i) => {
              if (t.text != null) return <span key={i}>{t.text}</span>;
              if (t.brand != null) {
                // Inline branded ingredient inside Forma prose. STAY
                // in the same family — switching to New Kansas inline
                // breaks the baseline (different ascent/descent metrics
                // make the highlighted run sit above or below the
                // surrounding text). Distinguish via color + italic +
                // heavier weight only; family stays Forma.
                return (
                  <span
                    key={i}
                    style={{
                      fontStyle: "italic",
                      fontWeight: 600,
                      color: CHIP_TONES.canonical.fg,
                    }}
                  >
                    {t.brand}
                  </span>
                );
              }
              // Strike + replacement pair. Same baseline-preserving
              // approach: keep the prose family, distinguish via
              // color + style only.
              return (
                <span key={i}>
                  <s style={{
                    opacity: 0.5,
                    color: CHIP_TONES.canonical.fg,
                    fontStyle: "italic",
                  }}>{t.strike}</s>
                  {t.after ? (
                    <span style={{
                      color: CHIP_TONES.canonical.fg,
                      fontStyle: "italic",
                      fontWeight: 600,
                    }}> {t.after}</span>
                  ) : null}
                </span>
              );
            });
          })()}
        </p>
        <TechniqueChips prose={[step.title, step.instruction].filter(Boolean).join("\n")} />
        {(() => {
          // Per-step view-state machine. The Timer / TimerStack
          // render below ONLY fires for "I'm the active assignee" or
          // "lead with no assignment on this step." Every other
          // combination renders an explicit banner instead so the UI
          // matches the data-layer's actual permissions.
          const ownership = stepOwnershipFor(step.id);
          // Lead defaults TRUE when no session row is loaded — same
          // convention as useCookTelemetry's isLeadRef. The session
          // is telemetry plumbing; if it's missing (insert race,
          // adopt lag after a remount, offline), a SOLO cook must
          // never be locked out of their own timers with a "waiting
          // on the lead" banner — they ARE the lead. Only a session
          // that is present AND owned by someone else demotes this
          // mount to viewer/helper.
          const isLeadView = !telemetry.session || telemetry.session.user_id === userId;
          if (!telemetry.session && !ownership) {
            // Impossible-solo diagnostic: we're rendering timers on
            // faith because telemetry never adopted a session. If
            // this logs persistently, startCook is failing upstream.
            console.warn("[CookMode] no telemetry session on step render — timers shown on lead-default (step:", step.id, ")");
          }

          // 1) Someone else owns this step → show their status (lead
          //    gets Revoke/Take Over, others read-only).
          if (ownership?.other) {
            return (
              <StepAssignmentBanner
                mode="other"
                assignment={ownership.assignment}
                assigneeName={ownership.name}
                isLead={isLeadView}
                stepId={step.id}
                cookSessionId={telemetry.session?.id || null}
                onRevoke={async () => {
                  if (team?.unassignStep) {
                    await team.unassignStep({
                      stepId: step.id,
                      assigneeId: ownership.userId,
                    });
                  }
                }}
                onTakeOver={async () => {
                  if (team?.takeOverStep) {
                    await team.takeOverStep(step.id);
                  }
                }}
              />
            );
          }

          // 2) I'm the assignee but my status is still pending →
          //    show Accept/Decline buttons in-step. Accepting flips
          //    status to 'accepted' and the next render falls through
          //    to the Timer UI below.
          if (ownership?.mine && ownership.assignment?.status === "pending") {
            return (
              <StepAssignmentBanner
                mode="mine-pending"
                onAccept={async () => {
                  if (team?.respondToAssignment) {
                    await team.respondToAssignment({
                      assignmentId: ownership.assignment.id,
                      accept: true,
                    });
                  }
                }}
                onDecline={async () => {
                  if (team?.respondToAssignment) {
                    await team.respondToAssignment({
                      assignmentId: ownership.assignment.id,
                      accept: false,
                    });
                  }
                }}
              />
            );
          }

          // 3) Helper on an unassigned step → viewer mode, no Timer.
          //    Only the lead (or an explicitly-assigned helper) can
          //    start a timer; the helper here is just watching.
          //    stepId + cookSessionId let the banner upgrade itself
          //    to a live ticker when SOMEONE (lead, typically) starts
          //    the step. Without those props, viewer would be stuck
          //    on the static "Waiting…" message.
          if (!ownership && !isLeadView) {
            return (
              <StepAssignmentBanner
                mode="viewer"
                stepId={step.id}
                cookSessionId={telemetry.session?.id || null}
              />
            );
          }
          // Runtime fallback for steps the LLM authored without a
          // timer field. The mushroom-risotto step "sauté for 5
          // minutes ... cook 1 more minute" is the canonical example
          // — clear duration cues in prose but no `step.timer` /
          // `step.timers` set, so the cook lost the countdown UI.
          // We scan the instruction for "N minutes/seconds" patterns
          // and synthesize the field at render time. Pure UI fill,
          // no DB write — the recipe row stays as the LLM authored.
          // resolveStepTimers is the single source of truth: explicit
          // step.timers[] win; else 2+ prose cues prefer the inferred
          // per-cue split over a rolled-up single step.timer (the
          // "3×30s mixes shown as one 1:00" fix); else the single
          // timer. Same helper the Live Activity rail + telemetry use,
          // so every surface agrees on this step's timers.
          const resolved = resolveStepTimers(step);
          const effectiveTimers = resolved?.timers || null;
          const effectiveTimer = !effectiveTimers && (resolved?.timer || null);
          return effectiveTimers && effectiveTimers.length > 0 ? (
          <TimerStack
            key={`${activeStep}-${step.id}-multi`}
            timers={effectiveTimers}
            resumeStageIdx={activeStep === initialStepIndex ? (initialTimerStageIdx ?? -1) : -1}
            resumeEndsAt={syncedEndsAt || (activeStep === initialStepIndex ? initialTimerEndsAt : null)}
            cookSessionId={telemetry.session?.id || null}
            parentStepId={step.id}
            onTimerStart={handleTimerStart}
            onTimerPause={handleTimerPause}
            onTimerResume={handleTimerResume}
            onTimerEnd={handleTimerEnd}
            onTimerReset={handleTimerReset}
            onStageReset={(stageIdx) => {
              // User manually reset this stage. Cancel the pending push
              // notification + finish the open telemetry row so the cook
              // banner stops ticking down a deadline that no longer
              // reflects what's on screen. Scoped by step_id (the
              // suffixed form) since TimerStack doesn't hold the row id.
              if (!telemetry?.session) return;
              const suffixedStepId = `${step.id}-s${stageIdx}`;
              const sessionId = telemetry.session.id;
              supabase.from("cook_step_notifications")
                .delete()
                .eq("cook_session_id", sessionId)
                .eq("step_id", suffixedStepId)
                .is("delivered_at", null)
                .then(({ error }) => {
                  if (error) console.warn("[CookMode] stage reset cancel push:", error.message);
                });
              supabase.from("cook_session_steps")
                .update({ finished_at: new Date().toISOString(), skipped: true })
                .eq("cook_session_id", sessionId)
                .eq("step_id", suffixedStepId)
                .is("finished_at", null)
                .then(({ error }) => {
                  if (error) console.warn("[CookMode] stage reset finish row:", error.message);
                });
            }}
            onStageStart={async (stageIdx, stage) => {
              // Register the active stage with telemetry so the cook
              // banner picks up the deadline + fires a push at
              // ring-time. Stage-id is suffixed with -sN so two stages
              // in the same step don't collide on the
              // (session, step_id) telemetry constraint — each stage
              // is its own logical step from telemetry's perspective.
              //
              // RETURNS the inserted row's id so TimerStack can thread
              // it through to ManualTimer → handleTimerStart →
              // markTimerStarted, bypassing the activeStepRef race.
              if (!telemetry?.session) return null;
              const stageSeconds = Number(stage?.seconds);
              if (!Number.isFinite(stageSeconds) || stageSeconds <= 0) return null;
              const stageLabel = stage?.label || `Stage ${stageIdx + 1}`;
              const row = await telemetry.startStep({
                stepId:         `${step.id}-s${stageIdx}`,
                stepTitle:      `${step.title || `Step ${activeStep + 1}`} · ${stageLabel}`,
                nominalSeconds: stageSeconds,
                timerBody:      `${stageLabel} — timer's up`,
                recipeSlug:     recipe?.slug,
                recipeTitle:    recipe?.title,
                recipeEmoji:    recipe?.emoji,
              });
              // Cuisine-accent voiceline — "Timer is started! …"
              // Plays only when the recipe has a generated voiceId
              // stamped on it; silent fallback otherwise.
              const voiceId = recipe?.audio?.voiceId;
              if (voiceId) playVoiceline("timer_started", voiceId);
              return row?.id || null;
            }}
            onStageWarning={() => {
              // Tiered warning fire (30s / 2m / 5m / 15m / 30m
              // depending on stage duration — see warningSecondsFor
              // in CookTimers). Single Timer path already plays this;
              // stages used to skip it entirely.
              const voiceId = recipe?.audio?.voiceId;
              if (voiceId) playVoiceline("timer_warning", voiceId);
            }}
            onStageDone={(stageIdx, stage) => {
              // Mark the just-finished stage as a completed timer on
              // the activity rail. The next stage's onTimerStart
              // overwrites activeTimerIndex; this ensures the
              // finished one stays painted as done in the meantime.
              const finishedIdx = findActivityTimerIndex(
                activeStep,
                stage?.label || ""
              );
              if (finishedIdx >= 0) {
                updateCookActivity((s) => {
                  if (!s.completedTimerIndices.includes(finishedIdx)) {
                    s.completedTimerIndices = [
                      ...s.completedTimerIndices,
                      finishedIdx,
                    ];
                  }
                });
              }
              // One chime per stage so the cook hears the flip cue
              // ("2-min sear done, start the rest").
              playTimerChime();
              // Cuisine-accent voiceline — "Time's up!"
              const voiceId = recipe?.audio?.voiceId;
              if (voiceId) playVoiceline("timer_done", voiceId);
              if (typeof document !== "undefined" && document.hidden &&
                  typeof Notification !== "undefined" && Notification.permission === "granted") {
                try {
                  new Notification(`${recipe.emoji || "⏲️"} ${stage?.label || "Stage"} done`, {
                    body: step.title ? `Step ${activeStep + 1}: ${step.title}` : "Stage timer ended",
                    tag: `cook-timer-${telemetry.session?.id || "local"}-${step.id}-s${stageIdx}`,
                    icon: "/icon-192.png",
                    badge: "/icon-badge-72.png",
                  });
                } catch { /* some contexts forbid Notification constructor */ }
              }
            }}
          />
        ) : effectiveTimer ? (
          <Timer
            key={`${activeStep}-${step.id}`}
            seconds={effectiveTimer}
            // Resolve endsAt for resume in this priority order:
            //   1. syncedEndsAt — helper-mirror path; the lead's
            //      timer_state.endsAt streamed via realtime
            //   2. telemetry.activeStep.timer_state.endsAt — live
            //      deadline pulled from the DB row that startStep
            //      ADOPTED for THIS step on (re-)entry. This is what
            //      makes navigation 1→2→1 preserve the timer that was
            //      already running on step 1; without it, the Timer
            //      remounted with endsAt=null and reset to nominal.
            //      Step-id match guards against the brief flash where
            //      telemetry.activeStep is still the prior step's row
            //      while the new startStep adopt is in-flight.
            //   3. initialTimerEndsAt on the originally-mounted step
            //      (CookMode resume from banner / push deep-link).
            endsAt={(() => {
              if (syncedEndsAt) return syncedEndsAt;
              const sa = telemetry?.activeStep;
              if (sa?.timer_state?.endsAt) {
                const baseId = String(sa.step_id || "").replace(/-s\d+$/, "");
                if (baseId === String(step.id)) {
                  // Treat the timer as ended when EITHER the flag was
                  // explicitly written OR the deadline is already in
                  // the past. Background-rung timers (user locked the
                  // phone, Live Activity ticked to 0, handleTimerEnd
                  // never fired in JS so the `ended` flag was never
                  // written) end up with endsAt-in-past + ended=false.
                  // Returning null here keeps the Timer's late-
                  // arriving endsAt effect from clobbering the DONE
                  // state derived from `initialDone`.
                  if (sa.timer_state.ended) return null;
                  const ms = new Date(sa.timer_state.endsAt).getTime();
                  if (Number.isFinite(ms) && ms <= Date.now()) return null;
                  return ms;
                }
              }
              // Suppress initialTimerEndsAt fallback when the session-
              // wide map already knows this step's timer rang — same
              // reasoning: don't hand the Timer a past deadline that
              // would un-do its DONE hydration.
              if (stepsWithEndedTimer.has(String(step.id))) return null;
              return activeStep === initialStepIndex ? initialTimerEndsAt : null;
            })()}
            initialDone={(() => {
              // Hydrate the Timer in DONE state when the DB says
              // the timer rang at any point in this session.
              //
              // Primary source: stepsWithEndedTimer — an ORed set
              // populated from every cook_session_steps row that
              // matches THIS step base id, so revisits after
              // markDone (which inserts a fresh empty row via
              // startStep) still see DONE because the original
              // finished row still carries `timer_state.ended`.
              //
              // Fallback to telemetry.activeStep for the brief
              // window before the session-wide query hydrates on
              // first mount.
              if (stepsWithEndedTimer.has(String(step.id))) return true;
              const sa = telemetry?.activeStep;
              const ts = sa?.timer_state;
              if (!ts) return false;
              const baseId = String(sa.step_id || "").replace(/-s\d+$/, "");
              if (baseId !== String(step.id)) return false;
              if (ts.ended === true) return true;
              if (ts.endsAt) {
                const ms = new Date(ts.endsAt).getTime();
                if (Number.isFinite(ms) && ms <= Date.now()) return true;
              }
              return false;
            })()}
            onTimerStart={handleTimerStart}
            onTimerPause={handleTimerPause}
            onTimerResume={handleTimerResume}
            onTimerEnd={handleTimerEnd}
            onTimerReset={handleTimerReset}
            onStart={() => {
              const voiceId = recipe?.audio?.voiceId;
              if (voiceId) playVoiceline("timer_started", voiceId);
            }}
            onWarning={() => {
              const voiceId = recipe?.audio?.voiceId;
              if (voiceId) playVoiceline("timer_warning", voiceId);
            }}
            onDone={() => {
              // Ring the bell. Server-side push already queued at
              // step-start (see useCookTelemetry.startStep) handles
              // the "app is closed" case; this handles "app is open
              // but user wandered off / backgrounded the tab."
              playTimerChime();
              const voiceId = recipe?.audio?.voiceId;
              if (voiceId) playVoiceline("timer_done", voiceId);
              if (typeof document !== "undefined" && document.hidden &&
                  typeof Notification !== "undefined" && Notification.permission === "granted") {
                try {
                  new Notification(`${recipe.emoji || "⏲️"} Timer's up`, {
                    body: step.title ? `Step ${activeStep + 1}: ${step.title}` : "Step timer ended",
                    tag: `cook-timer-${telemetry.session?.id || "local"}-${step.id}`,
                    icon: "/icon-192.png",
                    badge: "/icon-badge-72.png",
                  });
                } catch { /* some contexts forbid Notification constructor */ }
              }
            }}
          />
        ) : null;
        })()}
      </div>
      {/* ON YOUR COUNTER — the live "what's physically in front of me"
          list. Raw ingredients grouped wet / dry, each tap-checkable as
          it goes in the bowl; once a combine-step completes, consumed
          ingredients collapse into a named intermediate ("Wet mixture"
          → "Batter"). Driven entirely by counterComponents, which
          re-derives from (effectiveRecipe, completedSteps, session). */}
      {counterComponents.allPresentCount > 0 && (
        <div style={{
          marginTop: 16,
          padding: "16px 18px",
          background: COLOR.ground,
          border: `1px solid ${COLOR.border}`,
          borderRadius: 14,
        }}>
          <button
            type="button"
            onClick={() => setCounterOpen((v) => !v)}
            style={{
              display:"flex", alignItems:"center", gap:8,
              marginBottom: counterOpen ? 12 : 0,
              background:"transparent", border:"none", padding:0,
              width:"100%", cursor:"pointer", textAlign:"left",
            }}
          >
            <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.muted, letterSpacing:"0.16em", flex:1, textTransform:"uppercase" }}>
              On your counter
              <span style={{ marginLeft:6, fontSize:9, opacity:0.7 }}>
                {counterOpen ? "▾" : "▸"}
              </span>
            </div>
            {counterComponents.intermediates.length > 0 && (
              <span style={{
                fontFamily:FONT.mono, fontSize:9, fontWeight:700, letterSpacing:"0.08em",
                color: COLOR.leaf, background: COLOR.leafDeep,
                border:`1px solid ${COLOR.leaf}44`, padding:"3px 8px", borderRadius:8,
              }}>
                {counterComponents.intermediates.length} BUILT
              </span>
            )}
          </button>
          {counterOpen && (
            <CounterList
              intermediates={counterComponents.intermediates}
              groups={counterComponents.groups}
              sections={counterComponents.sections}
              onToggleAdded={toggleAddedToBowl}
              amountFor={counterAmountFor}
              onPickUnit={onCounterPickUnit}
              singleSectionLabel={
                /\brubs?\b/.test(`${recipe?.category || ""} ${recipe?.course || ""} ${recipe?.title || ""} ${(recipe?.tags || []).join(" ")}`.toLowerCase())
                  ? "Rub"
                  : "Ingredients"
              }
            />
          )}
        </div>
      )}
      {/* FOR THIS STEP — ingredient measurements the cook needs RIGHT NOW.
          When a step carries a structured `uses` array, render just those
          rows with amount+item. When it doesn't (legacy bundled recipes
          pre-retrofit, or AI drafts from an older prompt), fall back to
          the full recipe.ingredients list so measurements are always
          one glance away during cooking. The `heat` and `doneCue`
          fields, when present, render alongside — "medium heat" and
          "nutty smell, sand-colored paste" are the signals that
          actually drive the cook.

          Position: BELOW the prose + timer block. The cook reads the
          step prose + ticks the timer first, then peeks at the
          measurements when it's time to pour something in. The
          previous above-prose layout pushed the prose down the screen
          on every step, which felt cramped on phones. */}
      {(() => {
        // Pull the ingredient list for THIS step from the effective recipe
        // so any swap/skip the user made on the overview lands here too.
        // effectiveRecipe.steps[i].uses has been projected through
        // applyCookSessionToRecipe; entries that matched a swapped slot
        // carry `_swappedFrom`, skipped ones carry `_skipped: true`.
        // Fallback to effectiveRecipe.ingredients (same overrides applied)
        // for older recipes without a structured uses[] array.
        const usesList = Array.isArray(step.uses) && step.uses.length > 0
          ? step.uses
          : (effectiveRecipe.ingredients || []);
        if (!usesList.length && !step.heat && !step.doneCue) return null;
        const isFallback = !(Array.isArray(step.uses) && step.uses.length > 0);
        return (
          <div style={{
            marginTop: 16,
            padding: "16px 18px",
            background: COLOR.ground,
            border: `1px solid ${COLOR.border}`,
            borderRadius: 14,
          }}>
            <button
              type="button"
              onClick={() => setStepIngredientsOpen((v) => !v)}
              style={{
                display:"flex", alignItems:"center", gap:8,
                marginBottom: stepIngredientsOpen ? 12 : 0,
                background:"transparent", border:"none", padding:0,
                width:"100%", cursor:"pointer", textAlign:"left",
              }}
            >
              <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.muted, letterSpacing:"0.16em", flex:1, textTransform:"uppercase" }}>
                {isFallback ? "INGREDIENTS" : "FOR THIS STEP"}
                <span style={{ marginLeft:6, fontSize:9, opacity:0.7 }}>
                  {stepIngredientsOpen ? "▾" : "▸"}
                </span>
              </div>
              {step.heat && (
                <span style={{
                  fontFamily:FONT.mono, fontSize:9, fontWeight:700,
                  letterSpacing:"0.10em",
                  color: COLOR.rose,
                  background: COLOR.roseDeep,
                  border: `1px solid ${COLOR.roseDim}`,
                  padding:"3px 8px", borderRadius:8,
                }}>
                  🔥 {String(step.heat).toUpperCase()} HEAT
                </span>
              )}
            </button>
            {stepIngredientsOpen && (
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {usesList.map((ing, i) => {
                const swappedFrom = ing._swappedFrom?.item || null;
                const isSkipped   = !!ing._skipped;
                const overrideKey = prefKeyForIngredient(ing);
                const overrideAmount = overrideKey ? unitOverrides[overrideKey] : null;
                // Session override wins (one-off pick); fall back to
                // the user's saved preference via applyPreferredUnit.
                const preferred = overrideAmount || applyPreferredUnit(ing.amount, ing, DISPLAY_CONTEXT.COOK);
                const displayAmount = preferred || ing.amount || "—";
                // Lift the matched pantry row's branded display name
                // (Brand + canonical) so the FOR-THIS-STEP list reads
                // like the prep preview did instead of falling back to
                // the recipe's generic "butter" / "flour" text. AIRecipe
                // already does this via the pairings array; CookMode
                // computed pairings at render time but never threaded
                // them into this list — that's the visible disparity
                // between prep ("Sweet Cream Unsalted Butter") and cook
                // ("butter"). Falls back to the recipe text when no
                // pantry pair exists (missing ingredient, unmatched
                // shop item, etc).
                const pairing = isSkipped ? null : findPairing(ing);
                const matchedRow = pairing?.paired || pairing?.closestMatch || null;
                const matchedName = matchedRow ? deriveRowHeader(matchedRow) : "";
                const displayName = matchedName || ing.item || ing.ingredientId || "ingredient";
                return (
                  <div key={i} style={{ display:"flex", gap:10, alignItems:"center", fontFamily:FONT.forma, fontSize:14, color: isSkipped ? CHIP_TONES.canonical.fg : COLOR.ink, lineHeight:1.5, opacity: isSkipped ? 0.7 : 1, padding:"4px 0" }}>
                    <button
                      type="button"
                      onClick={() => !isSkipped && overrideKey && setUnitPicker({
                        key: overrideKey,
                        ingredientId: ing.ingredientId,
                        itemName: ing.item,
                        amountString: String(displayAmount),
                      })}
                      disabled={isSkipped}
                      style={{
                        fontFamily:FONT.mono, fontSize:12, color:CHIP_TONES.canonical.fg,
                        minWidth:68, flexShrink:0, textAlign:"left",
                        display:"inline-flex", alignItems:"center", gap:4,
                        background:COLOR.goldDeep, border:`1px solid ${COLOR.goldDim}`,
                        borderRadius:6, padding:"3px 8px",
                        cursor: isSkipped ? "default" : "pointer",
                        textDecoration: isSkipped ? "line-through" : "none",
                        opacity: isSkipped ? 0.6 : 1,
                      }}
                    >
                      <span>{displayAmount}</span>
                      {!isSkipped && <span style={{ fontSize:8, opacity:0.7 }}>▾</span>}
                    </button>
                    <span style={{ flex:1 }}>
                      <span style={{
                        fontFamily: FONT.itemTitle,
                        fontStyle: "italic",
                        fontWeight: 500,
                        fontSize: 16,
                        color: isSkipped ? CHIP_TONES.canonical.fg : COLOR.ink,
                        textDecoration: isSkipped ? "line-through" : "none",
                      }}>
                        {displayName}
                      </span>
                      {ing.state ? <span style={{ fontFamily:FONT.forma, color:COLOR.plum, fontSize:12 }}> · {ing.state}</span> : null}
                      {isSkipped && (
                        <span style={{ marginLeft:8, fontFamily:FONT.mono, fontSize:9, fontWeight:700, letterSpacing:"0.08em", color:CHIP_TONES.cut.fg, background:"#2a1208", border:"1px solid #3a2010", padding:"2px 6px", borderRadius:6 }}>
                          SKIPPED
                        </span>
                      )}
                      {!isSkipped && swappedFrom && (
                        <span style={{ marginLeft:8, fontFamily:FONT.mono, fontSize:10, color:"#7a7060", fontStyle:"italic" }}>
                          ↔ was: {swappedFrom}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
            )}
            {stepIngredientsOpen && step.doneCue && (
              <div style={{ marginTop:10, padding:"8px 10px", background:"#0f180f", border:"1px solid #1a2e1a", borderRadius:8, display:"flex", gap:8 }}>
                <span style={{ fontSize:12, flexShrink:0 }}>✓</span>
                <span style={{ fontFamily:FONT.forma, fontSize:12, color:"#9ec29e", lineHeight:1.45, fontStyle:"italic" }}>
                  Ready when: {step.doneCue}
                </span>
              </div>
            )}
          </div>
        );
      })()}
      {/* Voiceline player — when the step carries a voiceLine.url (a
          pre-recorded MP3 in the course-voicelines bucket), render
          the cook's voice card with native audio controls. The
          recipe-level voiceCook fields (name, photo, accent) attach
          attribution so the user knows whose voice they're hearing.
          When voiceLine is absent, this block hides entirely — the
          step falls back to text-only OR (future) the Web Speech
          fallback for AI-drafted recipes. */}
      {/* Pre-recorded chef voiceLine — hidden when TTS exists for this
          step (admin-generated narration takes precedence so the user
          doesn't get two audio players competing). When neither
          voiceLine nor TTS exists, the step is text-only. */}
      {step.voiceLine?.url && !recipe.audio?.steps?.[step.id]?.url && (
        <div style={{
          marginTop: 12,
          padding: "12px 14px",
          background: COLOR.deep,
          border: `1px solid ${COLOR.border}`,
          borderRadius: 10,
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          {recipe.voiceCook && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {recipe.voiceCook.photo && (
                <img
                  src={recipe.voiceCook.photo}
                  alt=""
                  style={{
                    width: 32, height: 32, borderRadius: 16,
                    objectFit: "cover", flexShrink: 0,
                    border: `1px solid ${COLOR.border}`,
                  }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: FONT.forma, fontSize: 9, fontWeight: 700,
                  color: COLOR.muted, letterSpacing: "0.16em",
                  textTransform: "uppercase",
                }}>
                  Voice · {recipe.voiceCook.name}
                </div>
                {recipe.voiceCook.accent && (
                  <div style={{
                    fontFamily: FONT.detail, fontStyle: "italic",
                    fontSize: 12, color: COLOR.dim, marginTop: 1,
                  }}>
                    {recipe.voiceCook.accent}
                  </div>
                )}
              </div>
            </div>
          )}
          <audio
            controls
            preload="metadata"
            src={step.voiceLine.url}
            style={{ width: "100%" }}
          />
        </div>
      )}
      {/* TTS narration — admin-generated via the
          generate-recipe-narration edge function. Lives at
          recipe.audio.steps[step.id].url. Auto-plays on step entry
          (`autoPlay` + the `key` forces a fresh element on each
          step transition so playback restarts from the top).
          Mobile autoplay policy: the user already tapped into cook
          mode, which counts as the necessary user gesture for the
          page; programmatic .play() calls in subsequent steps are
          allowed. If a browser blocks it, the user can hit the
          standard <audio> control to play manually. */}
      {recipe.audio?.steps?.[step.id]?.url && (
        <div style={{
          marginTop: 12,
          padding: "10px 12px",
          background: COLOR.deep,
          border: `1px solid ${COLOR.border}`,
          borderRadius: 10,
          display: "flex", flexDirection: "column", gap: 6,
        }}>
          <div style={{
            fontFamily: FONT.forma, fontSize: 9, fontWeight: 700,
            color: COLOR.muted, letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}>
            Listen to this step
          </div>
          <StepNarrationAudio
            src={recipe.audio.steps[step.id].url}
          />
        </div>
      )}
      {step.tip && (
        <div style={{ marginTop:12, padding:"14px 16px", background:COLOR.leafDeep, border:`1px solid ${COLOR.leafDim}`, borderRadius:10, display:"flex", gap:10 }}>
          <span style={{ fontSize:14, flexShrink:0 }}>💡</span>
          <p style={{ fontFamily:FONT.forma, fontSize:14, color:COLOR.leafMuted, lineHeight:1.5, letterSpacing:"0.012em", margin:0 }}>{step.tip}</p>
        </div>
      )}
      {/* OVERNIGHT REST — replace the regular DONE → NEXT row with a
          closing card. Tapping it stamps the rest as finished (so
          telemetry knows progress), then minimizes the cook. The
          session row stays active in the DB; the morning push will
          re-open CookMode at the post-overnight step.

          IMPORTANT: render BEFORE the regular button row, so the user
          never sees both. The early-return uses a Fragment.
      */}
      {step.overnight && activeStep < steps.length - 1 ? (
        <OvernightDay1Cap
          stepTitle={step.title}
          onCapDay1={() => {
            setCompletedSteps(s => new Set([...s, activeStep]));
            telemetry.finishStep({});
            playStepCompleteChime();
            // Minimize, don't end. The cook_sessions row stays at
            // status='active'; useActiveCookSession's overnight-aware
            // cutoff (Day 2 anchor + slack) keeps the banner alive
            // through the night.
            onExit?.();
          }}
        />
      ) : null}
      {/* Passive→active overlap reassurance.
          Renders ONLY when this step is passive (long timer, set-
          and-wait prose) AND the next step is active prep work
          that's meant to fill the wait. The split-step model from
          generate-recipe SPLITS "while pasta boils, dice onions"
          into two sequential steps; the banner lives on the
          passive one to tell the cook "move on to the next step,
          we've got the timer." Without all three preconditions
          (passive + active-next + long timer), the banner just
          adds noise to every step — Trevor caught this earlier:
          "no banner is only needed when there's a next step that
          says 'while that's boiling do this'." */}
      {(() => {
        const longestSec = Math.max(
          Number(step?.timer) || 0,
          ...(Array.isArray(step?.timers) ? step.timers.map(t => Number(t?.seconds) || 0) : []),
        );
        if (longestSec < 180) return null;
        if (activeStep >= steps.length - 1) return null;
        const PASSIVE_RE = /\b(rise|proof|prove|ferment|bake|roast|simmer|rest|cool|let\s+(sit|stand|cool|rest)|braise|smoke|chill|steep|marinate|set\s+aside|hold|reduce\s+to\s+a\s+simmer)\b/i;
        const CONTINUOUS_RE = /\b(stir|whisk|fold|knead|flip|baste|toss)\s+(constantly|continuously|frequently|every\s+\d+|until)/i;
        const ACTIVE_VERB_RE = /\b(dice|chop|mince|grate|slice|peel|zest|juice|crush|smash|whisk|fold|knead|sauté|saute|sear|fry|brown|deglaze|combine|mix|stir|prep|assemble|build|layer|spread|brush|drizzle|sprinkle|garnish)\b/i;
        const curText = step?.instruction || step?.body || step?.text || "";
        const nextStep = steps[activeStep + 1];
        const nextText = nextStep?.instruction || nextStep?.body || nextStep?.text || "";
        const isPassiveCur = PASSIVE_RE.test(curText) && !CONTINUOUS_RE.test(curText);
        const isActiveNext = ACTIVE_VERB_RE.test(nextText) && !PASSIVE_RE.test(nextText);
        if (!isPassiveCur || !isActiveNext) return null;
        return (
          <div style={{
            marginTop: 20,
            padding: "12px 14px",
            background: `${COLOR.leaf}14`,
            border: `1px solid ${COLOR.leaf}66`,
            borderRadius: 12,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ fontSize: 16, lineHeight: 1, color: COLOR.leaf }}>✓</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
                letterSpacing: "0.14em", color: COLOR.leaf, marginBottom: 2,
              }}>
                YOUR TIMER IS SAFE
              </div>
              <div style={{
                fontFamily: FONT.forma, fontSize: 13,
                color: COLOR.ink, lineHeight: 1.4,
              }}>
                Tap NEXT — start the prep while this runs. We&apos;ll ring you when the timer&apos;s done.
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bottom action row — PREV / NEXT STEP. Exit moved to the
          top-corner ✕ that opens the exit sheet, so this row stays
          focused on step progression and can't get accidentally
          tapped during a long braise. Hidden when the overnight cap
          is showing instead. */}
      {!(step.overnight && activeStep < steps.length - 1) && (
      <div style={{ display:"flex", gap:10, marginTop:24, alignItems:"stretch" }}>
        {/* At step 1, back means "let me see the recipe again" — it
            returns to the overview preview instead of dead-ending on
            a disabled button. Session/timers/Live Activity are
            untouched (telemetry.session already exists, so the
            overview's start effect won't re-fire) and START COOKING
            resumes at this same step. */}
        <button
          onClick={() => {
            if (activeStep === 0) setView("overview");
            else setActiveStep(s => Math.max(0, s - 1));
          }}
          style={{ flex:1, padding:"14px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, color:COLOR.dim, borderRadius:12, fontFamily:FONT.mono, fontSize:12, cursor:"pointer" }}
        >
          {activeStep === 0 ? "← RECIPE" : "← PREV"}
        </button>
        {activeStep < steps.length-1 ? (
          <button
            onClick={() => setActiveStep(s => Math.min(steps.length - 1, s + 1))}
            style={{
              flex: 2,
              padding: "14px",
              background: completedSteps.has(activeStep) ? "#1a3a1a" : COLOR.leaf,
              color: completedSteps.has(activeStep) ? COLOR.leaf : "#FFFFFF",
              border: "none",
              borderRadius: 12,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.3s",
            }}
          >
            {completedSteps.has(activeStep) ? "✓ NEXT STEP" : "NEXT STEP"}
          </button>
        ) : (
          <button onClick={finishCook} className="mise-cta" style={{ flex:2, padding:"14px", background:COLOR.leaf, color:COLOR.night, border:"none", borderRadius:12, fontFamily:FONT.mono, fontSize:12, fontWeight:600, cursor:"pointer" }}>
            🍝 DONE! LOG IT →
          </button>
        )}
      </div>
      )}
      {/* Finish + Cancel links — always visible on the cook view so the
          post-meal "what did you use?" dialogue is reachable at ANY
          step, not just after tapping through to the last one (the
          "barely fires" fix). Finish shows on non-final steps; the
          final step already has the green DONE! LOG IT button above. */}
      <div style={{ marginTop:14, textAlign:"center", display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
        {activeStep < steps.length - 1 && (
          <button
            type="button"
            onClick={finishCook}
            className="mcm-focusable"
            style={{
              background:"transparent", border:"none",
              color:COLOR.leaf,
              fontFamily:FONT.forma, fontWeight:600,
              fontSize:14, cursor:"pointer",
              padding:"8px 12px", touchAction:"manipulation",
            }}
          >
            Finish &amp; log this meal →
          </button>
        )}
        <button
          type="button"
          onClick={() => setExitSheetOpen(true)}
          style={{
            background:"transparent", border:"none",
            color:COLOR.muted,
            fontFamily:FONT.forma,
            fontSize:13, cursor:"pointer",
            padding:"8px 12px",
            textDecoration:"underline",
            textUnderlineOffset:3,
            textDecorationColor:COLOR.edge,
          }}
        >
          Cancel this cook
        </button>
      </div>

      {/* styles for the cook-prep per-row swap / shop affordances —
          declared inline at module scope at the bottom so the styles
          object isn't re-allocated on every render. */}
      {completing && (
        <CookComplete
          // Hand off the SERVINGS-SCALED recipe (not the authored
          // original) so the "What did you use?" deduction defaults,
          // the persisted cook_logs.nutrition total, and the leftover
          // math all reflect the batch size the cook actually made.
          // scaleRecipe spreads ...recipe (only ingredients + serves
          // change), so title/slug/skills/produces are preserved.
          recipe={scaledRecipe || recipe}
          userId={userId}
          family={family}
          friends={friends}
          pantry={pantry}
          setPantry={setPantry}
          // Shopping-list setter powers the "OUT AFTER THIS COOK →
          // ADD TO LIST" chips on the kitchen-update screen — the
          // deplete→shop loop closure. Same prop CookMode itself uses
          // for the pre-cook missing-ingredient affordances.
          setShoppingList={setShoppingList}
          // Shared cook-time session state (see useCookSession). Lets
          // CookComplete seed its selected pantry row from the user's
          // cook-prep swaps — "swap Mozzarella for Parmesan" on this
          // screen now carries through to the "What did you use?"
          // screen so the deduction hits the row they actually used.
          cookSession={cookSession}
          // Telemetry session id — CookComplete stamps cook_log_id
          // onto this row after the cook_log insert so cook_duration_stats
          // joins cleanly. Null when telemetry couldn't start (e.g.
          // missing userId).
          telemetrySessionId={completingSessionId}
          // Threaded so CookComplete can call recipeNutrition() at
          // save time to stamp cook_logs.nutrition (migration 0068).
          // CookMode already reads both for its own meta-row card; no
          // new hook calls on this path.
          ingredientInfo={ingredientInfo}
          brandNutrition={brandNutrition}
          // Fork-to-new-recipe handler. When present AND the cook had
          // active swaps/skips, CookComplete's celebrate phase shows
          // "SAVE CHANGES AS NEW RECIPE". Null → action is hidden.
          onForkRecipe={onForkRecipe}
          // Revise-for-next-time — when the cook leaves notes, the
          // post-summary offer can route them into the AI revise
          // sheet. Closes over the BASE (unscaled) recipe: revisions
          // target the saved version, not tonight's batch size.
          onReviseRecipe={onReviseRecipe
            ? (seedText) => onReviseRecipe(recipe, seedText)
            : null}
          onFinish={() => {
            // Wipe the persisted mid-cook session — the cook is logged,
            // so its check-offs / swaps must not resurrect on the next
            // cook of this recipe.
            resetSession();
            setCompleting(false);
            onDone?.();
          }}
        />
      )}
      {unitPicker && (
        <UnitPicker
          open={true}
          onClose={() => setUnitPicker(null)}
          amountString={unitPicker.amountString}
          ingredientId={unitPicker.ingredientId}
          itemName={unitPicker.itemName}
          prefKey={unitPicker.key}
          context={DISPLAY_CONTEXT.COOK}
          onPick={(newAmount) => {
            setUnitOverrides(prev => ({
              ...prev,
              [unitPicker.key]: newAmount,
            }));
          }}
        />
      )}
      {exitSheetOpen && (
        <ModalSheet onClose={() => setExitSheetOpen(false)} maxHeight="55vh">
          <h2 style={{
            fontFamily: FONT.forma,
            fontSize: 26,
            fontWeight: 600,
            color: COLOR.ink,
            margin: "4px 0 10px",
            lineHeight: 1.15,
            letterSpacing: "-0.01em",
          }}>
            Done with this cook?
          </h2>
          <p style={{
            fontFamily: FONT.forma,
            fontSize: 18,
            fontWeight: 500,
            color: COLOR.ink,
            lineHeight: 1.45,
            margin: "0 0 28px",
            maxWidth: "60ch",
          }}>
            The session and timer end. The recipe stays in your library —
            you can start it again anytime.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Finish primary — green/positive. Routes to the post-meal
                "what did you use?" dialogue so leaving a cook logs the
                meal instead of discarding it. (You can still skip the
                kitchen deduction from inside that screen.) */}
            <button
              type="button"
              onClick={finishCook}
              className="mcm-focusable"
              style={{
                width: "100%",
                padding: "16px",
                background: COLOR.leaf,
                border: "1px solid rgba(20,60,50,0.18)",
                borderRadius: 14,
                color: COLOR.night,
                fontFamily: FONT.mono,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.14em",
                cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              FINISH &amp; LOG IT →
            </button>
            {/* Destructive primary — solid burnt-orange (COLOR.rose,
                the palette's "alert" semantic on cream surfaces).
                Cream text gives clear contrast; tracked mono label
                matches every other primary CTA in the app. Color
                transitions are property-specific (no `transition:all`)
                per emil-design-eng. */}
            <button
              type="button"
              onClick={exitCook}
              style={{
                width: "100%",
                padding: "16px",
                background: COLOR.rose,
                border: "1px solid rgba(60,30,10,0.18)",
                borderRadius: 14,
                color: COLOR.night,
                fontFamily: FONT.mono,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.14em",
                cursor: "pointer",
                boxShadow: "0 6px 14px rgba(217,107,43,0.22), 0 1px 2px rgba(60,30,10,0.10)",
                transition: "background-color 160ms cubic-bezier(0.23,1,0.32,1), box-shadow 160ms cubic-bezier(0.23,1,0.32,1)",
              }}
            >
              YES, CANCEL COOK
            </button>
            {/* Secondary dismiss — ghost button. Transparent bg,
                ink hairline border, ink text. Reads cleanly on the
                cream sheet without competing with the destructive
                primary above. Replaces the previous grey-on-grey
                that was the original UI complaint. */}
            <button
              type="button"
              onClick={() => setExitSheetOpen(false)}
              style={{
                width: "100%",
                padding: "16px",
                background: "transparent",
                border: `1px solid ${COLOR.border}`,
                borderRadius: 14,
                color: COLOR.ink,
                fontFamily: FONT.mono,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.14em",
                cursor: "pointer",
                transition: "border-color 160ms cubic-bezier(0.23,1,0.32,1), background-color 160ms cubic-bezier(0.23,1,0.32,1)",
              }}
            >
              KEEP COOKING
            </button>
          </div>
        </ModalSheet>
      )}
    </div>
  );
}

// ── cook-prep row action styles ──────────────────────────────────────
// SWAP / + SHOP pills per ingredient on the cook prep screen. Visual
// language mirrors AIRecipe's tweak phase (same blue SWAP, same
// yellow SHOP) so the user learns one vocabulary across both screens.
const cookSwapBtn = {
  padding: "5px 10px",
  background: COLOR.skyDeep, border: `1px solid ${COLOR.skyBorder}`,
  color: COLOR.sky, borderRadius: 8,
  fontFamily: FONT.mono, fontSize: 9,
  letterSpacing: "0.06em", cursor: "pointer", whiteSpace: "nowrap",
};
const cookSwapBtnActive = { ...cookSwapBtn, background: COLOR.skyDeep, color: "#9bcae0" };
const cookShopBtn = {
  padding: "5px 10px",
  background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`,
  color: COLOR.gold, borderRadius: 8,
  fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
  letterSpacing: "0.08em", cursor: "pointer", whiteSpace: "nowrap",
};
const cookShopBtnDone = { ...cookShopBtn, background: COLOR.leafDeep, borderColor: COLOR.leafDim, color: COLOR.leaf, cursor: "default" };
const cookSwapSearchInput = {
  width: "100%", padding: "8px 10px",
  background: COLOR.deep, border: `1px solid ${COLOR.border}`,
  borderRadius: 8, color: COLOR.ink,
  fontFamily: FONT.forma, fontSize: 12,
  outline: "none", boxSizing: "border-box",
};
const cookSwapOptionBtn = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "8px 10px", width: "100%",
  background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
  borderRadius: 8, cursor: "pointer", textAlign: "left",
};
const cookSwapClearBtn = {
  marginTop: 4, padding: "6px 10px",
  background: "transparent", border: `1px dashed ${COLOR.border}`,
  color: COLOR.dim, borderRadius: 8,
  fontFamily: FONT.mono, fontSize: 10,
  letterSpacing: "0.06em", cursor: "pointer",
};

// Share-with-a-person sheet. Modal listing family + friends with a
// quick "SEND" tap per row. Sent recipients flip to "✓ SENT" locally
// so rapid taps don't re-ping the same person. A "SHARE EXTERNALLY"
// row at the bottom hands off to the OS native share sheet (Web
// Share API) or, on desktop, copies the recipe URL to clipboard.
// Separate from the family-visibility toggle on the preview — the
// toggle controls RLS visibility, this sheet sends individualized
// notifications.

// ── Per-step TTS narration audio ────────────────────────────────
//
// On iOS native (BoodaVoice supported): routes the clip through
// BoodaVoice.playNarration → narrationPlayer on the engine's audio
// graph. The engine has voice-processing IO enabled, so hardware AEC
// subtracts narrationPlayer's output from the mic input BEFORE SR
// receives it. SR doesn't hear Booda's own voice; the user can talk
// over narration cleanly with no false-wake leakage and no need to
// pause-on-wake.
//
// Elsewhere: HTML5 <audio> fallback. There's no AEC in this path —
// the recognizer either tolerates the leak (Web Speech sometimes
// does) or the user accepts that voice commands during narration are
// unreliable on web.
function StepNarrationAudio({ src }) {
  const useEngine = engineNarrationSupported();
  const speakingRef = useRef(false);
  const audioRef = useRef(null);
  const playStartedRef = useRef(false);
  const watchdogRef = useRef(null);

  const emitStart = (ttlMs = 8000) => {
    if (speakingRef.current) {
      audioFloorExtend(ttlMs);
      return;
    }
    speakingRef.current = true;
    audioFloorClaim(ttlMs);
  };
  const emitEnd = () => {
    if (!speakingRef.current) return;
    speakingRef.current = false;
    audioFloorRelease();
  };

  // Engine path — fire on src change. Two timing concerns wrapped in a
  // single START_DELAY_MS:
  //   (1) Action chime plays for ~290ms when a voice command lands;
  //       starting narration immediately clashes with the chime tail.
  //   (2) On banner-resume, BoodaVoice engine is still spinning up
  //       (INITIAL_GRACE_MS); a too-eager playEngineNarration call
  //       throws "engine not running" and we silently fail.
  // 400ms covers both. playEngineNarration resolves on natural end OR
  // when superseded by another call.
  const START_DELAY_MS = 400;
  useEffect(() => {
    if (!useEngine || !src) return undefined;
    console.log(`[narration:engine] src=${src.split("/").pop()} (delaying ${START_DELAY_MS}ms for chime + engine-ready)`);
    let cancelled = false;
    emitStart(60000);
    playStartedRef.current = true;
    const delayHandle = setTimeout(() => {
      if (cancelled) return;
      console.log(`[narration:engine] firing playEngineNarration for ${src.split("/").pop()}`);
      playEngineNarration(src)
        .then(() => {
          if (cancelled) return;
          console.log("[narration:engine] resolved (ended or superseded)");
          emitEnd();
        })
        .catch((err) => {
          if (cancelled) return;
          console.warn("[narration:engine] failed:", err?.message || err);
          emitEnd();
        });
    }, START_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(delayHandle);
    };
  }, [useEngine, src]);

  // HTML5 path — fire on src change. Original behavior kept verbatim.
  useEffect(() => {
    if (useEngine) return undefined;
    console.log(`[narration] src=${src ? src.split("/").pop() : "(none)"}`);
    playStartedRef.current = false;
    if (watchdogRef.current) clearTimeout(watchdogRef.current);

    emitStart(30000);

    const el = audioRef.current;
    if (el) {
      try { el.currentTime = 0; } catch { /* ignore */ }
      el.play().catch((e) => {
        console.log(`[narration] play() rejected: ${e?.message || e}`);
      });
    }

    watchdogRef.current = setTimeout(() => {
      if (!playStartedRef.current && speakingRef.current) {
        console.log("[narration] autoplay-block watchdog → release floor");
        emitEnd();
      }
    }, 1500);

    return () => {
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useEngine, src]);

  useEffect(() => {
    return () => {
      console.log("[narration] full unmount");
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      if (useEngine) {
        stopEngineNarration().catch(() => {});
      }
      emitEnd();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onTimerStarted = () => {
      // Engine path: the timer voiceline ("Timer set, I'll give you a
      // heads-up…") plays via playVoiceline → playEngineNarration,
      // which auto-supersedes any prior engine clip — including this
      // step narration. Don't manually stop here, or we'll cut the
      // voiceline off as it tries to come up.
      if (useEngine) return;
      const el = audioRef.current;
      if (el && !el.paused) {
        try { el.pause(); } catch { /* ignore */ }
      }
    };
    window.addEventListener("cookmode:timer-started", onTimerStarted);
    return () => window.removeEventListener("cookmode:timer-started", onTimerStarted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useEngine]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onReplay = () => {
      if (useEngine) {
        if (!src) return;
        console.log("[narration:engine] replay event received");
        emitStart(60000);
        playEngineNarration(src)
          .then(() => emitEnd())
          .catch(() => emitEnd());
        return;
      }
      const el = audioRef.current;
      console.log(`[narration] replay event received el=${!!el} src=${el?.src ? el.src.split("/").pop() : "(none)"} paused=${el?.paused} ended=${el?.ended}`);
      if (!el) return;
      try { el.currentTime = 0; } catch { /* ignore */ }
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.then(() => console.log("[narration] replay play() resolved"))
         .catch((e) => console.log(`[narration] replay play() rejected: ${e?.message || e}`));
      }
    };
    window.addEventListener("cookmode:request-narration-replay", onReplay);
    return () => window.removeEventListener("cookmode:request-narration-replay", onReplay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useEngine, src]);

  // Pause-on-wake — WEB ONLY. The iOS engine path is handled by a
  // centralized listener inside engineNarration.js, which pauses
  // whichever clip is on the engine narrationPlayer at wake time
  // (per-step narration, Chadwick step lines, voicelines — all
  // share the same player). Web fallback runs through this <audio>
  // element so we keep the listener here for that case only.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (useEngine) return undefined; // engineNarration owns it
    let resumeTimer = null;
    const onWakePaused = (e) => {
      const resumeAfter = Number(e?.detail?.resumeAfterMs) || 4000;
      const el = audioRef.current;
      if (!el || el.paused) return;
      try { el.pause(); } catch { /* ignore */ }
      console.log("[narration] paused on wake — waiting for command");
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        resumeTimer = null;
        const cur = audioRef.current;
        if (cur && cur.paused && !cur.ended) {
          cur.play().catch(() => {});
          console.log("[narration] auto-resumed after wake window");
        }
      }, resumeAfter);
    };
    window.addEventListener("voice:wake-paused", onWakePaused);
    return () => {
      window.removeEventListener("voice:wake-paused", onWakePaused);
      if (resumeTimer) clearTimeout(resumeTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useEngine]);

  const handlePlay = () => {
    playStartedRef.current = true;
    console.log("[narration] play event ▶");
  };

  const handleLoadedMetadata = () => {
    const el = audioRef.current;
    const dur = el && Number.isFinite(el.duration) ? el.duration : 0;
    console.log(`[narration] loadedmetadata duration=${dur}s`);
    if (dur > 0 && speakingRef.current) {
      audioFloorExtend(Math.ceil(dur * 1000) + 2500);
    }
  };

  const lastTimeUpdateLogRef = useRef(0);
  const handleTimeUpdate = () => {
    if (!speakingRef.current) return;
    const el = audioRef.current;
    let ttl = 5000;
    if (el && Number.isFinite(el.duration) && Number.isFinite(el.currentTime)) {
      const remainingMs = Math.max(0, (el.duration - el.currentTime) * 1000);
      ttl = Math.max(remainingMs + 5000, 5000);
    }
    audioFloorExtend(ttl);
    const now = Date.now();
    if (now - lastTimeUpdateLogRef.current > 1000) {
      lastTimeUpdateLogRef.current = now;
      const t = el ? el.currentTime : 0;
      console.log(`[narration] timeupdate t=${t.toFixed(1)}s`);
    }
  };

  // Engine path: render a minimal "narrating" indicator. No HTML5
  // <audio> element — the audio is flowing through the engine, not
  // through a DOM element. Pause/seek controls are intentionally
  // absent for now; NEXT/BACK supersedes via playEngineNarration.
  if (useEngine) {
    return (
      <div style={{
        fontFamily: FONT.mono, fontSize: 11, color: COLOR.dim,
        letterSpacing: "0.06em", padding: "6px 0",
      }}>
        {speakingRef.current ? "▶ narrating…" : "—"}
      </div>
    );
  }

  return (
    <audio
      ref={audioRef}
      controls
      preload="auto"
      src={src}
      onPlay={handlePlay}
      onLoadedMetadata={handleLoadedMetadata}
      onTimeUpdate={handleTimeUpdate}
      onPause={emitEnd}
      onEnded={emitEnd}
      style={{ width: "100%" }}
    />
  );
}
